/*
 * ONBOARDING GUIDE — the app's ONE guided walk (owner direction 2026-07-27, rebuilt 2026-08-11).
 *
 * The bloom breadcrumbs of the beacon engine are ON ICE (onboarding_tips.js,
 * BEACONS_ENABLED / ?beacons=on): the radiating flowers read as smoke puffs rather
 * than concentric rings, and vanish against small controls. This module replaces
 * them with the legacy help walls' own cue, rebuilt on tokens: the NEXT control to
 * press GLOWS (the old infoBorderGlow violet), and a popup beside it says what the
 * control is for.
 *
 * TWO KINDS OF STEP (owner call 2026-08-11), and the difference is the whole contract:
 *
 *   action — a control to press. Its popup carries NO button: it goes when the control
 *            is used, or when the next step's popup replaces it. Nothing to acknowledge,
 *            because the acknowledgement IS the press.
 *   info   — something to look at (the garden bed, the progress panel, a dictionary
 *            entry). Nothing to press, so the popup carries "Got it" and Esc dismisses
 *            it — and THAT is what advances the tour.
 *
 * THE POPUP IS NOT A HOVER TIP any more (same owner call). It used to appear only when
 * the pointer found the glowing control, which left the recap screen's "Go to the
 * practice now!" glowing in silence for anyone who did not go looking. The live step
 * always shows its popup, immediately.
 *
 * THREE CHAPTERS:
 *
 *   1. THE LOOP (predicate-driven, priority-ordered, one glow at a time — a step the
 *      user already did on their own simply never shows):
 *
 *        Learn tab → Elementary header (or its chip on the compact strip, when the
 *        level rows are folded) → its Add pill → (the menu opens on hover, its own
 *        machine) → "Random ▸" → the count row's add arrow (3 first while the walk is
 *        live — main.js buildCountItems reads OB_GUIDE.firstRun) → Start Learning →
 *        the ladder's recap "Go to the practice now!" → Start Exercise! on the
 *        Practice tab — plus the escort back to the Learn tab when words are already
 *        staged and the user is elsewhere.
 *
 *   1½. THE ENTRY (scripted, own cursor, once ever — owner call 2026-08-12): the first
 *      meet card of that run flips into a full dictionary entry, and this chapter reads
 *      it with the learner, starting with the flip itself —
 *
 *        the reveal prompt → what the entry is → the word's pronunciation → "Got it" to
 *        move on → and, on the card that follows, the one line that hands the rest of the
 *        run back to the reader.
 *
 *      FIVE of its steps were CUT on 2026-08-31 (owner call): add-to-list, examples and
 *      collocations, "click a word, any word", Esc/Back out of that excursion, and the
 *      sentence speaker. Between them they turned a first-time reader's first minute into a
 *      nine-screen guided read of one dictionary entry — and each of the five is taught
 *      again where it is actually wanted: the add button by chapter 2's `wlAdd`, the rest by
 *      the entry itself, the first time the reader goes looking. What is left names the
 *      entry, offers its sound, and gets out of the way.
 *
 *      The one chapter that speaks on a session screen (every step is `onStage`), for
 *      the reason a meet card is not a question: it is the app's own reading screen,
 *      which is why its words are study links in the first place. Its wall is tight —
 *      each step lights the CARD and lets through only what it names (owner call
 *      2026-08-12: "just look at the entry and then click Got it").
 *
 *   2. THE TOUR (scripted, in order, with a CURSOR persisted in the onboarding attr):
 *      the first finished drill hands over to it, on the finish screen itself —
 *
 *        the garden bed → My Progress → Back to Words → the Dictionary tab → the
 *        lookup field → your own list → the row's add-to-Learn → the
 *        Elementary band → its Add pill → Random → the count arrow → "add to what is
 *        already there".
 *
 *      It ends with a full tray and Start Learning one press away, which is the point:
 *      the chapter used to stop at the opened band, having told the reader to add a
 *      handful and then left them to do it inside a sealed app (owner call 2026-08-13).
 *
 *      Chapter 2 is a chain rather than a predicate set because it teaches an ORDER
 *      (look a word up, file it, learn it), not a state. The cursor only ever moves
 *      forward: a step whose anchor is missing (a reload landed elsewhere) is skipped
 *      to the first later step that IS on screen, so the tour self-heals instead of
 *      hanging on a control that no longer exists.
 *
 *   3. THE WORD LISTS (scripted, own cursor, OFFERED not started): chapter 2's last step
 *      raises a card that says the loop is complete and asks whether to go on. Yes walks
 *      the panel top to bottom —
 *
 *        what the panel is → My Garden → what fills it → the five level bands →
 *        Conjugation → its filter box → add a verb from it → Verb forms → add one from
 *        that → a topic → a word in the topic list → the search → its box → My List →
 *        "+" → name it → what the new list is for → the "?".
 *
 *      Same walker as chapter 2 (chainStep drives both). One actionable control at a
 *      time, one example per control, and nothing a first-day reader has no use for:
 *      rename, delete and sort have no steps at all.
 *
 *      It also TIDIES THE PANEL as it goes (owner spec 2026-08-13): each step's own
 *      `enter` hook closes what the last lesson opened, opens what this one is about,
 *      and puts the cursor in the box it asks the reader to type in. The moves are the
 *      panel's own (main.js window.plWlPanel) — the walk cannot press its way there,
 *      its own wall being what swallows every press but the step's.
 *
 * THE HALO IS A FLOATING ELEMENT, not a box-shadow on the control: the expanded
 * word-list section clips its children (overflow:hidden rounds its frame), and two
 * steps glow items INSIDE popover menus — a fixed, pointer-events:none ring pinned
 * over the target escapes every clip and stacking context the same way. A 300ms
 * tracker (plus scroll/resize capture) keeps it on the target's live rect.
 *
 * THE MODE TABS GET AN INSET RING, every other target keeps the outward one (owner
 * report 2026-07-28): the outward ring, grown past the control's box, doubled as a
 * second "this is selected" signal right next to the tabs' real one (tabActive's
 * inset orange strip in tabs.css) — the Learn tab read as already-active while
 * only being pointed at. Elsewhere (headers, pills, menu rows, arrows) that outer
 * frame is the only "look here" signal there is, so it stays.
 *
 * THE POPUP IS A PL.tip.pin (docs/design/tooltip-engine-consolidation.md §4.5): the
 * walk owns no popup implementation — no box building, no placement, no tracker, no
 * Esc handler. What stays here is the walk's half of the contract: WHICH step is being
 * explained and WHERE the box may not land (the open menu family, fed to the pin's
 * `avoid`). TWO pin instances, one per kind: the option set that decides "Got it or
 * no Got it" is read when a pin is BUILT, and a pin is built once per name.
 *
 * A PIN IS NOT A PL.layers ENTRY, deliberately: layers close on outside pointerdown,
 * but the popup must survive the user clicking the very control it explains. While
 * the add menu is open the box rides in that layer's anchors (finishAddMenu concats
 * OB_GUIDE.cueAnchors), so pressing inside it is not an "outside" press taking the
 * menu down mid-walk.
 *
 * ARMED by guided MODE (tooltip-engine-consolidation.md §2.3 — ONE switch governs all
 * teaching) plus, for chapter 1, the tour states `skipped`/`running` with no practice
 * drill finished yet. The first finished drill no longer ENDS the guide, it hands it
 * to chapter 2; the tour ends when chapter 2 runs out of steps.
 *
 * STAGE RULES: an onboarding card owns the stage outright — nothing at all behind it. A
 * session screen (a meet card, a drill question) owns the TEACHING — no glow, no popup,
 * recall focus is sacred — but NOT the wall: the dim and the click-mute hold there too, with
 * the whole central panel lit (owner call 2026-08-12, see sessionStage). The escort escorts
 * every beat of the run, or the beat it drops out on is the one where the reader wanders off.
 * Sync rides OB_TIPS.evaluate — every hook that fed the beacons feeds the guide.
 */
var OB_GUIDE;

(function () {
	"use strict";

	// Air between the control and the ring. 3px until 2026-09-01, when the corner study
	// (temp/ui-smoke/ring-corner-fixture.html) showed WHY the Start hero still read rough with
	// the radius made concentric: at 3px of air, with the dim's own edge only 1px outside the
	// ring, the button edge + air + stroke + glow + darkness fused into one thick band and the
	// corner turned muddy. 6px of air with the dim held 12px out separates the four edges, and
	// the corner reads as a clean arc. Still small enough not to swallow a menu arrow or a row's
	// add button — 8px, the next step tested, did.
	var GLOW_PAD = 6;         // px of clear air between the target's box and the ring
	var TAB_GLOW_PAD = -5;    // …for the mode tabs only: px the ring sits INSET instead (negative = inward)
	var GLOW_RING = 2;        // the ring's own stroke — .obGuideHaloClass border-width
	var TRACK_MS = 300;       // halo re-pin cadence while a control glows

	var halos = [];           // the floating glow rings — one per element this step marks
	var target = null;        // the element currently glowing
	var stepId = null;        // the live step's id
	var stepObj = null;       // …and the step itself
	var cueFor = null;        // step id the open popup belongs to (null = hidden)
	var dismissed = null;     // …and the step whose popup the reader put away with Esc
	var cueAnchor = null;     // the element the open popup points at
	var cuePlace = null;      // its preferred side, re-asked on every re-place
	var cueAlign = null;      // …and how it lines up along that side ("start" = flush tops/lefts)
	var cueSized = null;      // the anchor alignFor() measures — cueAnchor's twin, set BEFORE the
	                          // show (see showCue) so the FIRST placement already knows the size
	var clickBound = null;    // the click handler bound to `target` for a click-advanced step
	var settleTimer = null;   // the one re-measure after a step's own layout change lands
	var tracker = null;
	var liveRegion = null;

	// BACK — re-show, never undo (owner, 2026-10-08: "let's do re-show explanations now and see how
	// it works and if it is sufficient"). See the "Back" block above showCue.
	var shown = [];           // the popups that went up on this page, in order (no repeats in a row): { id, tab }
	var walkHere = false;     // the welcome card was answered on THIS page, so shown[0] is the walk's first popup
	var FLASH_MS = 400;       // a popup up for less than this was a flash, not a step the reader saw (showCue)
	var lastInputAt = 0;      // the reader's latest press or key, any target — what tells a flash from a fast reader
	["pointerdown", "keydown"].forEach(function (type) {
		window.addEventListener(type, function () { lastInputAt = Date.now(); }, true);
	});
	var peekIdx = null;       // the index into `shown` being re-shown (null = not peeking)
	var peekLive = null;      // …and the live step's id while it is: the step Continue goes back to
	var peekRing = null;      // the re-shown step's own rings (anchor first) while its anchor is on screen

	function T() { return window.ONBOARDING && ONBOARDING.tips; }

	// ── tiny state readers (twins of onboarding_tips.js — keep in step) ──────────

	function visible(node) {
		return !!(node && node.isConnected && node.getClientRects().length);
	}

	function activeTab() {
		var mode = document.getElementById("centralModeId");
		return mode ? mode.getAttribute("data-tab") : null;
	}

	function trayCount() {
		return (window.LRN && LRN.getTray) ? (LRN.getTray() || []).length : 0;
	}

	function expandedLevelSection() {
		return document.querySelector(".wlSectionClass.wlExpanded[data-wl-kind^='LEVEL_']");
	}

	function firstLevelSection() {
		return document.querySelector(".wlSectionClass[data-wl-kind='LEVEL_A']")
			|| document.querySelector(".wlSectionClass[data-wl-kind^='LEVEL_']");
	}

	// The level strip only — the VERB strip is built on the same shape and carries
	// .wlLevelStripClass too (main.js buildStrip starts from it and adds the group class).
	function levelChip(sec) {
		var strip = document.querySelector(".wlLevelStripClass:not(.wlVerbStripClass)");
		if (!strip) { return null; }
		var id = sec && sec.getAttribute("data-wl-id");
		return (id && strip.querySelector(".wlLevelChipClass[data-wl-id='" + id + "']"))
			|| strip.querySelector(".wlLevelChipClass");
	}

	// The control that OPENS the first level list — the walk's very first glow.
	//
	// Normally that is the section's header row. With "Compact dictionary lists" on (account
	// menu ▸ Settings ▸ Word list settings, or a double-click on a row's 📖; user attr
	// compactLevelLists) the five LEVEL_* rows fold behind a chip
	// strip and their sections go display:none — so the header is INVISIBLE and the chip is
	// the control. That was the whole of the owner's "replay does nothing" (report
	// 2026-08-08): the walk was armed, but its first step had no visible anchor, no later
	// step's predicate held on an empty tray, and a walk with no eligible step glows
	// nothing at all. Compact lists are a settled user preference, so the walk meets the
	// user in the panel they actually have.
	function firstLevelHeader() {
		var sec = firstLevelSection();
		var header = sec ? sec.querySelector(".wlHeaderClass") : null;
		return visible(header) ? header : levelChip(sec);
	}

	function levelAnchorIsChip() {
		var a = firstLevelHeader();
		return !!(a && a.classList.contains("wlLevelChipClass"));
	}

	// The level list the guided run's first words came from — LEVEL_<band>, the band
	// /loadTopUnseen answered with and onboarding.js stamped on the run (st.band). The tour's
	// "add a few more" step opens THAT list, so the next handful is the same level as the two
	// words the reader has just met (owner, 2026-10-07: "do we use the appropriate band, the one
	// corresponding to the initial choice on the welcome screen? We should"). It used to open the
	// first list, A1, while the welcome card's "I'm new" draws from A2 (ruling R19). The first
	// level list stays the fallback: a run stamped before this, or a pair whose lists carry no
	// such band. Same header-or-chip resolution as firstLevelHeader.
	function chosenLevelSection() {
		var t = T();
		var band = t && t.band ? t.band() : null;
		var sec = band ? document.querySelector(
			".wlSectionClass[data-wl-kind='LEVEL_" + band + "']") : null;
		return sec || firstLevelSection();
	}

	function chosenLevelHeader() {
		var sec = chosenLevelSection();
		var header = sec ? sec.querySelector(".wlHeaderClass") : null;
		return visible(header) ? header : levelChip(sec);
	}

	function learnHeroIsHandoff() {
		var label = document.querySelector("#learnControlBandId .lrnStartLabelClass");
		return !!label && label.textContent === getRes("learnRecap_drillNow");
	}

	// The ladder's recap screen ("N new words met") ends every Learn session, and its own
	// drill button is the handoff press. The recap deliberately lives OUTSIDE .lrnBodyClass
	// (it is wider than the session cap), so sessionScreenLive() does not mute the guide
	// there — without a step of its own the walk simply went dark on that screen.
	// Disabled = nothing graduated (a bailed session): no glow, nothing to hand over.
	function recapDrillButton() {
		var b = document.querySelector(".lrnRecapClass .lrnCtaButtonClass");
		return (b && !b.disabled) ? b : null;
	}

	// Practice's "Start Exercise!" — enabled exactly while a word set is staged
	// (showPracticeEmpty disables it, updateStartCount re-enables). The empty-state
	// hint is checked too: right after a tab switch the button can sit STALE-ENABLED
	// until the async empty render disables it, and no engine hook rides that render —
	// the smoke caught the halo stuck on a disabled Start (2026-08-08). The hint is
	// the empty state's own signature; either signal alone can lag, together they
	// cannot claim a drill that isn't there. (The tracker's eligibility re-check is
	// the belt for the hookless flip itself — see track().)
	function practiceStartButton() {
		var b = document.getElementById("startId");
		if (!b || b.disabled) { return null; }
		if (visible(document.querySelector(".practiceEmptyHintClass"))) { return null; }
		// …and NOT on the finish screen, where this same button is re-labelled "Practice Again!"
		// and re-enabled by exerciseFinished. The drill the step was escorting is over at that
		// point, so the step went eligible again the instant it completed and its popup flashed on
		// the button before chapter 2 took the screen (owner report 2026-08-31: "a popup briefly
		// appeared at Practice Again and the Planted! was shown"). Running the set a second time is
		// a fine thing to do and nobody needs to be told to do it — the handover to the garden is
		// what this screen is for, and it is the tour's own next beat.
		if (visible(document.querySelector(".exSummaryClass"))) { return null; }
		return b;
	}

	// The drill's answer field, while it is still waiting for the escorted reader's FIRST word.
	//
	// This is the one thing the walk says on a session screen, and it is said by owner's call
	// (2026-08-12): pressing Start Exercise! drops a first-time reader onto a screen holding a word
	// in a language they don't know and a bare box, with no statement anywhere of what to do with
	// either. Every other beat of chapter 1 names its press; this one didn't.
	//
	// "Still waiting" is the whole gate — the moment anything is typed the reader plainly has the
	// idea, and the glow and popup go for good (typedInDrill, latched, so clearing the field does
	// not summon them back mid-answer). Everything else about a session screen is unchanged: recall
	// focus is sacred, so this step is the exception that is allowed onto one (sessionStage's
	// onStage gate), and no other step is.
	var typedInDrill = false;

	function drillAnswerField() {
		var f = document.getElementById("answerId");
		return (!typedInDrill && visible(f) && !f.value) ? f : null;
	}

	$(document).on("input.obGuideDrill", "#answerId", function () {
		if (typedInDrill || !this.value) { return; }
		typedInDrill = true;
		sync();
	});

	function sessionScreenLive() {
		return visible(document.getElementById("answerId"))
			|| visible(document.querySelector(".lrnBodyClass"));
	}

	// ── chapter 1½'s own readers: the flipped meet card ──────────────────────────
	//
	// The rung-0 meet card flips to the word's FULL rich entry — the same card the Dictionary
	// tab renders, senses, examples, collocations, grammar and all (learning_session.js
	// renderMeet → renderLearnRichView). That flip is a first-time reader's first sight of a
	// dictionary entry in this app, and until now the walk said nothing about it: chapter 2
	// waited until after the first drill to explain the entry, on a word the reader had looked
	// up rather than on the one they were meeting.
	//
	// The card and the excursion out of it are two different surfaces and the chapter visits both,
	// so each has its own reader — and WHICH of the two is up cannot be read off the markup. A word
	// clicked inside the reveal is rendered by openLearnDrill into the very container the reveal
	// occupies, so the deeper word's entry wears the same selector as the meet word's; only the
	// other excursion path (openStudyOverlay, from a phrase word or a card headword) gets a box of
	// its own. main.js owns that state and now says so (window.learnDrillOpen), which is what keeps
	// the two readers from both answering yes at once.

	function drillOpen() {
		return !!(window.learnDrillOpen && window.learnDrillOpen());
	}

	function meetRichCard() {
		if (drillOpen()) { return null; }
		var rich = document.querySelector(".lrnMeetClass .lrnRichClass");
		return visible(rich) ? rich.querySelector(".rich-card") : null;
	}

	// The meet card BEFORE the flip: the headword, its speaker, and the prompt that opens the entry.
	// The chapter starts HERE now (owner call 2026-08-12) — pressing that prompt is the first thing a
	// first-time reader is ever asked to do inside a session, and the walk used to say nothing about
	// it. The reveal button's own visibility is the test, because it is what the flip hides.
	function meetFrontCard() {
		if (drillOpen()) { return null; }
		var card = document.querySelector(".lrnMeetClass");
		return (visible(card) && visible(card.querySelector(".lrnRevealClass"))) ? card : null;
	}

	function meetRevealButton() {
		var card = meetFrontCard();
		return card ? card.querySelector(".lrnRevealClass") : null;
	}

	// Either face of the meet card — the chapter's stage, whichever half of it is up.
	function meetSurface() {
		return meetRichCard() || meetFrontCard();
	}

	function studyOverlay() {
		if (!drillOpen()) { return null; }
		var box = document.querySelector(".lrnRevealRichClass")
			|| document.querySelector(".lrnMeetClass .lrnRichClass");
		return visible(box) ? (box.querySelector(".rich-card") || box) : null;
	}

	// One part of the meet card's entry, looked up WITHIN it. Scoped rather than fetched by id
	// from the document: the rich card's two header buttons carry ids (#richSoundBtnId,
	// #richWlAddId) and the Dictionary tab renders into the same page, so a card left expanded
	// there would answer a document-wide lookup first — with an element in a hidden tab, which
	// then fails `visible()` and drops the step for no reason the reader could ever see.
	function meetCardPart(sel) {
		var card = meetRichCard();
		var el = card ? card.querySelector(sel) : null;
		return visible(el) ? el : null;
	}

	// The session's forward action, parked top-right by setTopAction — "Got it →" once the meet
	// card is flipped. A <button>: the slot's sizing ghost wears the same class on <span>s.
	function meetGotItButton() {
		return document.querySelector(".lrnTopActionClass button.lrnTopActionBtnClass");
	}

	// The open Add menu (a body-mounted popover), and its revealed count submenu.
	function openMenu() {
		return document.querySelector(".wlAddMenuClass");
	}

	function inAddMenu(node) {
		return !!(node && node.closest && node.closest(".wlAddMenuClass"));
	}

	// The "Random ▸" row specifically. Other rows in this menu can also become .wlMenuParentClass —
	// over a NON-EMPTY tray each staging action grows a replace-or-add flyout (main.js
	// wireAddAction) — so both menu steps name Random by its own class. Every step here is gated on
	// an empty tray anyway, which is exactly when none of those flyouts exist; the class keeps that
	// from being the only thing holding the selector up.
	function openSubParent() {
		var menu = openMenu();
		return menu ? menu.querySelector(".wlMenuRandomClass.wlSubOpenClass") : null;
	}

	// The add DESTINATION is Learning on every tab except Practice (main.js
	// addDestIsLearning) — the menu steps apply wherever Random feeds the learn tray.
	function destIsLearning() {
		return activeTab() !== "practice";
	}

	// The two staging controls the menu steps ring, named once because every one of those steps
	// now also has to say that NOTHING ELSE in the menu answers (owner report 2026-09-01: "only
	// the specific action should be enabled — right now I can click in other places as well and it
	// breaks the tour"). An open menu is LIT whole, because a menu with three quarters of itself
	// dimmed reads as broken; what it is not is uniformly live.
	function randomRow() {
		var menu = openMenu();
		return menu ? menu.querySelector(".wlMenuRandomClass") : null;
	}

	function countAddArrow() {
		var p = openSubParent();
		return p ? p.querySelector(".wlAddSubMenuClass .wlMenuCountAddClass") : null;
	}

	// …and HOVER stays open to the whole family even so. These menus close themselves when the
	// pointer leaves the row that opened them (main.js wireAddMenuHover), and the wall mutes the
	// ENTER half of a hover only — so narrowing hover to the one live row would start that
	// away-timer the moment the reader stepped INTO the submenu, and nothing would cancel it. The
	// press is the thing being narrowed here, not the pointer.
	function menuFamily() {
		var pops = document.querySelectorAll(".wlPopoverClass"), out = [];
		for (var i = 0; i < pops.length; i++) { if (visible(pops[i])) { out.push(pops[i]); } }
		return out;
	}

	// The APPEND row of the replace-or-add flyout a count row grows over a non-empty tray
	// (main.js trayChoiceRows: replace first, append second). Chapter 2's staging steps end on
	// it — the tray holds the word the reader has just filed, and the walk is not about to
	// throw it away. A dimmed row (nothing left to add) is not offered: the step is skipped.
	function countChoiceRow() {
		var rows = document.querySelectorAll(".trayChoiceSubClass .trayChoiceRowClass");
		var row = rows.length ? rows[rows.length - 1] : null;
		return (row && !row.classList.contains("wlMenuDisabledClass") && visible(row)) ? row : null;
	}

	// Chapter 2's staging press has LANDED: the tray holds more than it did when the walk reached
	// the Add pill (bandAdd's `enter` notes that count). Null until then — and a null reads "not
	// yet", which sends a menu step whose menu is gone back to the pill rather than past it.
	var menuTray = null;

	function menuStaged() {
		return menuTray !== null && trayCount() > menuTray;
	}

	// ── chapter 2's own readers ──────────────────────────────────────────────────

	// The practice finish screen, and the growth bed on it. The bed's SPOTLIGHT is the
	// title + the patch alone: the schooling line under it ("watering twice a day does not
	// speed growth") is a garden aside, not part of what the tour is pointing at (owner
	// call 2026-08-11), so garden.js wraps the two in .gardenGrowSpotClass for us.
	function growthBed() {
		return document.querySelector("#exSummaryGrowthId .gardenGrowSpotClass");
	}

	// ⚠️ The bed is lit and live on ITS OWN step and nowhere else — the walk's ordinary rule, and
	// it took a detour to get back to it. For one day (owner call 2026-09-01) the bed was a
	// STANDING exception, lit across every finish-screen step, on the reasoning that dimming the
	// reward one sentence after handing it over reads as the app taking it back. What that ignored
	// is how the dim is DRAWN: one rectangle over the lit set's bounding box (see the wall's own
	// note). The bed sits far left, the progress panel far right, so "bed + panel" is a hole the
	// width of the screen, and the progress step came out as a bright band with two dim margins —
	// "ok, you win. it's ugly" (owner, same day, reverting it). A second lit thing on a step is
	// only free when it is NEXT TO the first one.
	//
	// The bed's own step keeps the press, and pressing it opens the garden vista — a modal the
	// walk stands down for (see cardLive) and picks its step back up when it closes.
	//
	// The finish screen's WORD links are deliberately not pressable either: a word on this screen
	// opens its rich entry and leaves the tour's screen behind it (owner call, same day — "the
	// rich translation when clicking on the words — no").
	//
	// ⚠️ THE BED HAS THE SAME DOOR IN IT, on the one step where it IS live (owner report
	// 2026-09-01, third telling): a PLANT in the bed is a word too, and its click ran
	// gardenNavigate — the dictionary uncovered under the finish screen, the rich card opened
	// there, the tour's screen gone. The wall cannot tell a plant from the soil around it (one
	// canvas, one hit test), so the seal is not in the lit/press lists but at the two plant-click
	// handlers themselves: garden.js sends a plant to the VISTA while the walk is armed, and
	// garden_vista.js opens no rich popup while it is armed. Both ask OB_GUIDE.armed(). Anything
	// made pressable here that can reach a rich entry needs the same treatment at its own handler.

	function finishScreenLive() {
		return visible(document.querySelector(".exSummaryClass"));
	}

	function richCard() {
		return document.querySelector("#defId .rich-card");
	}

	// The dictionary entry's own add-to-list button. Asked of the CARD and not of the document:
	// the same id is on the meet card's header, and a session left standing behind the dictionary
	// would hand the step the wrong button.
	function cardWlAddButton() {
		var card = richCard();
		return card ? card.querySelector("#richWlAddId") : null;
	}

	// …the list picker it raises, and the ONE row in it the tour offers: My List, which is the
	// only fileable list a chapter-2 reader has and the one whose badge the step reads back.
	function addPickBox() {
		var pick = document.querySelector(".wlAddPickClass");
		return visible(pick) ? pick : null;
	}

	function addPickRow() {
		var pick = addPickBox();
		return pick ? pick.querySelector(".wlAddPickRowClass") : null;
	}

	function defaultSection() {
		return document.querySelector(".wlSectionClass[data-wl-kind='DEFAULT']");
	}

	function defaultExpanded() {
		var s = defaultSection();
		return (s && s.classList.contains("wlExpanded")) ? s : null;
	}

	// The first word row of an open list section, and the add-to-Learn button on it. Row controls
	// are hover-revealed (word-list-row-hover-surface.md): inline when they fit, otherwise in a
	// floating box pinned to the row's top edge. So the anchor has THREE shapes and the step
	// takes whichever is on screen — the button itself when the walk (or the pointer) has revealed
	// it, else the row.
	//
	// Falling back to the ROW is now a fallback and no longer the normal case. It used to be: the
	// steps rang the whole row and left revealing the cluster to the reader, which put a ring round
	// a WORD while the copy talked about a button that was not on screen ("you are actually not
	// showing the button" — owner report 2026-08-13). Every step that uses this now carries
	// ROW_MARK, whose class reveals the row's cluster for as long as the step is live, so the ring
	// lands on the button itself. The row shape survives for the tick between a step going live and
	// its mark landing, and for a row the panel re-renders under it.
	//
	// Written for the reader's own list; chapter 3's verb steps shared it until 2026-10-08 (they are
	// tombstones now), and the shapes stay general: same markup, same reveal, any list.
	function sectionRow(sec) {
		return sec ? sec.querySelector(".wlWordRowClass") : null;
	}

	function rowAddOf(sec) {
		var row = sectionRow(sec);
		if (!row) { return null; }
		var inline = row.querySelector(".wlRowAddClass");
		if (visible(inline)) { return inline; }
		var host = document.querySelector(".wlRowActionsPopHostClass");
		var floated = document.querySelector(".wlRowActionsPopClass .wlRowAddClass");
		if (host === row && visible(floated)) { return floated; }
		return row;
	}

	// The `mark` every row-add step wears: force the row's hover cluster open, so the step has a
	// BUTTON to ring instead of the row it lives on. The class goes on the row and comes off with
	// the glow (applyMark / clearMark), and the panel's own hover handlers never touch it — they
	// drive .wlRowActionsInlineClass, which this rule sits beside rather than on top of.
	//
	// `host` resolves from whatever the step is anchored to, because that changes UNDER this mark:
	// the first pass rings the row (nothing revealed yet), the mark reveals the cluster, and the
	// next sync re-anchors to the button — whose own row is the host from then on.
	var ROW_MARK = {
		cls: "obGuideRowOpenClass",
		host: function (el) {
			return (el && el.closest) ? el.closest(".wlWordRowClass") : null;
		}
	};

	// …and the button ALONE, or nothing: what a step lets the reader PRESS, as opposed to what it
	// rings. A pressable row is not what these steps want — a click on a row's background ticks its
	// checkbox, which is not the add the step is asking for.
	function rowAddButton(sec) {
		var el = rowAddOf(sec);
		return (el && el.classList && el.classList.contains("wlRowAddClass")) ? el : null;
	}

	// A list's own badge — the count the SERVER sent, on screen long before the rows are. Steps
	// that need to know whether a list is empty ask this and never the rendered rows: an expanded
	// section paints its words asynchronously, so "no rows yet" is a state every open list passes
	// through, and a `gone` predicate reading it would skip its step on every single run.
	function listCount(sec) {
		var pill = sec ? sec.querySelector(".wlHeaderClass .pl-count-pill") : null;
		var n = pill ? parseInt((pill.textContent || "").replace(/\D/g, ""), 10) : NaN;
		return isNaN(n) ? null : n;
	}

	function myListRow() { return sectionRow(defaultExpanded()); }

	function myListRowAdd() { return rowAddOf(defaultExpanded()); }

	// The word the lookup step offers as an example — a reasonably common NOUN LEMMA of the
	// pair, chosen SERVER-side (/dictionary/example-word, DefinitionSelectorService).
	//
	// It used to be read off the screen: the first ._entryClass[data-term] in #defId, on the
	// theory that the dictionary's own first row is by construction a word the corpus really
	// has — and that matters, because typing a word it does NOT have sends the app off to
	// GENERATE an entry (real spend), which the tour must not teach as the normal move.
	//
	// That read was wrong twice over (owner report 2026-08-12). The dictionary's first row is
	// its ALPHABETICAL head — "look up Aal, say" is not an example anybody wants. And it was
	// not even reading the dictionary: all three tabs render into the same #defId box and the
	// dictionary list arrives asynchronously after the tab flips, so at the instant this step
	// opens the box still holds the PREVIOUS tab's rows — the Learn tray, i.e. one of the three
	// words the guided session had just taught. The tip then froze (a step's body is composed
	// once, when it goes live) while the A–Z painted underneath it.
	//
	// So the pick moved to where the facts are: rank, part of speech and "is this a lemma or an
	// inflected form" all live in the DB and none of them are on screen.
	//
	// `null` until the answer lands (or for a pair with no qualifying noun) — the step falls
	// back to the wordless sentence rather than waiting.
	var exampleWord = null, exampleFor = null;

	function langPair() {
		var to = $("#langToValId").attr("data-lang-code");
		var from = $("#langFromValId").attr("data-lang-code");
		return (to && from) ? { termLang: to, defLang: from, key: to + "|" + from } : null;
	}

	// Asked ONCE per language pair, and early — the tour kicks this off when chapter 2 takes
	// over on the finish screen, four steps before the word is needed, so the popup opens with
	// it in hand. Keyed by pair so a language switch mid-tour re-asks instead of naming a word
	// from the dictionary the reader has left.
	function fetchExampleWord() {
		var pair = langPair();
		if (!pair || exampleFor === pair.key) { return; }
		exampleFor = pair.key;
		ajaxPost("dictionary/example-word",
			{ termLang: pair.termLang, defLang: pair.defLang },
			function (data) {
				exampleWord = (data && data.word) || null;
				// The step may already be up and saying the wordless sentence — a body is composed
				// once per step, so it takes a fresh show to pick this up.
				if (cueFor === "lookup") { hideCue("lookup"); sync(); }
			},
			null,
			// A background nicety: no message box — the wordless sentence is a perfectly good
			// popup and the tour must not stall on a failed request. Releasing the pair key lets
			// the lookup step's own belt ask once more when it opens, which is the only retry
			// there is (showCue composes a body on a step change, not on a timer).
			function () { exampleWord = null; exampleFor = null; });
	}

	function dictExampleWord() {
		fetchExampleWord();   // belt for a reload that lands mid-tour, past the finish screen
		return exampleWord;
	}

	// The language THIS CARD is answered in, as the drill itself named it one line before it wrote
	// the field's placeholder (training_session.js setNextDefinition_continue): langTo normally,
	// langFrom on a reversed card, which in a Mix session changes card by card. READ rather than
	// re-derived — the guide cannot see which way the card in hand runs, and a step that told a
	// first-time reader to answer in the wrong language would be worse than saying nothing.
	function answerLanguage() {
		return $("#answerBoxId").attr("data-answer-lang") || learnedLanguage();
	}

	// Reader language == learned language: the monolingual pair, where "translation" is the wrong
	// word for what the field wants. Same identity test main.js's isSameLangPair() and the
	// placeholder use, so the step and the placeholder it points at never disagree.
	function sameLangPair() {
		var from = $("#langFromValId").attr("data-lang-code");
		var to = $("#langToValId").attr("data-lang-code");
		return !!from && from === to;
	}

	// The learned language, named in the READER's language rather than as the endonym the picker
	// shows (docs/project/i18n-runtime-grammar.md §4.1) — the guided tour splices this into
	// obGuideType_body / obGuideTypeWord_body, both sentences in the reader's own language.
	// ⚠️ textContent, not the code, was also fragile for a second reason: the picker's <ul> menu is
	// appended INTO the holder when it opens, so textContent could pick up every menu row.
	function learnedLanguage() {
		var val = document.getElementById("langToValId");
		var text = val && val.textContent.trim();
		var shown = (text && text !== "[not defined]") ? text : "";
		return langDisplayName(val && val.getAttribute("data-lang-code"), shown);
	}

	// The popup's body goes in as HTML (PL.tip renders it), so a term coming out of the
	// dictionary is escaped on the way in. Everything else in these strings is ours.
	function escText(s) {
		return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
	}

	// ── the walk (chapter 1: priority order; first match glows, one at a time) ───

	var STEPS = [
		{
			// FIRST, because a live drill also satisfies the Learn-tab escort below ("you are not
			// on the Learn tab") — and the reader is standing exactly where they should be.
			//
			// The one step that runs ON a session screen, which `onStage` is what declares: sync()
			// hands every other one back to the plain panel-wide wall there. `lit` keeps that wall
			// exactly as it is — the whole central panel stays READABLE, because on a session screen
			// the panel is what the reader is working in — while the ring goes round the field alone.
			// `press` is the same session workflow the wall grants when no step is up (sessionLive):
			// it used to fall through to `lit`, which handed this one step the whole panel live while
			// every other beat of the same screen had been narrowed to the run's forward path.
			id: "type", place: "above", onStage: true, lit: sessionStage, focus: true,
			press: function () { return sessionLiveIn(sessionStage()); },
			titleKey: "obGuideType_title",
			bodyText: function () {
				return strFormat(getRes(sameLangPair() ? "obGuideTypeWord_body" : "obGuideType_body"),
					escText(answerLanguage()));
			},
			when: function () { return activeTab() === "practice" && !!drillAnswerField(); },
			anchor: drillAnswerField
		},
		{
			// A recap screen is up = that IS the step, whatever else the predicates
			// below would make of the tray: "Go to the practice now!" is the handoff,
			// one press away. Same message as the tray hero's handoff face, so it
			// wears that step's copy.
			id: "recap", place: "above",
			titleKey: "obTipExpDrill_title", bodyKey: "obTipExpDrill_body",
			when: function () { return activeTab() === "learn" && !!recapDrillButton(); },
			anchor: recapDrillButton
		},
		{
			// Deepest first: the count submenu is out — the top row's arrow (3 for a
			// fresh list while the walk is live) is the press that actually fills the tray.
			id: "five", place: "right",
			// Copy follows the row: 3 for the first scoop, the normal 5 wording on any
			// later one (the override and this split read the same firstLearn signal,
			// so the cue can never claim a number the row doesn't show).
			titleKey: function () { return firstLearn() ? "obGuideFive_title" : "obGuideFiveNext_title"; },
			bodyKey: function () { return firstLearn() ? "obGuideFive_body" : "obGuideFiveNext_body"; },
			when: function () { return destIsLearning() && trayCount() === 0 && !!openSubParent(); },
			anchor: countAddArrow,
			press: function () { return [countAddArrow()]; },
			hover: menuFamily
		},
		{
			// The Add menu is open: Random is the simplest way to a first handful.
			// The cue sits LEFT — the count submenu reveals on the right.
			id: "random", place: "left",
			titleKey: "obGuideRandom_title", bodyKey: "obGuideRandom_body",
			when: function () { return destIsLearning() && trayCount() === 0 && !!openMenu(); },
			anchor: randomRow,
			press: function () { return [randomRow()]; },
			hover: menuFamily
		},
		{
			// A level list is open — its Add pill is the control that fills the tray.
			// The cue sits ABOVE: the menu itself opens under the pill.
			id: "add", place: "above",
			titleKey: "obTipExpAdd_title", bodyKey: "obTipExpAdd_body",
			when: function () {
				return activeTab() === "learn" && trayCount() === 0 && !!expandedLevelSection();
			},
			anchor: function () {
				var sec = expandedLevelSection();
				return sec ? sec.querySelector(".wlAddPracticeClass") : null;
			}
		},
		{
			// Learn tab, empty tray, nothing open: words live in the level lists —
			// Elementary first.
			// Copy follows the shape the panel is in: a row is a word list to open, a chip
			// is a letter that opens one — "click on this dictionary word list" pointing at
			// an "A" the size of a key explains nothing.
			id: "wl", place: "right",
			titleKey: "obTipExpList_title",
			bodyKey: function () {
				return levelAnchorIsChip() ? "obTipExpChip_body" : "obTipExpList_body";
			},
			when: function () {
				return activeTab() === "learn" && trayCount() === 0 && !expandedLevelSection();
			},
			anchor: firstLevelHeader
		},
		{
			// The handed-over words sit in the Practice set: Start Exercise! is the
			// press that ends chapter 1 — the finished drill hands the guide to the
			// tour. MUST outrank the escort below, which would otherwise glow the Learn
			// tab (empty again after the handoff) while the user stands exactly where
			// they should be.
			// LEFT of the Start band, over the dimmed Word Lists pane — the Learn tab's `start`
			// step's placement, for the same reason (owner, 2026-10-07 evening round,
			// docs/project/first-run-redesign.md §11): below, the box sat on the staged words,
			// the very cards the reader is about to drill. Same narrow-pane flip (cuePlaceOpts).
			id: "drill", place: "left",
			titleKey: "obGuideDrill_title", bodyKey: "obGuideDrill_body",
			when: function () { return activeTab() === "practice" && !!practiceStartButton(); },
			anchor: practiceStartButton
		},
		{
			// Elsewhere in the app: the walk starts (or resumes) on the Learn tab.
			// Copy splits on whether words already wait there (the goto-tray escort).
			id: "tab", place: "below",
			titleKey: function () { return trayCount() ? "obTipExpTray_title" : "obTipExpLearn_title"; },
			bodyKey: function () { return trayCount() ? "obTipExpTray_body" : "obTipExpLearn_body"; },
			when: function () { return activeTab() !== "learn"; },
			anchor: function () { return document.getElementById("tabLearnId"); }
		},
		{
			// LEFT of the Start band, over the dimmed Word Lists pane — not below it (owner ruling R2,
			// docs/project/first-run-redesign.md, 2026-10-07). Below, the box sat on the tray, and
			// this is the first screen where the reader sees their words WITH their meanings. The
			// band spans the whole centre, so "left" is the pane; a pane too narrow for the box
			// flips it to the right pane (cuePlaceOpts), which is just as clear of the tray.
			id: "start", place: "left",
			titleKey: "obTipExpStart_title", bodyKey: "obTipExpStart_body",
			when: function () {
				return activeTab() === "learn" && trayCount() > 0 && !learnHeroIsHandoff();
			},
			anchor: function () {
				return document.querySelector("#learnControlBandId .lrnStartButtonClass");
			},
			// The two words are LIT here, the button alone is live (owner ruling 2026-10-09,
			// tour-exhaustive-test.md §5.7). This step drew the most quit cards on prod: readers press
			// the words they are about to learn before they press Start — reaching for the thing itself,
			// which the lit-press rule never counts (onPress). The tray still answers nothing.
			lit: function () {
				return elemList([document.querySelector("#learnControlBandId .lrnStartButtonClass"),
					document.getElementById("defBoxScrollId")]);
			},
			press: function () {
				return [document.querySelector("#learnControlBandId .lrnStartButtonClass")];
			}
		},
		{
			// Every tray word graduated — the same hero now reads "Go to the practice
			// now!" and this glow hands the words over.
			id: "handoff", place: "below",
			titleKey: "obTipExpDrill_title", bodyKey: "obTipExpDrill_body",
			when: function () { return activeTab() === "learn" && learnHeroIsHandoff(); },
			anchor: function () {
				return document.querySelector("#learnControlBandId .lrnStartButtonClass");
			}
		}
	];

	// ── the tour (chapter 2: in order, cursor persisted in the attr) ─────────────
	//
	// Each entry is either `info` (a popup with Got it — dismissing it IS the advance) or
	// an action step, which advances when `done()` says the press has happened. `click:true`
	// is the shorthand for a press with no state to read back: the anchor's own click
	// advances the cursor. A step whose `when` fails or whose anchor is missing is SKIPPED
	// (the cursor moves past it) — a tour that hangs on a control the app no longer shows
	// is worse than a tour that says one thing less.
	var TOUR = [
		{
			// The finish screen's garden bed: the investment hook, and the reward moment
			// the tour opens on. The words are already planted, so there is nothing the step
			// ASKS for — the bed is lit and pressable for this step only (see growthBed).
			id: "bed", kind: "info", place: "right",
			titleKey: "obBeatBed_title", bodyKey: "obBeatBed_body",
			when: finishScreenLive, anchor: growthBed,
			lit: growthBed, press: growthBed
		},
		{
			// …and the panel that will keep score of it from now on.
			id: "progress", kind: "info", place: "left",
			titleKey: "obTourProgress_title", bodyKey: "obTourProgress_body",
			anchor: function () { return document.getElementById("statsPanelId"); }
			// The panel is READ, not pressed — its own doors (My Garden, the history views) go
			// somewhere the tour cannot follow. No `lit`: the panel alone, and the bed goes dim
			// with the rest of the screen (see growthBed on why it does not stay lit here).
		},
		{
			// Out of the finished session. Its own button says "Back to Words".
			id: "back", place: "below",
			titleKey: "obTourBack_title", bodyKey: "obTourBack_body",
			when: finishScreenLive,
			anchor: function () { return document.getElementById("stopId"); },
			done: function () { return !finishScreenLive(); }
		},
		{
			id: "dictTab", place: "below",
			titleKey: "obTourDict_title", bodyKey: "obTourDict_body",
			anchor: function () { return document.getElementById("tabDictId"); },
			done: function () { return activeTab() === "dict"; }
		},
		{
			// Type a word, press Enter. The example is a word the dictionary already has
			// (dictExampleWord) — never an invented one, which would send the app off to
			// generate an entry.
			id: "lookup", place: "below",
			titleKey: "obTourLookup_title",
			// The field has to HAVE focus when this box says "type a word". The Dictionary tab's own
			// chrome focuses it (main.js applyDictChrome), but that focus lands while the PREVIOUS
			// step (dictTab, the tab itself) is still the live one, and the wall's onFocusIn hands it
			// straight back — so the reader typed into nothing: on the first measured day 8 of the 13
			// who reached this step put 28 keystrokes on the page body, and 6 of the 13 never finished
			// it (prod 2026-10-08, docs/project/ad-funnel-diagnosis.md §8.5). Deferred one tick past
			// the sync that makes THIS step live, so the same onFocusIn lets the focus stand.
			enter: function () {
				setTimeout(function () {
					var f = document.getElementById("lookupFieldId");
					if (f && stepId === "lookup" && document.activeElement !== f) { f.focus(); }
				}, 0);
			},
			bodyText: function () {
				var word = dictExampleWord();
				var lang = escText(learnedLanguage());
				return word
					? strFormat(getRes("obTourLookup_body"), lang, "<b>" + escText(word) + "</b>")
					: strFormat(getRes("obTourLookupPlain_body"), lang);
			},
			when: function () { return activeTab() === "dict"; },
			anchor: function () { return document.getElementById("lookupFieldId"); },
			done: function () { return !!richCard(); }
		},
		// NO "the whole word" step here any more (owner call 2026-08-12). It used to sit between
		// the lookup and the add-to-list press, saying what a rich entry holds — which chapter 1½
		// now says four screens earlier, in more detail, on the first entry the reader ever sees
		// (their own first word, on the flipped meet card). Repeating it here on a word they just
		// typed in is the same lesson twice, the second time with less to look at. Its resource
		// keys (obTourEntry_*) are gone with it.
		{
			// …but the add-to-list press IS here, RESTORED 2026-08-13 after one day out. It was
			// dropped as a repeat of chapter 1½'s `mList`, which named the same button on the meet
			// card — and that reading missed what the press is doing in THIS chapter. `mList` only
			// named the button ("more about those later"); here the reader actually files a word,
			// and the three steps after this one are about the list they just filed it into. Without
			// the press the chapter walks from a looked-up entry straight to the word-list panel with
			// nothing to show there (owner report 2026-08-13: "the abrupt going to the WL is
			// illogical") — and, when the own list really was empty, the row step below simply
			// skipped itself.
			//
			// `mList` itself is GONE since 2026-08-31, which leaves this the only place the app ever
			// explains the button — one more reason it stays.
			//
			// TWO presses, not one (2026-08-16): the add button raises a picker of the reader's
			// word lists and a row in THAT is what files the word — there is no standing
			// destination any more (docs/project/wl-add-target-redesign.md). So the step cannot
			// finish on its anchor's click the way `click: true` steps do; it finishes when a word
			// has actually landed, which `done` reads off My List's badge. `lit`/`press` are what let the
			// picker through the walk's wall — without them the popover would be dimmed and its rows
			// dead, and the reader would have no second press to make.
			//
			// My List is the ONLY fileable list a chapter-2 reader has (own lists are chapter 3's
			// lesson), so the badge it reads is the badge the picker's one row moves.
			id: "wlAdd", place: "left",
			titleKey: "obTourWlAdd_title", bodyKey: "obTourWlAdd_body",
			when: function () { return activeTab() === "dict" && !!richCard(); },
			done: function () { return listCount(defaultSection()) > 0; },
			// The picker stays READABLE whole — a menu with three quarters of itself dimmed reads
			// as broken — while exactly ONE row answers: My List, the one the ring is on and the one
			// `done` reads the badge of. `pass` used to hand the whole popover through, which made
			// its "new list" field live as well, and naming a list is chapter 3's lesson, a dozen
			// screens away from here (owner report 2026-09-01: "only My List should be clickable,
			// create new list — no").
			lit: function () { return elemList([cardWlAddButton(), addPickBox(), addPickRow()]); },
			press: function () { return elemList([cardWlAddButton(), addPickRow()]); },
			// …and HOVER goes to the whole picker even so — the same split the menu steps make
			// (menuFamily), for the same reason, learned here the hard way. This picker holds
			// ITSELF open: `wlAddOpenPicker` binds `mouseenter → machine.enter()` on the popover so
			// that moving off the button and into the menu cancels the away-timer the leave
			// started. Hover is muted by the PRESS list, so narrowing press to My List muted the
			// popover's own enter, and the picker shut while the pointer was still travelling to
			// the row it was pointing at — "when I'm moving the cursor to My List, it collapses the
			// view" (owner report 2026-09-01). Pressability is what this step narrows; the pointer
			// is not, and a menu that closes on the way to its own answer cannot be pressed at all.
			hover: function () { return elemList(cardWlAddButton()).concat(menuFamily()); },
			// The SECOND press gets a ring of its own once the picker is open. The row was already
			// live, but nothing said which one to press: the halo stayed on the add
			// button the reader had just pressed, and the row that actually files the word — My
			// List, the only fileable list a chapter-2 reader has — sat in an open menu looking
			// like a list of equals (owner report 2026-08-31, "the option My List should be
			// highlighted and clickable"). Additive: ringTargets keeps the anchor's own ring, so
			// the pair reads as "you pressed this, now press that". The FIRST row, which is the
			// same one `done` reads the badge of.
			rings: function () { return [addPickRow()]; },
			// Asked of the CARD and not of the document: the same id is on the meet card's own
			// header, and a session left standing behind the dictionary would hand this step the
			// wrong button.
			anchor: cardWlAddButton
		},
		{
			id: "myList", place: "right",
			titleKey: "obTourMyList_title", bodyKey: "obTourMyList_body",
			anchor: function () {
				var sec = defaultSection();
				return sec ? sec.querySelector(".wlHeaderClass") : null;
			},
			done: function () { return !!defaultExpanded(); }
		},
		{
			// The filed word, and the button that takes it to the Learn tray. The anchor
			// is the row until the pointer reveals its controls — see myListRowAdd.
			//
			// An EMPTY own list is the one state this step cannot be taught in, and since the
			// add-to-list press left the chapter it is a state a reader can genuinely arrive in:
			// `gone` says so on the spot rather than letting the walker spend its five-second
			// anchor wait on a row that does not exist. It reads the list's BADGE, not its rows —
			// see listCount.
			id: "rowAdd", place: "right", click: true,
			when: function () { return !!defaultExpanded(); },
			gone: function () { return listCount(defaultSection()) === 0; },
			titleKey: "obTourRowAdd_title", bodyKey: "obTourRowAdd_body",
			anchor: myListRowAdd,
			mark: ROW_MARK,
			// Centred in its panel, not merely "in sight": the open list sits at the bottom of the word
			// lists, and at 1536×730 (a 1920×1080 laptop at 125 %) a sliver of the row showed, so
			// bringIntoView left it — and the ring landed on a button clipped under the panel's edge
			// (tour-exhaustive-test.md D7).
			scrollTo: "center",
			// The ring may still be on the row for a tick; the press is the button's alone (see
			// rowAddButton) — a click on a row's background ticks its checkbox and would advance a
			// step that is about taking the word to Learn.
			press: function () { return [rowAddButton(defaultExpanded())]; }
		},
		{
			// The word is in the Learn tray now, and the tray lives on another tab — the
			// escort the owner's walk-through takes for granted ("once the Learn tab is
			// open…"). Skipped outright when the reader is already there (done() is checked
			// before when()).
			id: "learnTab", place: "below",
			titleKey: "obTipExpTray_title", bodyKey: "obTipExpTray_body",
			when: function () { return activeTab() !== "learn"; },
			anchor: function () { return document.getElementById("tabLearnId"); },
			done: function () { return activeTab() === "learn"; }
		},
		{
			// …and back to where chapter 1 started: the dictionary's own lists are where
			// the next handful comes from — the list of the band the first words CAME FROM
			// (chosenLevelHeader), not the easiest one (owner, 2026-10-07 evening round).
			// BELOW the row, over the dimmed lists under it — not beside it (owner, same round): to
			// the right the box sat on the first card's headword, the one thing on screen the
			// reader had just learned. Same rule as the Start popup: over a dimmed pane, never over
			// the words. A folded strip (the chip anchor) flips it as cuePlaceOpts allows.
			id: "band", place: "below",
			titleKey: "obTourBand_title", bodyKey: "obTourBand_body",
			anchor: chosenLevelHeader,
			done: function () { return !!expandedLevelSection(); }
		},
		// …and then all the way through the staging menu, one control at a time, until five
		// random words are actually IN the tray (owner call 2026-08-13: the tour used to stop at
		// the opened list, having told the reader to add a handful and then left them to it —
		// with every control around them still walled off).
		//
		// These four are chapter 1's `add`/`random`/`five` steps' counterparts for a tray that is
		// NOT empty: the same three controls, plus the replace-or-add question the menu asks at
		// the end when there is something to lose. Chapter 1's own versions cannot serve, being
		// gated on an empty tray — which by here it is not, the filed word is in it.
		{
			id: "bandAdd", place: "above",
			titleKey: "obTourBandAdd_title", bodyKey: "obTourBandAdd_body",
			// The tray as it stands before the menu opens — what menuStaged reads the words going
			// in against. Taken once per chapter run (and again after a reload, which starts with no
			// `entered`): nothing between here and the staging press changes the tray.
			enter: function () { menuTray = trayCount(); },
			when: function () { return !!expandedLevelSection(); },
			anchor: function () {
				var sec = expandedLevelSection();
				return sec ? sec.querySelector(".wlAddPracticeClass") : null;
			},
			// The menu opens on HOVER as readily as on a click (main.js wireAddMenuHover), so the
			// read-back is the menu being open, never the press.
			done: function () { return !!openMenu(); }
		},
		{
			// The cue sits LEFT — the count submenu reveals on the right.
			id: "bandRandom", place: "left",
			titleKey: "obTourBandRandom_title", bodyKey: "obTourBandRandom_body",
			when: function () { return !!openMenu(); },
			retreat: "bandAdd",
			anchor: randomRow,
			press: function () { return [randomRow()]; },
			hover: menuFamily,
			done: function () { return !!openSubParent(); }
		},
		{
			id: "bandFive", place: "right",
			titleKey: "obTourBandFive_title", bodyKey: "obTourBandFive_body",
			when: function () { return !!openSubParent(); },
			retreat: "bandAdd",
			anchor: countAddArrow,
			press: function () { return [countAddArrow()]; },
			hover: menuFamily,
			// Two ways this press can land: the flyout below (a tray with something in it), or
			// the words going straight in and the menu closing (a tray the reader has emptied
			// meanwhile). Both are "done"; only the first has a step of its own. The second is
			// read off the TRAY, never off the menu being shut: until 2026-10-08 "the menu closed"
			// counted as done, so the quit card or a reload taking the menu down carried the
			// cursor forward onto bandAppend, which had nothing to ring either — a dead tour with
			// no popup and no wall (tour-exhaustive-test.md D1).
			done: function () { return !!countChoiceRow() || menuStaged(); }
		},
		{
			// The tray already holds the word chapter 2 filed, so the count arrow asks before it
			// stages: replace what is there, or add to it. Adding is the answer this walk wants —
			// the word the reader has just been taught to file must still be there at the end.
			id: "bandAppend", place: "right", click: true,
			titleKey: "obTourBandAppend_title", bodyKey: "obTourBandAppend_body",
			when: function () { return !!countChoiceRow(); },
			retreat: "bandAdd",
			// The press advances the chain itself (`click`); this is the straight-in landing of
			// bandFive's press, which skips the flyout and so this step's control.
			done: menuStaged,
			anchor: countChoiceRow,
			// The REPLACE row beside it is the one answer this step is not asking for — it would
			// throw away the word the reader has just been taught to file, which is the whole
			// subject of the sentence in the popup.
			press: function () { return [countChoiceRow()]; },
			hover: menuFamily
		}
	];

	// ── chapter 3: the Word Lists panel ─────────────────────────────────────────
	//
	// Owner spec 2026-08-13. Chapters 1–2 use the panel for exactly two things — open the
	// easiest list, open your own — and leave a reader with no idea that the rest of it is a
	// garden, five graded lists, eighteen topics and a search across all of them (the two verb
	// lists were part of the lesson until 2026-10-08 — see the three tombstones below). This
	// chapter walks the panel top to bottom, ONE actionable control at a time, and
	// is OFFERED rather than started: tourContinueCard asks first (see offerWordLists).
	//
	// Same machinery as chapter 2 — a cursor in the attr, `done()` per action step, info steps
	// finished by their Got it — with four rules of its own that come straight from the spec:
	//
	//   • ONE EXAMPLE PER CONTROL. Each list is opened once, and that is the whole lesson; the
	//     tour never asks the reader to work through what is inside.
	//   • WHAT IS NOT IMPORTANT ON DAY ONE IS NOT TAUGHT. Rename, delete and the sort menu have
	//     no steps here (and the wall keeps them dead), because a first-time reader who has just
	//     made their first list has nothing to rename, delete or re-order.
	//   • A LIST THIS PAIR DOES NOT HAVE IS SKIPPED, not waited for (`gone`). The Conjugation row
	//     exists for German and not for every pair.
	//   • THE PANEL IS TIDIED BETWEEN LESSONS, and the walk does the tidying (owner spec
	//     2026-08-13). A step that needs a clean panel says so in its own `enter` hook — close
	//     what the last lesson opened, open what this one is about, focus the box it asks the
	//     reader to type in. Written on the step that NEEDS the state rather than on the step
	//     that left it behind: a chapter whose steps can be skipped cannot rely on the one
	//     before it having run. The moves themselves are the panel's (main.js
	//     `window.plWlPanel`) — the walk owns no word-list machinery of its own, and its wall
	//     would swallow a synthetic press anyway.

	function wlSection(kind) {
		return document.querySelector(".wlSectionClass[data-wl-kind='" + kind + "']");
	}

	function wlHeader(kind) {
		var sec = wlSection(kind);
		var header = sec ? sec.querySelector(".wlHeaderClass") : null;
		return visible(header) ? header : null;
	}

	function openSection(kind) {
		var sec = wlSection(kind);
		return (sec && sec.classList.contains("wlExpanded")) ? sec : null;
	}

	function wlOpen(kind) {
		return !!openSection(kind);
	}

	// The panel's own moves, as the chapter's `enter` hooks ask for them. Absent only before
	// main.js has built the panel, which is not a state this chapter can be reached in.
	function panelDo(move, arg) {
		var p = window.plWlPanel;
		if (p && p[move]) { p[move](arg); }
	}

	// …and the same seam for a QUESTION. False before the panel exists, which is the safe answer
	// for the one caller: "is the panel folded" decides whether the walk owes it an unfold, and a
	// panel that is not built yet owes nothing.
	function panelAsks(move) {
		var p = window.plWlPanel;
		return !!(p && p[move] && p[move]());
	}

	// The first row's WORD in an open list — the thing to press to reach its dictionary entry
	// (the row's own handler, main.js buildRow: a plain click on the word navigates).
	function sectionWord(sec) {
		var row = sectionRow(sec);
		var text = row ? row.querySelector(".wlWordTextClass") : null;
		return visible(text) ? text : null;
	}

	// The list the reader has just made: the open section of the server's own "a list of your
	// own" kind. No id needed — the walk has just asked the panel to open that very list.
	function ownExpanded() {
		var sec = document.querySelector(".wlSectionClass.wlExpanded[data-wl-kind='USER']");
		return visible(sec) ? sec : null;
	}

	function ownExpandedHeader() {
		var sec = ownExpanded();
		var header = sec ? sec.querySelector(".wlHeaderClass") : null;
		return visible(header) ? header : null;
	}

	// One focus call, guarded: a field the walk asks the reader to type in is a field the walk
	// should have put the cursor in. (The wall's business is presses, not the keyboard — but a
	// press is how a field is normally focused, so without this the reader had to get past the
	// wall to reach a box the popup was telling them to type in.)
	function focusBox(el) {
		if (el && el.focus) { el.focus(); }
	}

	// Every level row that is actually on screen — the five bands as five rings. With compact
	// lists on there are no rows to ring and the chip strip is the one thing there is, which
	// firstLevelHeader already resolves to.
	function levelHeaders() {
		var secs = document.querySelectorAll(".wlSectionClass[data-wl-kind^='LEVEL_'] .wlHeaderClass");
		var out = [];
		for (var i = 0; i < secs.length; i++) { if (visible(secs[i])) { out.push(secs[i]); } }
		return out.length ? out : elemList(firstLevelHeader());
	}

	// …and the words of whichever band the reader has opened on the levels step.
	function levelWords() {
		var words = document.querySelectorAll(".wlSectionClass.wlExpanded[data-wl-kind^='LEVEL_'] .wlWordTextClass");
		var out = [];
		for (var i = 0; i < words.length; i++) { if (visible(words[i])) { out.push(words[i]); } }
		return out;
	}

	function topicPicker() {
		var pick = document.querySelector(".wlTopicPickClass");
		return visible(pick) ? pick : null;
	}

	function globalFilterBox() {
		var input = document.querySelector("#wlGlobalFilterRowId .wlFilterInputClass");
		return visible(input) ? input : null;
	}

	// The lists the search has found something in: a match badge above zero. The badges exist only
	// while the search is on (main.js paintMatchBadge; exitFilterMode removes them), so no badge = no
	// search = nothing here.
	function matchSections() {
		var out = [];
		var badges = document.querySelectorAll(".wlSectionClass > .wlHeaderClass .wlxMatchBadgeClass");
		for (var i = 0; i < badges.length; i++) {
			var sec = badges[i].closest(".wlSectionClass");
			if (Number(badges[i].textContent) > 0 && visible(sec)) { out.push(sec); }
		}
		return out;
	}

	function matchHeaders() {
		return matchSections().map(function (sec) { return sec.querySelector(".wlHeaderClass"); });
	}

	// …and the words such a list shows once it is opened, each a door into the dictionary (the
	// `wTopicWord` lesson, two steps back).
	function matchWords() {
		var out = [];
		matchSections().forEach(function (sec) {
			if (!sec.classList.contains("wlExpanded")) { return; }
			var words = sec.querySelectorAll(".wlWordTextClass");
			for (var i = 0; i < words.length; i++) { if (visible(words[i])) { out.push(words[i]); } }
		});
		return out;
	}

	function newListInput() {
		var input = document.querySelector(".wlNewRowClass input");
		return visible(input) ? input : null;
	}

	var WL = [
		{
			// What the panel IS, before any of it is opened — which is why the chapter opens by
			// CLOSING what chapter 2 left standing (owner report 2026-08-13: the Elementary list
			// was still open behind this box, "just a distraction"). The panel stays LIT: the
			// dozen steps after this one all happen inside it.
			id: "wPanel", kind: "info", place: "right",
			enter: function () { panelDo("collapse"); },
			titleKey: "obWlPanel_title", bodyKey: "obWlPanel_body",
			anchor: function () { return document.getElementById("wlPanelBoxId"); },
			lit: function () { return document.getElementById("wlPanelBoxId"); },
			press: function () { return []; }
		},
		{
			id: "wGarden", place: "right",
			titleKey: "obWlGarden_title", bodyKey: "obWlGarden_body",
			anchor: function () { return wlHeader("GARDEN"); },
			done: function () { return wlOpen("GARDEN"); }
		},
		{
			// …and the half of the garden a first-time reader cannot guess from an open list of
			// three words: it is not a list to curate, it is where learning DEPOSITS its words
			// (owner spec 2026-08-13). Said on the open garden, and the next step is what closes
			// it again.
			id: "wGardenAuto", kind: "info", place: "right",
			titleKey: "obWlGardenAuto_title", bodyKey: "obWlGardenAuto_body",
			when: function () { return wlOpen("GARDEN"); },
			anchor: function () { return openSection("GARDEN"); },
			lit: function () { return openSection("GARDEN"); },
			press: function () { return []; }
		},
		{
			// The graded lists as one lesson: one ring per band. The reader has already opened one
			// of them in chapter 2 — this says what the others are. My Garden is put away first: the
			// bands sit directly under it, and an open garden pushes them off the panel.
			// The headers are LIVE and the copy says so (owner, 2026-10-08: rings on labels that
			// cannot be pressed leave a reader wondering what they are) — open a band, look, open
			// another, the same as the search step's matched lists: a list's words are doors into
			// the dictionary, every control ON a header (the Add pill, the sort button) stays asleep.
			// Got it moves on; the next step tidies whatever was left open.
			id: "wLevels", kind: "info", place: "right",
			enter: function () { panelDo("collapse"); },
			titleKey: "obWlLevels_title", bodyKey: "obWlLevels_body",
			anchor: firstLevelHeader,
			rings: levelHeaders,
			lit: function () { return levelHeaders().concat(levelWords()); },
			press: function () { return levelHeaders().concat(levelWords()); },
			deny: function () {
				var out = [];
				levelHeaders().forEach(function (h) {
					var ctl = h.querySelectorAll("button, .wlAddPracticeClass, .wlHeaderActionsClass");
					for (var i = 0; i < ctl.length; i++) { out.push(ctl[i]); }
				});
				return out;
			}
		},
		{
			// ⛔ THREE TOMBSTONES (owner, 2026-10-08). The verb lesson — open "Verbs in use", type in
			// that list's own filter box, add a verb to Learn — left the tour: a conjugated form in
			// the Learn tray overwhelms a novice, and verb forms are not what a first-time reader
			// came for ("let them discover it later on their own"). The entries STAY in the list
			// with `gone` always true, because the stored `wl` cursor and the visit log's `tourstep`
			// marks are INDICES into it (first-run-redesign.md §8, the `mSound` precedent): deleting
			// them would move every stored cursor and relabel every logged step after them, before
			// and after the deploy alike. The step table reads them as skipped from now on; their
			// copy (obWlConjugation_*, obWlListFilter_*, obWlConjAdd_*) is gone from every bundle.
			// (The 2026-09-02 history: this chapter once went on to a SECOND verb list, "Verb
			// forms"/VERBS, dropped that day as too much for a first-run reader.)
			id: "wConjugation",
			gone: function () { return true; },
			anchor: function () { return null; }
		},
		{
			id: "wListFilter",
			gone: function () { return true; },
			anchor: function () { return null; }
		},
		{
			id: "wConjAdd",
			gone: function () { return true; },
			anchor: function () { return null; }
		},
		{
			// The Topics row is a launcher, and the walk pulls it: ONE step now where there were
			// two (owner spec 2026-08-13 — "combine: open the Topics automatically and suggest to
			// pick a topic"). One cell answers, the ringed one; the rest of the picker stays LIT,
			// because a reader has to see that there are eighteen of them. Lit-but-not-pressable
			// is the whole distinction: an unringed topic opens a list the chapter cannot follow.
			id: "wTopicPick", place: "right",
			enter: function () { panelDo("collapse"); panelDo("openTopics"); },
			titleKey: "obWlTopics_title", bodyKey: "obWlTopicPick_body",
			when: function () { return !!topicPicker(); },
			gone: function () { return !wlSection("TOPIC"); },
			anchor: function () {
				var pick = topicPicker();
				return pick ? pick.querySelector(".wlTopicCellClass") : null;
			},
			press: function () {
				var pick = topicPicker();
				return [pick ? pick.querySelector(".wlTopicCellClass") : null];
			},
			done: function () { return wlOpen("TOPIC") && !topicPicker(); }
		},
		{
			// A word in a list is a door into the dictionary — true of every row in the panel, and
			// taught here because this is the list the chapter has open (owner spec 2026-08-13).
			// One word answers, the ringed one, and pressing it is what closes the lesson.
			id: "wTopicWord", place: "right", click: true,
			titleKey: "obWlTopicWord_title", bodyKey: "obWlTopicWord_body",
			when: function () { return wlOpen("TOPIC"); },
			gone: function () { return !wlSection("TOPIC"); },
			anchor: function () { return sectionWord(openSection("TOPIC")); },
			press: function () { return [sectionWord(openSection("TOPIC"))]; }
		},
		{
			id: "wFilter", place: "below",
			titleKey: "obWlFilter_title", bodyKey: "obWlFilter_body",
			anchor: function () { return document.getElementById("wlFilterBtnId"); },
			done: function () { return !!globalFilterBox(); }
		},
		{
			// The box itself, and here the reader really types: the topic list is put away first,
			// so what a search across every list does has a whole panel to show it in. The letters
			// are cleared by the next step — a tour that made the reader tidy up after it would be
			// teaching the wrong lesson.
			id: "wFilterBox", kind: "info", place: "right",
			enter: function () { panelDo("collapse"); focusBox(globalFilterBox()); },
			titleKey: "obWlFilterBox_title", bodyKey: "obWlFilterBox_body",
			when: function () { return !!globalFilterBox(); },
			// Got it is WITHHELD until the reader has typed (owner, 2026-10-08: "the user would have
			// to type something" — the button used to move the tour on past a search nobody had
			// made). Still an info step, not an action one: the first letter would end an action
			// step, and the next step tidies the search away in the same tick, so the matches would
			// never be seen. Letters in the box are the whole test — NOT "the panel is searching":
			// the search index loads on the wire (applyGlobalFilter stays inert until WLFX.ready()),
			// and a reader who has typed must never be left without the button. See syncFoot.
			ready: function () {
				var box = globalFilterBox();
				return !!(box && box.value.trim());
			},
			anchor: globalFilterBox,
			// …and every list the letters were found in OPENS (owner, 2026-10-08 fifth pass: the badges
			// said "2 here, 13 there" and the wall kept every one of those lists shut). Only a whole
			// word opens a list by itself (main.js refreshFilterMode), so a reader typing "wom" has to
			// press the list — its header is live, its words are lit and are doors into the dictionary
			// like any list's. A list with no match stays dim and dead; every control ON a header
			// (the Add pill, a level list's sort button, an own list's rename/delete) stays asleep —
			// this step is about looking, and the header itself is what opens.
			lit: function () { return [globalFilterBox()].concat(matchSections()); },
			press: function () { return [globalFilterBox()].concat(matchHeaders(), matchWords()); },
			deny: function () {
				var out = [];
				matchHeaders().forEach(function (h) {
					var ctl = h.querySelectorAll("button, .wlAddPracticeClass, .wlHeaderActionsClass");
					for (var i = 0; i < ctl.length; i++) { out.push(ctl[i]); }
				});
				return out;
			}
		},
		{
			// My List, OPENED by the walk — chapter 2 opens it too, but that is a dozen steps and
			// a search ago, and this step is about what the list is FOR rather than about a row in
			// it. The search folds away with the same press, matches and all.
			id: "wMine", kind: "info", place: "right",
			enter: function () { panelDo("closeSearch"); panelDo("open", "DEFAULT"); },
			titleKey: "obWlMine_title", bodyKey: "obWlMine_body",
			anchor: function () {
				var sec = defaultSection();
				var header = sec ? sec.querySelector(".wlHeaderClass") : null;
				return visible(header) ? header : null;
			},
			lit: function () { return defaultExpanded() || defaultSection(); },
			press: function () { return []; }
		},
		{
			id: "wNew", place: "below",
			enter: function () { panelDo("collapse"); },
			titleKey: "obWlNew_title", bodyKey: "obWlNew_body",
			anchor: function () { return document.getElementById("wlNewListBtnId"); },
			done: function () { return !!newListInput(); }
		},
		{
			// The inline name field. Typed into, not pressed — the wall never touched the
			// keyboard — and finished either way: Enter creates the list, Esc drops the row.
			id: "wNewName", place: "right",
			titleKey: "obWlNewName_title", bodyKey: "obWlNewName_body",
			when: function () { return !!newListInput(); },
			anchor: newListInput,
			lit: newListInput,
			press: newListInput,
			// The cursor goes back in the field (tour-exhaustive-test.md D3). main.js startCreate
			// focuses it, but one tick before this step is live — the wall still belonged to `wNew`,
			// whose one pressable control is the "+", so onFocusIn blurred it and the reader typed
			// into nothing. `focus` puts it back the moment the field is on this step's own press
			// list, the drill's answer field's rule. NOT an `enter` hook: that runs before the step
			// is live, the wall would blur it again, and by then startCreate's blur handler is bound
			// — its deferred closeRow would take the row away under the reader.
			focus: true,
			done: function () { return !newListInput(); }
		},
		{
			// …and what the new list is for, said ON it: the walk opens the list the reader has
			// just named (owner spec 2026-08-13). The body carries the add-to-list icon inline,
			// because the sentence is about a button they will meet on a word screen rather than
			// anywhere in this panel. A reader who dropped the name row with Esc has no new list;
			// the panel then opens the newest own list they DO have, or none at all — and with
			// nothing open there is no anchor, so the step is skipped rather than pointing at a
			// list this chapter never mentioned.
			id: "wNewUse", kind: "info", place: "right",
			enter: function () { panelDo("openNewest"); },
			titleKey: "obWlNewUse_title",
			bodyText: function () {
				return strFormat(getRes("obWlNewUse_body"), plIcon("list-plus"));
			},
			anchor: ownExpandedHeader,
			lit: function () { return ownExpanded() || ownExpandedHeader(); },
			press: function () { return []; }
		}
		// (A `wDone` step stood here until 2026-09-01: "now the tour is complete — the ? here
		// replays this walk whenever you want it", pointed at the help button. The SIGN chapter
		// below says exactly that, with the menu OPEN and the row it means ringed, and it says it
		// on every ending rather than only on the one that walks this chapter to its end.)
	];

	// ── chapter 4: the sign-off, in the open help menu ───────────────────────────
	//
	// Owner spec 2026-09-01. Every other chapter ends by simply stopping, which leaves a reader who
	// has just been walked through the whole app with no idea where the walk lives. So the walk
	// signs off by OPENING the help menu itself and pointing at the row that answers it: the tour
	// is repeatable, from the "Guided tour" row.
	//
	// (A second step used to stand here — "Sorry, one last thing", pointed at the tooltip-size row
	// — removed 2026-09-02 on the owner's call along with the rest of that day's fly-to-help
	// rework: the corner shrink button already teaches the large-tooltip switch on its own, on
	// every hover, and saying it again here crowded a moment that is supposed to be one sentence.)
	//
	// One info step, no press at all, and since 2026-10-07 a NOTICE rather than an escort (owner
	// ALERT, docs/project/first-run-redesign.md R17): it raised the walk's wall like every other
	// step, so a reader who quit the tour and went straight for Start Learning found the app dimmed
	// and every press swallowed until they found the farewell's own button — "I went back to the
	// dimmed tour mode". The walk is over by the time this speaks, so nothing is dimmed or muted
	// under it, and any press outside the menu and its popup takes it down (`closeNotice`) and then
	// does what it was pressed for. (Until then the menu was lit so it could be read and dead so it
	// could not be wandered into — "the other controls are still disabled".) It runs on EVERY ending the walk
	// has: the quit card's "yes", the Word Lists offer's "no", that chapter walked to its end, and
	// the welcome card's "I'll explore on my own" (owner call, same day — a reader who declines the
	// tour is exactly the reader who has not been told the tour is repeatable).
	//
	// Once ever, tracked like the other chapters by a cursor in the attr, cleared by a replay.
	// A menu that goes (Esc, an outside press) takes the chapter with it — see `done` below.

	function hubMenuEl() {
		var menu = document.querySelector(".obHubMenuClass");
		return visible(menu) ? menu : null;
	}

	function hubRow(name) {
		var menu = hubMenuEl();
		return menu ? menu.querySelector("[data-ob-hub='" + name + "']") : null;
	}

	function hubGone() {
		return !hubMenuEl();
	}

	// The ringed row keeps FULL CONTRAST while the rest of the menu wears the asleep face. It is
	// the one place in the walk where lit-and-not-live is deliberate rather than a defect: nothing
	// in this menu is pressable, so a bright row promises nothing the reader can be caught out by,
	// and "highlighted" is what the owner's spec asks for (2026-09-01). Rides the `mark` hook, so
	// the class goes on with the glow and comes off with it.
	var HUB_ROW_MARK = { cls: "obAwakeClass" };

	var SIGN = [
		{
			id: "sReplay", kind: "info", place: "left", align: "start",
			titleKey: "obSignReplay_title", bodyKey: "obSignReplay_body",
			// The one cue in the app that FADES in: it is a beat of the sign-off's opening, not an
			// answer to a press (see cueOpts, and PL.tip.pin's `fadeInMs`).
			fadeIn: true,
			// No wall, no mute, and an outside press ends it (see the chapter's header).
			notice: true,
			// A menu that goes takes the chapter with it — and it is `done` rather than `gone`
			// because only a banked step moves the cursor: an Esc or a stray dismissal ENDS the
			// sign-off, instead of leaving it half-said with a cursor that would resume months
			// later the next time the reader happened to open the help menu.
			done: hubGone,
			anchor: function () { return hubRow("replay"); },
			mark: HUB_ROW_MARK,
			lit: hubMenuEl,
			press: function () { return []; }
		}
	];

	// ── chapter 1½: the entry tour, on the first meet card ───────────────────────
	//
	// Owner call 2026-08-12. The escorted run's first meet card flips to a full dictionary
	// entry, and a first-time reader is given no reason to believe there is anything in it
	// beyond the translation they came for. These nine steps read it with them, once ever
	// (cursor `meet` in the attr, cleared by "?" ▸ Replay the quick start), starting with the
	// press that does the flipping.
	//
	// The same two kinds as everywhere else — info reads and advances on Got it, an action step
	// waits for its own read-back — with three rules particular to this chapter:
	//
	//   • EVERY step is `onStage`. This is the one chapter that lives on a session screen, which
	//     the walk is otherwise silent on (recall focus is sacred). The exception is narrow and
	//     defensible: a meet card is not a question, it is the app's own reading screen —
	//     learning_session says as much by making it, and the recap, the only two Learn surfaces
	//     whose words are study links.
	//   • EVERY step names its own LIT area, and it is the entry rather than the panel around it
	//     (owner call 2026-08-12, screenshot by screenshot). The chapter's whole point is that the
	//     reader looks at the word and nothing else: "back to the words", the session's forward
	//     action and the word-list panel all go dim and dead for its duration, and the escape from
	//     a chapter is the quit card, not a stray button. What each step lets through is narrower
	//     still and spelled out per step in `press` — usually nothing, sometimes the entry's two
	//     harmless buttons (hear it, file it).
	//   • A step whose surface is not up YIELDS (the cursor stays put, the chapter resumes at the
	//     next meet card); a step whose surface IS up but whose anchor does not exist is SKIPPED.
	//     An entry with no examples must not strand the chapter, and a reader who walks out of
	//     the session mid-chapter must not lose the rest of it.
	var MEET = [
		{
			// Before the flip: the prompt that opens the entry. The chapter's one press that is not
			// about the entry itself, and the reason it is here is that a first-time reader arrives
			// on a word in a language they do not know with no statement of what to do next. Enter
			// works too and the button says so, so the copy names both.
			id: "mReveal", place: "below",
			titleKey: "obMeetReveal_title", bodyKey: "obMeetReveal_body",
			when: function () { return !!meetFrontCard(); },
			anchor: meetRevealButton,
			lit: meetFrontCard,
			press: function () {
				var card = meetFrontCard();
				return [meetRevealButton(), card && card.querySelector(".lrnSndLgClass")];
			},
			// The FLIP is the read-back, not the card merely going: a reader who walks out of the
			// session here has not done this step, and the chapter owes it to them on the next card.
			done: function () { return !!meetRichCard(); }
		},
		{
			// What the flip actually produced. The tour of the entry starts by naming it — and this
			// is the one step with nothing at all to press: read it, press Got it, move on.
			//
			// The box used to park in the VIEWPORT's top-left, so as not to sit on the card it is
			// describing. That read the owner's "top left corner" as the SCREEN's (report
			// 2026-08-13): it meant next to the highlighted element's own top-left corner. So the
			// box sits immediately left of the entry, top edges flush — same treatment as the
			// examples step below, and the same room: the panel beside the card is dim and empty.
			id: "mEntry", kind: "info", place: "left", align: "start",
			titleKey: "obMeetEntry_title", bodyKey: "obMeetEntry_body",
			when: function () { return !!meetRichCard(); },
			anchor: meetRichCard,
			lit: meetRichCard,
			// The entry's speaker, and nothing else: hearing the word takes the reader nowhere.
			press: function () { return [meetCardPart("#richSoundBtnId")]; }
		},
		{
			// ⛔ RETIRED, never shown (owner ruling R4, docs/project/first-run-redesign.md,
			// 2026-10-07): "the icon is pretty self-explanatory and we pronounce the word in any
			// case". The speaker itself still works — the steps either side of this one let it
			// through. KEPT IN THE LIST on purpose: the chain's stored cursors and the visit log's
			// `tourstep` marks are INDICES into it (VisitTrailService.TOUR_LISTS maps them back to
			// ids), so taking the entry out would move every stored cursor and re-label every
			// logged step after it. No anchor = the chapter's SKIP rule (meetStep) passes it.
			id: "mSound", kind: "info",
			when: function () { return !!meetRichCard(); },
			anchor: function () { return null; }
		},
		{
			// The entry's last word, and the only step whose copy names Esc — which is why it is
			// the one that RELEASES the Esc hold (`freeEsc`, read by holdsEscape): through the
			// rest of the chapter Esc belongs to the popup, and here it means what the meet card
			// has always made it mean.
			//
			// The copy names the button that is ON SCREEN — the card's own label, minus the arrow
			// (R4: it used to quote the popups' "Got it", which the German card spells „Alles klar“
			// beside a button reading „Verstanden →“).
			id: "mGotIt", place: "below", freeEsc: true,
			titleKey: "obMeetGotIt_title",
			bodyText: function () {
				var label = getRes("learnMeet_gotIt_label").replace(/\s*→\s*$/, "");
				return strFormat(getRes("obMeetGotIt_body"), escText(label));
			},
			when: function () { return !!meetRichCard(); },
			anchor: meetGotItButton,
			// The entry stays lit beside the button: the reader is being told to finish reading it.
			lit: function () { return [meetGotItButton(), meetRichCard()]; },
			press: function () { return [meetGotItButton(), meetCardPart("#richSoundBtnId")]; },
			// The card is gone AND the reader is not merely one word deeper inside it: a second
			// excursion from this last step must not read as "the card advanced".
			done: function () { return !meetRichCard() && !studyOverlay(); }
		},
		{
			// The hand-back (owner call 2026-08-31), and the chapter's real last word. "Got it →"
			// takes the reader off the entry and onto the NEXT word's unflipped card — the same
			// screen mReveal opened on, minus the walk that explained it. With the five entry
			// steps gone the chapter now ends there rather than deep inside a dictionary page, so
			// it says one sentence on that card and stops: the app's own prompt is what runs the
			// rest of the run, and this is what tells the reader to trust it.
			//
			// Anchored on the prompt itself rather than the card, because "the instructions on
			// screen" is a thing to point AT — and the same two controls the front card has always
			// allowed stay live under it (the prompt, the headword's speaker), so a reader who has
			// read enough can simply carry on without pressing Got it first.
			//
			// TWO ways it ends, and it needs both. Got it is the ordinary one. `done` is for the
			// reader who takes the invitation literally and just flips the card: an info step is
			// otherwise only ever finished by its own dismissal, and this one YIELDS rather than
			// skips when its surface goes (the chapter's rule) — so without a read-back the cursor
			// would sit on it for the rest of the run and raise the same sentence on every
			// remaining word.
			//
			// `freeEsc` for the same reason from the other side. Esc is held for the chapter's
			// benefit only on a FLIPPED card (learning_session asks while `richRevealOpen`), which
			// is a screen this step never speaks on — and a cursor parked here after the run's last
			// front card would otherwise swallow Esc on every flipped card that followed.
			id: "mNext", kind: "info", place: "below", freeEsc: true,
			titleKey: "obMeetNext_title", bodyKey: "obMeetNext_body",
			when: function () { return !!meetFrontCard(); },
			anchor: meetRevealButton,
			lit: meetFrontCard,
			press: function () {
				var card = meetFrontCard();
				return [meetRevealButton(), card && card.querySelector(".lrnSndLgClass")];
			},
			done: function () { return !!meetRichCard(); }
		}
	];

	// Stamped rather than written out step by step: both properties are true of the CHAPTER, not
	// of any one step, and a step appended later must not be able to forget them.
	for (var m = 0; m < MEET.length; m++) {
		MEET[m].onStage = true;      // this chapter is the walk's one licence to speak on a session screen
		MEET[m].noHover = true;      // …and nothing in it is taught by hovering (see hoverAllowed)
	}

	// ── arming ───────────────────────────────────────────────────────────────────

	// One cue system at a time: ?beacons=on re-arms the parked bloom engine for a
	// visit (dev/testing, the tips harness) — the guide yields there so each can be
	// watched in isolation, not layered on the same controls.
	var BEACONS_FORCED = /[?&]beacons=on\b/.test(window.location.search);

	// The LEADING condition for everything below is guided MODE
	// (tooltip-engine-consolidation.md §2.3): ONE switch governs all teaching. Turning the
	// mode off mid-walk disarms it on the spot (the plmodechange listener below syncs,
	// clearing glow and popup); turning it back on re-arms whatever still holds.
	// NB guided MODE, not the tour's guided RUN (`running`, tested in walkArmed).
	function guidedOn() {
		var t = T();
		return !!(PL.mode.guided() && !BEACONS_FORCED && t && t.armed());
	}

	// Chapter 2's cursor: null = not started, 0..n-1 = the live step, -1 = the tour is over.
	function tourAt() {
		var t = T();
		var v = t && t.tour();
		return typeof v === "number" ? v : null;
	}

	function tourLive() {
		var i = tourAt();
		return i !== null && i >= 0 && i < TOUR.length;
	}

	function tourOver() {
		var i = tourAt();
		return i === -1 || (i !== null && i >= TOUR.length);
	}

	// Chapter 3's cursor, read exactly like the other two. -1 covers both endings it can have —
	// walked to the end, or declined at the offer card — because neither is asked again.
	function wlAt() {
		var t = T();
		var v = t && t.wl();
		return typeof v === "number" ? v : null;
	}

	function wlLive() {
		var i = wlAt();
		return i !== null && i >= 0 && i < WL.length;
	}

	// Chapter 4's cursor, read exactly like the other three. null = never signed off, -1 = done.
	function signAt() {
		var t = T();
		var v = t && t.sign();
		return typeof v === "number" ? v : null;
	}

	function signLive() {
		var i = signAt();
		return i !== null && i >= 0 && i < SIGN.length;
	}

	// Chapter 1 arms for the declined welcome (`skipped`) AND for the guided run
	// (`running`, owner call 2026-08-08): the run used to have Driver.js beats of its own
	// and the stretch between them went unescorted; the walk covers the whole of it now.
	//
	// "Since when" is the mark the replay moves (owner report 2026-08-08: "?" ▸ replay
	// the quick start ▸ I'll explore on my own left the app cue-less). The retire test
	// is NOT drills == 0 — it is "no drill finished since the guide was last armed":
	// `guideFrom` is the drill count stamped at the arming moment, 0 for a first visit
	// (absent = 0, so the first-run behaviour is unchanged) and today's count for a
	// user who asks for the walk again after 32 drills. Deliberately not a reset of the
	// drill counter: the milestone-gated tips read it too, and a replay re-runs the
	// tour without forgetting what the user has done.
	//
	// The finished drill no longer ENDS the guide — it hands over to chapter 2 (see
	// practiceFinished), which is why the tour cursor disarms this half.
	function walkArmed() {
		var t = T();
		var s = t && t.state();
		return !!(guidedOn() && !tourLive() && !tourOver()
			&& (s === "skipped" || s === "running")
			&& t.drills() <= t.guideFrom());
	}

	// The walk is ESCORTING: a chapter is leading the reader somewhere, and the wall, the press mute
	// and the session-screen dim all exist for that and nothing else.
	function escorting() {
		return walkArmed() || (guidedOn() && (tourLive() || wlLive()));
	}

	// …and ARMED is that plus the sign-off, which still has a popup to show and a ring to hold but
	// leads nowhere: it is a notice (see the SIGN chapter), never a reason to dim or mute the app.
	function armed() {
		return escorting() || (guidedOn() && signLive());
	}

	// ── the sign-off, raised on every way the walk can end ───────────────────────
	//
	// Owner call 2026-09-02, spaced out into an explicit seven-beat choreography on
	// 2026-09-03, and its OPENING re-cut on 2026-09-04 into one continuous gesture instead of
	// four beats in a queue (owner call: "the dialog starts to move, the menu expands at the same
	// moment, the item colours as soon as it is visible, the popup appears with it, and the dialog
	// disappears IN the menu item"). On ANY opt-out (the quit card's "yes", the Word Lists offer's
	// "no", the welcome card's "I'll explore on my own"), all in ONE moment:
	//
	//   1. the card that just declined the walk flies, visibly SHRINKING as it travels — a "genie"
	//      close (PL.shrinkInto), not an empty outline arriving full-size — and it flies to the
	//      "Guided tour" ROW, not to the "?": the card vanishes INTO the thing the sequence is
	//      about, which is only a target because of 2., below;
	//   2. at the very same instant the icon lights up (a still violet glow) and the help menu
	//      GROWS OUT OF IT (PL.choreo.unfold). The row's resting rect is read in `onPlaced` —
	//      placed, full size, one frame before the unfold's transform touches it — which is the
	//      only geometry 1. can aim at while the menu is still growing;
	//   3. the moment the menu has finished arriving (beats().menuOpen, and the card is still
	//      travelling): the "Guided tour" row rings and the popup opens, both GRADUALLY — the row's
	//      ring is hand-marked (markSignRow) and `t.signSet(0)` hands the stage to the generic SIGN
	//      chain in the same tick, which shows the popup (tip.js fades it in) and re-applies the
	//      very same ring (a harmless no-op — the hand-marking already put it there). `fadeGlowIn`
	//      is what makes the violet arrive rather than snap. Not one frame earlier than the unfold's
	//      end, and not for tempo: the ring and the wall's hole are PINNED to a measured rect, and a
	//      row measured mid-unfold is a row still scaled and translated toward the "?".
	//      The popup's on-screen rect is stashed here too (`signPopupRect`), because by the time
	//      step 4 wants it, tip.js's own dismissal has already hidden the real box (see below).
	//
	// One TICK after the press, not zero: the press that ends the walk closes its own card in the
	// same tick, and PL.layers takes down everything stacked above a layer it closes — a menu opened
	// alongside would go down with the card. So the card is measured and cloned inside the press
	// (`snap`) and everything else runs from the next tick, which is the same moment to the eye.
	//
	// Closing, on "Got it" (`endSignOff`, the SIGN chain's `ended`):
	//
	//   4. a ghost of the popup flies and shrinks into the ringed row — and the row's ring fades
	//      OUT under it over the very same span (`fadeGlowOut`): the highlight lets go the moment
	//      the box starts to leave, and is gone as it arrives;
	//   5. the instant that lands, with no pause at all: the menu FOLDS BACK INTO the "?" it grew
	//      out of — the real menu, collapsing where it stands, not a ghost of it flying home
	//      (owner call 2026-09-03: unfolding and folding are one gesture seen twice, and a
	//      ghost-flight was a third thing pretending to be the second) — and only then does
	//      the layer come down;
	//   6. and the icon's violet, which is not a beat at all but the ENVELOPE around the other
	//      three: it starts dimming on the press that closed the popup and is fully out as the
	//      menu vanishes into it (beats().glowDown — the ghost's flight plus the fold). Its
	//      opening half is the same shape, started at step 2 and lasting until the ring and the
	//      popup have finished arriving (beats().glowUp). Owner call 2026-09-04: the button lights
	//      up across the whole opening and dims across the whole closing, rather than snapping to
	//      full three beats early and then waiting.
	//
	// The closing was re-cut with the opening (owner call 2026-09-04) and now has the same shape:
	// three beats that OVERLAP, each one starting the next as it begins rather than after it ends.
	// The gaps that used to separate them (`menuCloseGap`, `glowGap`) are gone — with the ghost
	// already landed and the ring already out, a pause there read as the sequence stalling rather
	// than as a beat.
	//
	// EVERY NUMBER BELOW COMES FROM PL.choreo, which the large tooltip's shrink pill reads too
	// (js/onboarding.js): the owner asked for the two sequences to run at one speed and in one
	// visual language, and two private tables would have drifted apart on the first tweak.
	// Change the tempo THERE, not here.
	//
	// Read through a FUNCTION, not into constants: this file is a classic script and runs before
	// the module seam sets window.PL (see js/pl/app.js — "never call PL.* at parse time"), so a
	// `var X = PL.choreo.MOTION` up here would be a TypeError on every page load.
	function beats() {
		var C = PL.choreo;
		return {
			cardFly: C.MOTION,             // 1 — outlasts the unfold on purpose: the violet has
			menuOpen: C.UNFOLD,            // 2   already begun (3) when the card lands in the row
			glowIn: C.GLOW_FADE,           // 3, the ring and the wall arriving rather than snapping
			popupClose: C.MOTION,          // 4, and the row's ring fades out over the same span
			menuClose: C.FOLD,             // 5, the instant 4 lands
			// 2 and 6, the icon's own violet: NOT a beat but the ENVELOPE around all of them (owner
			// call 2026-09-04). Up over the whole opening — the unfold, then the ring and popup
			// fading in behind it, which is the last thing to finish (the card lands inside that,
			// at cardFly). Down over the whole closing — the ghost's flight plus the fold it hands
			// over to. Both start with the press that begins their half.
			glowUp: C.UNFOLD + C.GLOW_FADE,
			glowDown: C.MOTION + C.FOLD
		};
	}

	// The popup's last on-screen rect (viewport space), stashed at step 3 — tip.js's own
	// `retireBox` sets the real box to `display:none` BEFORE telling anyone it went (see
	// `cueGone`'s doc comment), so by the time `endSignOff` runs this is the only geometry
	// step 4 has left to shrink away from.
	var signPopupRect = null;

	// Step 3's ring, by hand: the SAME asleep/awake pair the generic engine's own `mark` hook
	// (HUB_ROW_MARK) applies once its cursor moves — applied here first because it is the row's
	// own face, and the engine's `applyGlow` (the same tick, one call later) reapplies both classes
	// to the same elements, which is a no-op, not a flicker. The dim of the OTHER rows fades in on
	// its own: components/onboarding.css puts a .7s transition on the hub's items.
	function markSignRow() {
		var row = hubRow("replay");
		var menu = hubMenuEl();
		if (!row || !menu) { return; }
		menu.classList.add("obAsleepClass");
		row.classList.add("obAwakeClass");
	}

	// …and off again, by hand for the same reason: these two classes were put on directly rather
	// than through `markAsleep`, so `clearAsleep`'s bookkeeping does not know about them. Called at
	// the top of the fold (step 5) — the menu is on its way out either way, but it should wake up as
	// it goes rather than fold away still wearing the walk's one-row-lit face.
	function unmarkSignRow() {
		var menu = hubMenuEl();
		if (!menu) { return; }
		menu.classList.remove("obAsleepClass");
		var awake = menu.querySelector(".obAwakeClass");
		if (awake) { awake.classList.remove("obAwakeClass"); }
	}

	// Asked ONCE, ever: a cursor that is not null has already been through here, which covers both
	// endings (walked, or the menu dismissed) and every later ending of a replayed walk's
	// predecessor. A replay clears it and signs off again.
	function signOff(cardEl) {
		var t = T();
		// Guided MODE off is a reader the ringed row would be lying to (there is no large-tooltip
		// switch worth pointing at right now). Left unsaid rather than said wrong: the cursor
		// stays null, so a later ending with the mode on still gets it.
		if (!t || !t.armed() || !guidedOn() || signAt() !== null) { return; }
		if (!window.ONBOARDING || !ONBOARDING.openHelpHub) { return; }
		var btn = document.getElementById("helpBookmarkId");
		if (!btn) { return; }
		var B = beats();

		// The card, frozen at the size and place it still has THIS instant — every caller passes
		// `cardEl` before closing its own card, and the close runs before the next tick, when the
		// flight actually starts. A clone, so the original is free to vanish under it.
		var snap = (cardEl && document.contains(cardEl) && window.PL && PL.shrinkInto)
			? { node: cardEl.cloneNode(true), rect: cardEl.getBoundingClientRect() }
			: null;

		// Steps 1 + 2, in one moment: the icon lights, the menu grows out of it, and the card flies
		// into the row that menu is about — all started here, none of them waiting on another.
		function raiseHub() {
			if (signAt() !== null || !document.contains(btn)) { return; }
			var rowRect = null;
			// The tray first: a word opened into its entry on Learn/Practice folds back (owner,
			// 2026-10-08) — the farewell is about what to do next, said over the words to do it with.
			if (window.plFoldStagedRich) { window.plFoldStagedRich(); }
			// …and the icon starts lighting NOW, over the opening's full length: it is lit exactly
			// when the sequence finishes settling, not three beats early.
			ONBOARDING.helpGlow(btn, true, B.glowUp);
			ONBOARDING.openHelpHub(btn, {
				reopen: true,
				unfoldMs: B.menuOpen,
				// Esc belongs to the POPUP standing on this menu, not to the menu (see openHub):
				// dismissing the box that way is one of the two endings, and it has to run the same
				// closing as "Got it" — which it cannot if the menu it is about to fold is taken
				// down by the same key press.
				keepOnEsc: true,
				// The row at its RESTING rect: read one frame before the unfold's transform, which
				// is the only geometry the flight can aim at while the menu is still growing.
				onPlaced: function (menu) {
					var row = menu.querySelector("[data-ob-hub='replay']");
					rowRect = row ? row.getBoundingClientRect() : null;
				}
			});
			entered = {};
			quitAsked = false;
			blocked = 0;
			dismissed = null;
			// Step 1. Into the ROW when there is one to aim at, and into the "?" if the menu could
			// not be measured (a hub that refused to open) — the card still has somewhere to go.
			if (snap) { PL.shrinkInto(snap, rowRect || btn, B.cardFly); }
			// Step 3, the moment the menu has finished arriving and while the card is still in the
			// air — bail quietly if the reader has already closed the menu (nothing forces it to
			// stay open in between).
			setTimeout(function () {
				if (signAt() !== null || !hubMenuEl()) { return; }
				markSignRow();
				t.signSet(0);
				sync();
				fadeGlowIn(B.glowIn);
				var pin = pins[PIN_INFO];
				signPopupRect = pin && pin.el ? pin.el.getBoundingClientRect() : null;
			}, B.menuOpen);
		}

		// One tick, and not zero: the press that ends the walk closes its own card right after this
		// returns, and PL.layers takes down everything stacked above a layer it closes — a menu
		// opened in the same tick would go down with the card. With no card to fly there is no press
		// to wait out either, but the floor the fallback used before the flight existed is kept:
		// nothing here is racing a close, and the reader has just pressed something else entirely.
		setTimeout(raiseHub, snap ? 0 : 450);
	}

	// The violet arriving, and letting go, rather than snapping on and off (owner call 2026-09-04,
	// steps 3 and 4). The ring and the wall around it are display-toggled — pinned and shown in one
	// tick, which is right for a walk stepping from control to control — but the sign-off's is the
	// first glow of its sequence and the last: it has to fade UP under a card that is still flying,
	// and DOWN under the popup ghost leaving. Opacity only, and run after the engine has placed
	// both: nothing here touches the geometry, or the ring's own breathing keyframes (those animate
	// box-shadow, which composes with this).
	function glowBoxes() {
		var all = halos.concat(wall ? [wall] : []);
		var live = [];
		for (var i = 0; i < all.length; i++) {
			if (all[i] && all[i].animate && all[i].style.display !== "none") { live.push(all[i]); }
		}
		return live;
	}

	function fadeGlowIn(ms) {
		if (!ms || !window.PL || !PL.choreo || PL.choreo.reduced()) { return; }
		glowBoxes().forEach(function (b) {
			b.animate([{ opacity: 0 }, { opacity: 1 }], { duration: ms, easing: "ease-out" });
		});
	}

	// …and out. `fill: "forwards"` is what keeps the ring at nothing for the frames between the fade
	// ending and the bookkeeping taking it down — without it the ring snaps back to full for a frame
	// and the whole point is lost. The handle is parked ON the element because these elements are
	// REUSED by every later step: a held-at-zero animation left running would glow the next step
	// invisibly, so `letGoFade` cancels exactly this animation (never `getAnimations()`, which would
	// take the ring's own breathing CSS animation with it) as the ring goes down.
	function fadeGlowOut(ms) {
		if (!ms || !window.PL || !PL.choreo || PL.choreo.reduced()) { return; }
		glowBoxes().forEach(function (b) {
			b._plGlowFade = b.animate([{ opacity: 1 }, { opacity: 0 }],
				{ duration: ms, easing: "ease-in", fill: "forwards" });
		});
	}

	function letGoFade(el) {
		if (el && el._plGlowFade) {
			el._plGlowFade.cancel();
			el._plGlowFade = null;
		}
	}

	// …and its ending: steps 4–6, then the menu comes down for real — it was only ever open
	// because the chapter asked for it.
	function endSignOff() {
		var btn = document.getElementById("helpBookmarkId");
		var menu = hubMenuEl();
		var row = hubRow("replay");
		var popupRect = signPopupRect;
		var B = beats();
		signPopupRect = null;

		// The highlight, PUT BACK before it is faded — and this is not a workaround, it is where the
		// ring actually lives. `advanceStep` is what brings us here (Got it → cueGone → advanceStep),
		// and it takes the glow down TWICE around us: `hideCue(); clearGlow();` before it moves the
		// cursor, and a trailing `sync()` after `chainSet` has already called this function, which
		// finds nothing armed and clears it again. So the marks go back on now (they survive the
		// second pass — `clearAsleep` only knows about what `markAsleep` put on) and the RING goes
		// back on the far side of that sync, one frame later, together with the fade that is the
		// whole of step 4's second half. Both measured, in that order: without the re-mark the ghost
		// flew home to an unlit row; with the ring re-pinned INLINE the halo was still null for the
		// entire flight (`signoff-open-probe`, closing t=120ms).
		if (row) { markSignRow(); }

		function restoreRingAndFade() {
			if (!row || !document.contains(row)) { return; }
			pinRing(haloAt(0), row);
			fadeGlowOut(B.popupClose);
		}

		// Step 6, and like step 2 it is the ENVELOPE rather than a beat: the icon starts dimming on
		// the press that closes the popup and is fully out as the menu disappears into it
		// (beats().glowDown = the ghost's flight plus the fold). Toggling the class IS the fade —
		// the violet lives on an ::after overlay whose opacity is transitioned over a span the
		// caller names (it used to take a two-class reflow dance, and the gradient snapped anyway:
		// see the ⚠️ note on #helpBookmarkId::after).
		function fadeIconOut() {
			if (btn && window.ONBOARDING && ONBOARDING.helpGlow) {
				ONBOARDING.helpGlow(btn, false, B.glowDown);
			}
		}

		// Step 5, the instant step 4 lands and not a beat later (owner call 2026-09-04: the pause
		// there read as the sequence stalling, with the ghost already gone): the menu FOLDS back
		// into the "?" it grew out of and the layer comes down at the end of the fold — one gesture,
		// played backwards, rather than the ghost-flight this used to be (owner call 2026-09-03).
		// Nothing doubles up: it is the real menu collapsing where it stands, so there is no clone
		// to line up with an original, and `foldHelpHub` cuts its pointer events for the duration.
		//
		// The walk's own bookkeeping runs HERE, at the top of the fold, rather than at the end of
		// it: the ring it takes down has just finished fading out under the ghost (step 4), and holding
		// `.obAsleepClass` on a menu that is already folding would leave the other rows dim all the
		// way into the "?" — they lift on the hub's own .7s transition, which is the fold's length.
		// `signAt()` is already -1 by the time this function runs at all (chainSet writes the cursor
		// before calling `ended`), so nothing elsewhere reads the walk as still live in the
		// meantime — the exact moment this bookkeeping runs is cosmetic, not a
		// state-consistency risk.
		function closeMenu() {
			hideCue("menu");
			clearGlow();
			hideWall();
			unmarkSignRow();
			var canFold = menu && document.contains(menu) && btn
				&& window.ONBOARDING && ONBOARDING.foldHelpHub;
			if (canFold) {
				ONBOARDING.foldHelpHub(btn, B.menuClose);
			} else if (window.ONBOARDING && ONBOARDING.closeHelpHub) {
				ONBOARDING.closeHelpHub();
			}
		}

		// Step 4: a ghost of the popup (its last rect, stashed at step 3 — tip.js has already
		// hidden the real box by now) flies and shrinks into the ringed row, and the row's ring
		// fades out UNDER it over the same span — the highlight lets go the moment the box starts
		// to leave, and is gone as it arrives (owner call 2026-09-04).
		// Step 6 starts HERE, with step 4 and not with the fold: the press that closes the popup is
		// the beginning of the closing, and the glow is its envelope (see fadeIconOut). Ahead of the
		// branch so a sequence with no ghost to fly still dims rather than snapping off.
		fadeIconOut();

		if (popupRect && row && window.PL && PL.shrinkInto) {
			(window.requestAnimationFrame || function (f) { setTimeout(f, 16); })(restoreRingAndFade);
			PL.shrinkInto(popupRect, row, B.popupClose, closeMenu);
		} else {
			closeMenu();
		}
	}

	// ── the handover from chapter 2 into chapter 3 ───────────────────────────────
	//
	// The dictionary chapter's last step has just landed: the tray is full, "Start Learning!"
	// is one press away, and the panel the reader has used two rows of is still a mystery. So
	// the card SAYS the loop is complete and OFFERS the next chapter (owner spec 2026-08-13).
	//
	// Asked exactly once, and only in the session where the chapter actually ended: `wl` is a
	// new key, so every user who finished the tour before it existed carries wl == null, and a
	// test on the cursor alone would ambush all of them at their next page load. Declining
	// writes -1, which is also what walking the chapter to its end writes — neither is asked
	// again, and a replay clears both.
	var wlOffered = false;
	// Which door chapter 3 was entered by on this page: the offer card (false) or the Help hub's own
	// door (true). Read only by the walked-to-the-end marker (tips.walkFinished). Page-local: a hub
	// walk that resumes after a reload is recorded as "tour", which is acceptable for a marker.
	var wlFromHub = false;

	function offerWordLists() {
		var t = T();
		if (!t || wlOffered || wlAt() !== null) { return; }
		if (!window.ONBOARDING || !ONBOARDING.tourContinueCard) { endTour(); return; }
		wlOffered = true;
		// A BEAT LATER, and not for cosmetics: this runs inside the very press that finished
		// chapter 2, and that press also closes the staging menu — PL.layers takes down
		// everything stacked ABOVE a layer it closes, so a card raised in the same tick is
		// swallowed by the menu going away (2026-08-13, and it looked exactly like the card
		// never being raised at all). The half second is also what the card's first line is
		// about: by then the five words have landed and the tray says so.
		setTimeout(function () {
			ONBOARDING.tourContinueCard(function () {
				entered = {};   // a chapter that starts runs its steps' preparation again
				wlFromHub = false;
				t.wlSet(0);
				sync();
			}, function (cardEl) {
				// "No", Esc, the overlay: the walk ends here either way — this card is the last
				// thing chapter 2 had to say — and endTour records the ending for both chapters,
				// so nothing resumes by itself. A replay clears it and offers it again. `cardEl` is
				// undefined on Esc/the overlay (there is no card to shrink away); endTour's own
				// fallback covers that.
				endTour(cardEl, "declinedWl");
			});
			sync();   // the card is an .obCardClass: the stage rule takes wall and glow down
		}, 500);
	}

	// ── chapter 3 on its own, from the Help hub ──────────────────────────────────
	//
	// The panel walk is worth more than the one moment it is offered in (owner call 2026-08-13):
	// it is fifteen screens about a surface the reader uses every day, and until now the only way
	// to see it was to say "yes" to a card that appears once, at the end of a first-run tour.
	// So the hub carries a door of its own into it — no welcome card, no drill, no chapters 1–2:
	// this is the panel and nothing else.
	//
	// Three things have to be true before the first step can land, and this makes all three:
	//   • the PANEL must be on screen — the left rail is collapsible, and a chapter whose every
	//     anchor lives inside a 40px band would skip itself step by step to the end;
	//   • the walk's own bookkeeping must be fresh (`entered` especially — a step's `enter` hook
	//     runs once per arming, so a second viewing would tidy nothing);
	//   • the cursor goes to 0 whatever it held. -1 is the normal value here, being what both the
	//     decline and the walked-to-the-end write, and neither is a reason to refuse a re-run.
	//
	// Guided MODE and the attr itself are the hub's business, not this function's — it is called
	// with both already seen to (see onboarding.js startWordListTour).
	function startWordLists() {
		var t = T();
		if (!t || !t.armed()) { return false; }
		panelDo("reveal");
		wlOffered = true;    // this IS the offer, taken — chapter 2 must never re-ask
		wlFromHub = true;
		quitAsked = false;
		blocked = 0;
		entered = {};
		dismissed = null;
		t.wlSet(-1);         // …so wlSet(0) is a real change even if the cursor already sat at 0
		t.wlSet(0);
		sync();
		return true;
	}

	// Chapter 1½'s cursor, read exactly like the tour's. It rides INSIDE chapter 1's arming
	// (walkArmed) rather than beside it, which is what makes the chapter belong to the first
	// escorted loop and nothing else: the first finished drill starts the tour, walkArmed goes
	// false, and an unfinished entry chapter simply stops existing along with the walk that
	// raised it. Without that it would lie in wait in the attr and ambush a meet card months
	// later, the first time guided mode happened to be on.
	function meetAt() {
		var t = T();
		var v = t && t.meet();
		return typeof v === "number" ? v : null;
	}

	function meetLive() {
		var i = meetAt();
		return walkArmed() && i !== null && i >= 0 && i < MEET.length;
	}

	function meetOver() {
		var i = meetAt();
		return i === -1 || (i !== null && i >= MEET.length);
	}

	// Esc belongs to the chapter's popup while the chapter is explaining something.
	//
	// On a flipped meet card the Learn session gives Esc the meaning "Got it" — a rich view is
	// dismissed with Esc everywhere else in the app, so a reader of this one reaches for it
	// (learning_session.js, 2026-07-31). That is the right key on that screen and the wrong one
	// here: the walk's popups are Esc-dismissable too (PL.tip pins, and tip.js never consumes the
	// key), so one press would put the popup away AND skip the word the chapter is reading —
	// taking the rest of the chapter with it.
	//
	// So the session asks this before acting. It holds for every step but the one that says
	// otherwise — `mGotIt`, whose whole copy is "press Got it — or Esc — to move on": there the key
	// means what the card has always made it mean. Read off the step's own `freeEsc` rather than
	// off its POSITION in the array, which is what it used to be: mGotIt stopped being the last
	// entry the day mNext was appended after it, and a positional test would have quietly taken
	// the key away from the one step whose copy promises it. Not gated on a popup being up: an
	// info step's popup is already gone by the time the session's own handler runs (tip.js's
	// window capture listener fires first and hides it synchronously), so testing for one would
	// let exactly the press this exists to stop through.
	function holdsEscape() {
		var i = meetAt();
		return !!(meetLive() && !MEET[i].freeEsc);
	}

	// "3 words" is for the FIRST learn session only (owner call 2026-08-08, tightened
	// same day): true while no learn session has completed since the guide was last
	// armed — the ladder's recap painting bumps learns() (finishSession →
	// tips.learnFinished), and replay() moves learnsFrom the way it moves guideFrom.
	// A second scoop, or any scoop after the handoff, offers the normal 5 again.
	function firstLearn() {
		var t = T();
		return !!(t && t.learns() <= t.learnsFrom());
	}

	// An onboarding card (welcome, "are you trying to leave?") owns the stage OUTRIGHT: it is
	// modal, it brings its own backdrop, and a second dim behind it would fight both.
	//
	// The GARDEN VISTA is in here for the same reason since 2026-09-01, and it is a consequence of
	// the finish screen's bed becoming pressable on its step: pressing the bed opens a full-screen
	// overlay, and under a wall that stayed up it landed UNDER the dim with the walk's own halo and
	// popup floating over it — "click on the garden and it all looks like a mess" (owner report
	// 2026-08-12, which is why the bed was sealed then). Sealing is no longer the answer, standing
	// down is: the walk goes quiet for as long as the overlay is up and resumes on the step it was
	// on when it closes.
	//
	// `:not(.pl-fly-shrink)` is the sign-off's flying GHOST, and it is load-bearing since the
	// opening was re-cut (2026-09-04): the ghost is a clone of the card, class list and all, and it
	// is still in the air when the sign-off's own glow goes up — so without this the walk read a
	// card as owning the stage, stood down, and the ring and the popup never appeared at all
	// (measured: `signoff-open-probe`, halo and popup null for the whole sequence). A ghost is a
	// picture of a card that has already closed: pointer-events:none, no backdrop, nothing to fight.
	function cardLive() {
		return !!document.querySelector(".obCardClass:not(.pl-fly-shrink), .gardenVistaOverlay");
	}

	// …and the latch that hands the stage over and takes it back. A card raised by this module
	// tells the engine both ways (its own handlers call sync); the vista is not ours and says
	// nothing in either direction, so the tracker watches this flag instead — see track().
	var stageOwnedWas = false;

	// The stage the WALL covers when there is no step to point at — a live session screen (a
	// meet card, a drill question).
	//
	// The walk deliberately says nothing on these screens: recall focus is sacred, so no glow,
	// no popup. That used to mean no wall either, and the escort simply stopped escorting at
	// the "meet the words" screen — the word-list panel, the side panes and the account menu
	// all sat there, lit and live, one beat after a first-time reader had been walled off from
	// exactly those things (owner report 2026-08-12). The wall belongs to EVERY beat of the
	// escorted run, not only the ones with a control to press.
	//
	// What stays lit here is the central panel WHOLE, because on a session screen the panel IS
	// the workflow — the card, its options, its forward action and the reveal drill that renders
	// inside it. Everything around it goes dim and dead. Its "back to the words" is lit but NOT
	// pressable (sealedControl): leaving the run is not one of the ways out of the tour, the quit
	// card is, and pressing it again is what raises that card (BLOCKED_BEFORE_ASK).
	// The drill half asks for the answer BAND, not for the field inside it: a self-check card
	// swaps that field for a two-button row in the same band, and a wall that came down for
	// those cards would blink on and off through a session. (sessionScreenLive itself is left
	// alone — the tooltip engine reads it as "is a QUESTION on screen", which is a narrower
	// thing than "is a session screen up".)
	function sessionStage() {
		var live = sessionScreenLive() || visible(document.getElementById("answerBoxId"));
		return live ? document.getElementById("centralPanelId") : null;
	}

	// The practice finish screen: chapter 1 is done teaching the loop, chapter 2 takes
	// the stage right here (onboarding.js calls this from its finish hook, before the
	// drill counter moves). Only for a walk that was actually live — an experienced user
	// finishing a drill is not owed a dictionary tour.
	function practiceFinished() {
		var t = T();
		if (!t || !walkArmed() || tourAt() !== null) { return; }
		t.tourSet(0);
		// The lookup step is four steps away; ask for its example word now so the popup opens
		// with one rather than with the wordless fallback.
		fetchExampleWord();
	}

	// ── aria-live: popup openings are announced, not just painted ────────────────

	function announce(text) {
		if (!liveRegion) {
			liveRegion = document.createElement("div");
			liveRegion.className = "obLiveRegionClass";
			liveRegion.setAttribute("aria-live", "polite");
			document.body.appendChild(liveRegion);
		}
		liveRegion.textContent = text;
	}

	// ── the halo ─────────────────────────────────────────────────────────────────

	// The rings, by index. A step normally marks ONE element and uses ring 0; a step that
	// names `rings` marks several INDEPENDENT boxes — the entry chapter's examples step rings
	// the example block and the collocation block separately, because they are two things and
	// one box drawn round both would also enclose everything between them (owner call
	// 2026-08-13). Pooled rather than rebuilt: a step change re-pins them, and the surplus is
	// hidden rather than removed.
	function haloAt(i) {
		while (halos.length <= i) {
			var h = document.createElement("div");
			h.className = "obGuideHaloClass";
			h.setAttribute("aria-hidden", "true");
			document.body.appendChild(h);
			halos.push(h);
		}
		return halos[i];
	}

	function hideHalos() {
		for (var i = 0; i < halos.length; i++) {
			letGoFade(halos[i]);   // a ring parked at opacity 0 by the sign-off's fade-out
			halos[i].style.display = "none";
		}
	}

	// Everything this step rings: the anchor, plus whatever `rings` adds. The anchor is always
	// first — it is what the popup points at and what bringIntoView scrolls to.
	function ringTargets() {
		// A re-shown step (Back) rings its own controls instead, when its anchor is still on screen.
		if (peekRing && target) { return peekRing; }
		if (!target) { return []; }
		var els = [target];
		if (stepObj && stepObj.rings) {
			var extra = elemList(stepObj.rings());
			for (var i = 0; i < extra.length; i++) {
				if (els.indexOf(extra[i]) < 0 && visible(extra[i])) { els.push(extra[i]); }
			}
		}
		return els;
	}

	// The target's own 2-D transform WITHOUT its translation part, as a matrix() string —
	// or null when there is none (or it's a 3-D one we won't try to mirror). The
	// translation is dropped because the halo is placed from the target's on-screen
	// rect, which already includes it; re-applying it would shift the ring by that much
	// a second time. What's left is the linear part (skew/scale/rotate), which is exactly
	// the shape information the ring needs.
	function shapeTransform(cs) {
		var tf = cs.transform;
		if (!tf || tf === "none" || tf.indexOf("matrix(") !== 0) { return null; }
		var m = tf.slice(7, -1).split(",");
		if (m.length !== 6) { return null; }
		return "matrix(" + m[0] + "," + m[1] + "," + m[2] + "," + m[3] + ",0,0)";
	}

	// The ring must repeat the target's SILHOUETTE, not just its bounding box: the mode
	// tabs are leaning parallelogram chips (components/tabs.css skewX(-14deg)), and an
	// upright rectangle around one reads as a mis-aligned frame rather than a glow ON
	// that control. So the halo copies the radius AND the target's shape transform.
	//
	// Sizing then has to use the UNtransformed box (offsetWidth/offsetHeight) — the rect
	// the skew is applied TO — because getBoundingClientRect() reports the box AFTER the
	// skew, visibly wider than the chip's own frame. The two boxes share a centre: the
	// linear part of a transform about the default 50% 50% origin maps that origin to
	// itself. Centre + untransformed size + the same linear transform = the same shape.
	//
	// The figure set is the ring's OUTER size (the sheet is box-sizing:border-box), so the
	// stroke is counted in: pad + stroke on each side. Sizing to the pad alone left the
	// stroke growing outside the box while `left`/`top` still placed the border edge, which
	// hung the whole ring 2px low and right of its target.
	// ONE ring on ONE element. A ring whose element has scrolled out of its panel goes dark
	// rather than following it up under the app header: the popup stays (a pin holds on
	// through a scroll now — PL.tip's `sticky`), but a bright frame drawn over whatever
	// happens to be at those coordinates points at the wrong thing.
	// The radius a ring sitting `off` px outside (or, for a tab, inside) its target must carry to
	// stay concentric with it: every px of outward offset is a px of radius. Computed border-radius
	// is always a resolved list of px and/or % — each px token moves, each % is left alone (a 50%
	// corner is a proportion of the box, and the offset box already carries it), and a negative
	// result clamps at 0 so an inset ring around a lightly-rounded chip goes square rather than
	// inverting. Per-corner and elliptical ("6px 6px 0 0", "20px / 10px") shorthands come out right
	// token by token, which is why this does the arithmetic here instead of a single calc().
	function ringRadius(rad, off) {
		if (!rad) { return "6px"; }
		return rad.replace(/(-?[\d.]+)px/g, function (_, n) {
			return Math.max(0, parseFloat(n) + off) + "px";
		});
	}

	function pinRing(h, el) {
		if (PL.tip.outOfSight(el)) { h.style.display = "none"; return; }
		var r = el.getBoundingClientRect();
		var cs = getComputedStyle(el);
		// offsetWidth/Height are 0 for anything without a CSS box (an SVG child, say) —
		// keep the plain bounding-box ring there rather than collapsing to nothing.
		var shape = (el.offsetWidth && el.offsetHeight) ? shapeTransform(cs) : null;
		var isTab = el.classList.contains("centralTabClass");
		var grow = 2 * ((isTab ? TAB_GLOW_PAD : GLOW_PAD) + GLOW_RING);
		var w = (shape ? el.offsetWidth : r.width) + grow;
		var hgt = (shape ? el.offsetHeight : r.height) + grow;
		h.style.left = (r.left + r.width / 2 - w / 2) + "px";
		h.style.top = (r.top + r.height / 2 - hgt / 2) + "px";
		h.style.width = w + "px";
		h.style.height = hgt + "px";
		// The ring keeps the target's own silhouette (round buttons stay round, leaning
		// chips stay leaning) — GROWN by the same offset the box grew, exactly as the wall's
		// hole does (holeRadius). Copying the radius verbatim made the two outlines
		// non-concentric: offsetting a rounded rect outwards by p leaves the corner arcs
		// parallel only if their radius grows by p too, so an unchanged radius opened the
		// gap from p on the straight edges to p·√2 at each corner — the Start hero read as a
		// tight box inside a ring with four loose corners (owner report 2026-08-31).
		h.style.borderRadius = ringRadius(cs.borderRadius, grow / 2);
		h.style.transform = shape || "";
		h.classList.toggle("obGuideHaloInsetClass", isTab);
		h.style.display = "block";
	}

	function pinHalo() {
		var els = ringTargets();
		for (var i = 0; i < els.length; i++) { pinRing(haloAt(i), els[i]); }
		for (var k = els.length; k < halos.length; k++) { halos[k].style.display = "none"; }
		// The dim is pinned to the same rects on the same tick — one measure, two surfaces,
		// and no chance of the rings and the hole in the wall disagreeing by a frame.
		pinWall();
	}

	function clearGlow() {
		if (clickBound) { window.removeEventListener("click", clickBound, true); }
		clickBound = null;
		clearTimeout(settleTimer);
		settleTimer = null;
		clearMark();
		target = null;
		stepId = null;
		stepObj = null;
		hideHalos();
		hideWall();
	}

	// A control that has scrolled out of its panel is still perfectly eligible — its rect is
	// good, so `visible()` says yes — but the glow would land off-screen and the popup would
	// hide on arrival (a following box lets go of an anchor that left its clipping panel).
	// The step therefore scrolls to its own control: a long "My words" list pushes the level
	// bands below the fold, which is exactly where step 10 points.
	//
	// ONCE, when the step becomes live — never from the tracker. Past that the panel is the
	// reader's to scroll, and a walk that keeps yanking it back reads as a fight. Centred
	// rather than "nearest" so the control lands clear of the panel's sticky edges, with room
	// on all four sides for the popup to choose from.
	//
	// ⚠️ "The panel is the reader's to scroll" is narrower than it was since 2026-09-01: `onScroll`
	// mutes the wheel outside the LIT set, so what the reader may scroll is the lit thing, and this
	// one scroll is what guarantees they never need to scroll anything else. If a step is ever
	// reported as cramped — its control brought into view but the reader wanting to look around the
	// panel it sits in — the fix is to LIGHT that panel for the step, not to open the wheel back up
	// (the halo, the popup and every body-anchored popover are pinned to a measured rect and do not
	// follow a scroll; that is what the mute is there for).
	//
	// A step may ask for a scroll it would not otherwise get, by naming `scrollTo` — the side
	// of the viewport it wants its anchor brought to. `rowAdd` does (2026-10-09, D7: its row showed a
	// sliver at the panel's bottom edge on a 730px window); the entry chapter's examples block and
	// phrase gutter did until they were cut on 2026-08-31, and the
	// need they proved is general enough to keep the option (owner report 2026-08-13): a target
	// PARTLY on screen is not "out of sight", so nothing scrolled and the reader was shown the
	// top edge of the thing being explained with the rest below the fold. `scrollTo: "start"`
	// puts it at the top of the reading area, where its popup can sit beside it.
	function bringIntoView(el, step) {
		var want = step && step.scrollTo;
		if (!el || (!want && !PL.tip.outOfSight(el))) { return; }
		el.scrollIntoView({ block: want || "center", inline: "nearest" });
	}

	// A step may need its own control REVEALED before it can be pointed at: a word row's add
	// button is hidden until the row is hovered, and hovering is exactly what the wall now mutes
	// (see ROW_MARK). The class goes on while the step is live and comes off with the glow — the
	// only DOM outside its own halo, wall and popup that the walk ever writes.
	var marked = null, markedCls = null;

	function applyMark(step, el) {
		if (!step.mark) { return; }
		var host = step.mark.host ? step.mark.host(el) : el;
		if (!host) { return; }
		host.classList.add(step.mark.cls);
		marked = host;
		markedCls = step.mark.cls;
	}

	function clearMark() {
		if (marked && markedCls) { marked.classList.remove(markedCls); }
		marked = null;
		markedCls = null;
	}

	function applyGlow(step, el) {
		dropPeek("step");   // a Back being read ends when the step it was read from does
		clearGlow();
		stepId = step.id;
		stepObj = step;
		target = el;
		blocked = 0;        // a new step is a fresh start for "are you trying to leave?"
		dismissed = null;   // …and its popup is owed, whatever the reader did with the last one
		// Before the measuring: a mark that reveals hidden controls changes the rect the ring and
		// the hole are pinned to.
		applyMark(step, el);
		bringIntoView(el, step);
		// …and ONCE more when the layout has settled. A step usually goes live in the middle of
		// the change that made it eligible — a word-list section expanding pushes every row under
		// it down, and the accordion is a scrolling surface — so the position measured on the spot
		// can be a position the anchor is about to leave. Without this the ring simply went dark
		// (it hides on an anchor that has scrolled out of its panel) with the step still live: the
		// Topics row after two lists above it opened, in the chapter-3 probe. Still ONCE, not a
		// tracker: past this the panel is the reader's to scroll.
		clearTimeout(settleTimer);
		settleTimer = setTimeout(function () {
			settleTimer = null;
			if (target !== el) { return; }
			bringIntoView(el, step);
			pinHalo();
		}, 300);
		pinHalo();
		// A field the walk asks the reader to type in is a field the walk puts the cursor in
		// (chapter 3's rule, through its `enter` hooks). The drill's answer field needs it here,
		// at the moment ITS step goes live: training_session focuses the field as the card paints,
		// but that is one tick before this step is picked — the wall still belongs to the Start
		// step, whose press list is the Start button alone, so onFocusIn blurred the field right
		// back out, and the reader sat under a popup saying "type here" with no cursor anywhere
		// (owner, 2026-10-08: "users trip on this one, losing seconds trying to figure out where
		// to type"). Now that the step is live the field is on its own press list, so this focus
		// is one the wall lets stand.
		if (step.focus) { focusBox(el); }
		// A press with nothing to read back afterwards (add-to-list files the word and
		// leaves the page as it was) advances the chain on the control's own click.
		//
		// On WINDOW, in the CAPTURE phase, rather than on the control: a listener on the element
		// runs after the app's own, and the app's own is free to take the element down first —
		// which the staging flyout's row does (it closes the whole menu), and a listener removed
		// mid-dispatch is never called. The tour then sat on a step whose press had plainly
		// happened (2026-08-13). Capture at window is ahead of every app handler, so what
		// advances the chain is the press itself, not the state it happens to leave behind.
		// Nothing is prevented or consumed here — the control still does what it does.
		if (step.click && chainOfStep(step)) {
			clickBound = function (e) {
				// A press the WALL swallowed is not a press. stopPropagation does not stop the
				// other listeners on this same node (that would take stopImmediatePropagation),
				// so without this line a click the app never heard would still advance the chain —
				// which is exactly what a row-wide ring invites: the row is the anchor until its
				// hover-revealed add button appears, and a click on its background is a checkbox
				// tick the step is not asking for.
				if (!pressAllowed(e.target)) { return; }
				if (el === e.target || el.contains(e.target)) { advanceStep(step.id); }
			};
			window.addEventListener("click", clickBound, true);
		}
		showCue(step, el);
		startTracker();
	}

	// ── the popup: the walk's half of a PL.tip.pin ───────────────────────────────

	function res(keyOrFn) {
		return getRes(typeof keyOrFn === "function" ? keyOrFn() : keyOrFn);
	}

	function stepTitle(step) { return res(step.titleKey); }

	function stepBody(step) {
		return step.bodyText ? step.bodyText() : res(step.bodyKey);
	}

	function isInfo(step) { return step.kind === "info"; }

	// TWO pins, and the split is not cosmetic: `foot` and `escHide` are read when a pin
	// instance is BUILT, and there is one instance per name. The action box carries no
	// button at all (owner call 2026-08-11 — the press is the acknowledgement); the info
	// box carries Got it, and its dismissal is what moves the tour on. Which is also why
	// the build options live in ONE function: the add menu asks for these boxes (cueAnchors)
	// long before the first show, and a box first built without its foot would never grow
	// one.
	var PIN_ACTION = "obGuideStep", PIN_INFO = "obGuideInfo";

	function pinName(step) { return isInfo(step) ? PIN_INFO : PIN_ACTION; }

	// Both boxes carry a foot since 2026-10-08: a quiet Back on the left (both kinds) and the
	// primary on the right — Got it on the info box; on the action box a Continue that only shows
	// while a Back is being read (syncFoot). `obTipOkClass` stays on the INFO box's button alone:
	// the pins live on in the page hidden, and a harness asking for `.obGuideCueClass .obTipOkClass`
	// must still find exactly one.
	function pinBuildOpts(name) {
		var info = name === PIN_INFO;
		return {
			name: name,
			pinClass: "obGuideCueClass" + (info ? " obGuideInfoCueClass" : ""),
			foot: { buttons: [
				{ key: "back", label: getRes("obBack_button"),
					className: "pl-btn-quiet obTipBackClass", onClick: goBack },
				{ key: "ok", label: getRes(info ? "obGotIt_button" : "obContinue_button"),
					className: "pl-btn-pill obPrimaryBtnClass " + (info ? "obTipOkClass" : "obTipContinueClass"),
					onClick: okPressed }
			] }
		};
	}

	// The primary: Continue while a Back is being read, otherwise the info box's Got it.
	function okPressed(hide) {
		if (peekIdx !== null) { endPeek("continue"); return; }
		hide();
	}

	// The pin's option set. Everything the engine needs to draw a walk popup, and nothing
	// about which one — title/body come per show.
	//
	// `place` is a THUNK: PL.tip.place re-asks it at every placement, which is the whole
	// reason the box can follow its anchor. A box placed before the count submenu revealed
	// would otherwise keep being re-placed against a menu family that has since grown.
	function cueOpts(step, title, body) {
		var o = pinBuildOpts(pinName(step));
		o.title = title;
		o.body = body;
		// Got it, Esc, a dead anchor, an anchor scrolled off the viewport, a guided-mode
		// flip: every exit still has to clear the bookkeeping — and for an INFO step a
		// dismissal by the reader is also the advance (see cueGone).
		o.onHide = cueGone;
		o.place = cuePlaceOpts;
		// A cue is instant everywhere except the sign-off, whose box arrives inside a choreography
		// (step 3) next to a ring that is itself fading up — see the step's own `fadeIn`.
		if (step.fadeIn && PL.choreo) { o.fadeInMs = PL.choreo.GLOW_FADE; }
		return o;
	}

	// The pin boxes, built on demand and handed to the add menu's layer (cueAnchors)
	// before there is anything to say. Kept by name, so "take whatever is up down" costs no
	// box that was never needed — an empty pin parked in the page is a real thing to trip
	// over (its Got-it answers to `.obPrimaryBtnClass` like any other).
	var pins = {};

	function ensurePin(name) {
		if (!pins[name]) { pins[name] = PL.tip.pin(null, pinBuildOpts(name)); }
		return pins[name];
	}

	// WHY the box went matters. An INFO step is finished by its reader — Got it, or Esc —
	// and that is the only thing that advances it. Every other exit (the anchor died, the
	// mode flipped, a re-place lost the viewport) is just a box going away, and the step
	// is still pending: the next sync puts it back. The two are told apart by the anchor,
	// which is still on screen exactly when the reader is the one who closed the box.
	// `reason` comes from the engine (js/pl/tip.js): "user" for the Got-it button, "esc" for
	// the key, absent for every other way a box can go — the anchor dying, the anchor
	// scrolling out of its panel, a groupmate taking the group, a mode flip. The difference is
	// load-bearing and cannot be re-derived here: an anchor that scrolls out of view is still
	// perfectly `visible()`, and inferring the reader's intent from that made the tour advance
	// itself past the dictionary entry (smoke, 2026-08-11).
	function cueGone(reason) {
		if (cueFor && window.VISIT_LOG) { VISIT_LOG.mark("tipclosed", { step: cueFor, how: reason || "other" }); }
		var step = cueFor && stepById(cueFor);
		var byReader = !!(step && (reason === "user" || reason === "esc"));
		cueFor = null;
		cueAnchor = null;
		if (!byReader) { return; }
		// An INFO step is finished by being read: Got it, or Esc, and the chain moves on.
		if (isInfo(step)) { advanceStep(step.id); }
		// An ACTION step is not — its press is still owed. But Esc did mean something, so the
		// popup stays down until the walk moves to another step (without this the next sync
		// would put it straight back, and Esc would read as broken).
		// ⚠️ Under the wall this branch no longer runs for Esc at all: onKeyDown (below) takes the
		// key in the capture phase and raises the quit card instead (owner, 2026-10-07). What is
		// left here is the off-wall case — a popup with no walk to quit.
		else { dismissed = step.id; }
	}

	// Which of the five lists a step belongs to, as the visit log names it
	// (docs/project/visitor-click-recording-plan.md §3.4a).
	function vlList(step) {
		return TOUR.indexOf(step) >= 0 ? "tour" : MEET.indexOf(step) >= 0 ? "meet"
			: WL.indexOf(step) >= 0 ? "wl" : SIGN.indexOf(step) >= 0 ? "sign" : "steps";
	}

	function stepById(id) {
		for (var i = 0; i < TOUR.length; i++) { if (TOUR[i].id === id) { return TOUR[i]; } }
		for (var j = 0; j < MEET.length; j++) { if (MEET[j].id === id) { return MEET[j]; } }
		for (var w = 0; w < WL.length; w++) { if (WL[w].id === id) { return WL[w]; } }
		for (var k = 0; k < STEPS.length; k++) { if (STEPS[k].id === id) { return STEPS[k]; } }
		for (var g = 0; g < SIGN.length; g++) { if (SIGN[g].id === id) { return SIGN[g]; } }
		return null;
	}

	// The two SCRIPTED chains and the cursor each is driven by. Chapter 1 (STEPS) is not here:
	// it is predicate-driven and has no cursor to move — a step of it is live exactly while its
	// own state holds, and "advancing" is not a thing that happens to it.
	var CHAINS = [
		// `ended` is what happens when a chain runs off its own end. Chapter 2 hands over to the
		// offer card rather than simply stopping; the other two are finished when they finish.
		{ steps: TOUR, at: tourAt, set: function (i) { T().tourSet(i); }, ended: offerWordLists },
		{ steps: MEET, at: meetAt, set: function (i) { T().meetSet(i); } },
		// Chapter 3 hands over to the sign-off exactly as chapter 2 hands over to the offer card:
		// walking the panel to its end IS one of the walk's endings.
		// The marker comes first, while the attr still reads as the walk left it.
		// A walk taken to its END always gets its closing word, said before or not (owner report
		// 2026-10-08, fifth pass: "clicked Got it and the tour seems to finish abruptly"). The sign-off
		// is once-ever for the early exits, and any earlier ending — a decline, a quit, a previous walk
		// of this chapter from the help menu's own door, which no replay clears — had spent it, so the
		// last Got it took the ring down and left nothing on screen at all. Measured on 8071: cursor
		// -1 before → no menu, no popup, 3.5 s on.
		{ steps: WL, at: wlAt, set: function (i) { T().wlSet(i); }, ended: function () {
			var t = T();
			if (t && t.walkFinished) { t.walkFinished(wlFromHub ? "hub" : "tour"); }
			if (t && t.signSet) { t.signSet(null); }
			signOff();
		} },
		{ steps: SIGN, at: signAt, set: function (i) { T().signSet(i); }, ended: endSignOff }
	];

	// Move a cursor, and tell the chain when that move was off the end. EVERY write of a
	// chain cursor goes through here — the walkers bank a step whose done() already holds, and
	// advanceStep moves it on a press, and both can be the move that finishes a chapter.
	function chainSet(chain, i) {
		var end = i >= chain.steps.length;
		if (window.VISIT_LOG && chain.at() !== (end ? -1 : i)) { VISIT_LOG.mark("tourstep", { list: vlList(chain.steps[0]), at: end ? -1 : i }); }
		chain.set(end ? -1 : i);
		if (end && chain.ended) { chain.ended(); }
	}

	function chainOfStep(step) {
		for (var c = 0; c < CHAINS.length; c++) {
			if (CHAINS[c].steps.indexOf(step) >= 0) { return CHAINS[c]; }
		}
		return null;
	}

	function showCue(step, anchorEl) {
		if (!visible(anchorEl) || dismissed === step.id) { hideCue(visible(anchorEl) ? "dismissed" : "anchor"); return; }
		var fresh = cueFor !== step.id;
		var title = stepTitle(step);
		var body = stepBody(step);
		// A step of the OTHER kind may be holding the other pin — one explanation at a time.
		if (cueFor && pinName(stepById(cueFor) || step) !== pinName(step)) { hideCue("pinswap"); }
		// `cuePlace` before the show (the place thunk is asked during it), the rest AFTER:
		// showing one pin hides its groupmate, and that hide runs cueGone re-entrantly — which
		// would wipe the bookkeeping this line had just written.
		cuePlace = step.place;
		cueAlign = step.align || "center";
		// Set here and not with cueAnchor below for the same reason cuePlace is: the place thunk
		// is asked DURING the show, and cueAnchor is only assigned after it (a groupmate hiding
		// re-entrantly would wipe an earlier assignment). Without this the first placement of a
		// step ringing a big card would measure the PREVIOUS step's anchor and centre the box,
		// then jump to the top edge on the tracker's next tick.
		cueSized = anchorEl;
		// KEPT, not discarded: `pins` is what hideCue closes, and until 2026-08-12 only the add
		// menu's cueAnchors ever put a handle in it — so in every session where that menu never
		// opened, hideCue looped over nothing and "take whatever is up down" took nothing down.
		// It read as working because the usual way a step ends is its control disappearing, and
		// the engine drops a box whose anchor died all by itself. Where the control SURVIVES the
		// step — Start Exercise! becoming Pause Exercise as the drill opens — the box just stayed
		// on screen, explaining a button that no longer exists. One instance per name (PL.tip.pin's
		// own contract), so this is the same handle ensurePin would have built.
		pins[pinName(step)] = PL.tip.pin(anchorEl, cueOpts(step, title, body));
		cueFor = step.id;
		cueAnchor = anchorEl;
		// A popup that was up for less than FLASH_MS with no input from the reader was never read — the
		// walk passing through a state on its way somewhere (the welcome card closes a beat before
		// openLearnTab, and the `tab` step goes up for that beat): it is REPLACED, not kept, or Back would
		// lead to a popup nobody saw. A press or key in between means the reader moved the walk on — a
		// fast reader's step is kept, however briefly it was up.
		var last = shown[shown.length - 1];
		if (!step.notice && (!last || last.id !== step.id)) {
			if (last && Date.now() - last.t < FLASH_MS && lastInputAt < last.t) { shown.pop(); }
			// …and a flash between two showings of the SAME step leaves one entry, not two.
			var under = shown[shown.length - 1];
			if (!under || under.id !== step.id) { shown.push({ id: step.id, tab: activeTab(), t: Date.now() }); }
		}
		syncFoot();
		if (fresh && window.VISIT_LOG) { VISIT_LOG.mark("tip", { step: step.id, list: vlList(step) }); }
		if (fresh) { announce(title + ". " + body); }
	}

	// An INFO step may hold its Got it back until something has happened (`ready` on the step —
	// the search step waits for letters in the box and matches on screen, owner 2026-10-08). The
	// info pin is ONE box reused by every info step and its foot is built once (pinBuildOpts), so
	// the button is not rebuilt per step: it is HIDDEN while the step's ready() says no and shown
	// the moment it says yes. Asked on every show, which also clears a hold the previous step left,
	// and on every tracker tick, because the keystroke that satisfies it rides no hook.
	//
	// The same pass owns the rest of both feet (2026-10-08): Back shows only where it has somewhere
	// sensible to go (backTarget — never on a notice, never across a screen change), and while a Back
	// is being read the primary is Continue on either box and is never held.
	function syncFoot() {
		var step = cueFor ? stepById(cueFor) : null;
		var peeking = peekIdx !== null;
		var held = !peeking && !!(step && step.ready && !step.ready());
		var back = !!(step && !step.notice && backTarget());
		[PIN_INFO, PIN_ACTION].forEach(function (name) {
			// PL.tip.pin hands back { el, close } — `el` IS the box (the same node cueAnchors lends to
			// the add menu's layer), and it survives hides, so the foot inside it is found whether or
			// not the box is up at this instant.
			var pin = pins[name];
			var foot = pin && pin.el ? pin.el.querySelector(".obTipFootClass") : null;
			if (!foot) { return; }
			var ok = foot.querySelector("[data-foot='ok']");
			var bk = foot.querySelector("[data-foot='back']");
			var info = name === PIN_INFO;
			var label = getRes(peeking || !info ? "obContinue_button" : "obGotIt_button");
			if (ok && ok.textContent !== label) { ok.textContent = label; }
			footShow(ok, peeking || (info && !held));
			footShow(bk, back);
			footShow(foot, peeking || info && !held || back);
		});
	}

	// Inline, not a class: the buttons wear `.pl-btn-*` display rules from another layer, and the
	// foot's own display is the box's flex column's business.
	function footShow(node, on) {
		if (!node) { return; }
		var want = on ? "" : "none";
		if (node.style.display !== want) { node.style.display = want; }
	}

	// ── Back: one screen back, or no Back at all (owner, 2026-10-08, second round) ─────────────────
	//
	// The first cut (that morning) re-showed the previous EXPLANATION, wherever it had been given.
	// The owner's localhost pass showed why that is not enough: on a flipped card it put "have a
	// guess, then click the prompt" over the answer, and on the run's first popup it led somewhere
	// different on every loop (a step the page had flashed for one frame while the replay emptied the
	// trays). The rule now is what a reader expects of a Back — the screen they were on a moment ago:
	//
	//   • SAME SCREEN: the previous popup belongs to the same list, the same tab, and its control is
	//     still on screen → it is re-shown on the box that is up (`peek`), rung on that control; nothing
	//     moves, Continue returns to the live step, and any press the live step allows ends the
	//     reading (onPress). Back again walks further only while the screen stays the same.
	//   • THE WALK'S FIRST POPUP (the welcome card was answered on this page) → the welcome card again:
	//     startOver, no confirming card — nothing is lost that the welcome card does not re-offer.
	//     Only an EXPLANATION is re-shown (an info step with nothing to wait for — owner, 2026-10-08,
	//     third round). A popup that asked for a press or for typing names a thing the screen can no
	//     longer take: its press has already happened ("Add to list" over a word already filed) or the
	//     wall now holds its control ("type a word" over a field the live step does not open). Re-shown,
	//     it reads as an invitation the page then refuses.
	//   • THREE REAL UNDOS: the first word flipped → its unflipped face (LRN.unflipMeet, the meet
	//     chapter's cursor back to mReveal); unflipped first word → the tray it was started from
	//     (`start`); the drill's "type here" → the drill stopped and Start Exercise! again (`drill`,
	//     owner 2026-10-08 — the same exit the drill's own Stop takes). The history is cut back with
	//     them, so Back there is exactly as it was.
	//   • THE DICTIONARY CHAPTER'S VIEW MOVES (owner, 2026-10-08, fifth round: "I think we can"):
	//     where the press the previous popup asked for only changed what is ON SCREEN — a tab, an
	//     open list, a looked-up entry — Back puts the screen back and that popup with it, its
	//     cursor rewound (TOUR_BACK). Not where the press changed the reader's DATA (a word filed in
	//     My List, a word sent to Learn): that screen cannot be had back without taking the word
	//     away again — and not in the staging menu either, whose layer has its own hover machinery
	//     opening and closing it.
	//   • ANYTHING ELSE — a screen changed in between (a session started, a word filed): no Back
	//     button. A Back that cannot take the reader back must not be offered.
	//
	// What a re-shown box is composed FROM is the step's own title and body — never its `enter`,
	// `done` or `click`: re-reading a step must not prepare a panel, bank a cursor or arm a press.

	// Keyed by the LIVE step; `from` is the step that must be the one shown just before it (a step
	// the walker skipped — `learnTab` for a reader already on Learn — leaves no Back on the next).
	// `undo` gets that earlier step's history entry, whose `tab` is where it was shown.
	function canShowTab() { return !!window.plShowTab; }
	function canFold() { return !!(window.plWlPanel && plWlPanel.collapse); }
	var TOUR_BACK = {
		// the Dictionary tab → the tab the reader pressed it from
		lookup: { from: "dictTab", can: canShowTab, undo: function (e) { plShowTab(e.tab); } },
		// the looked-up entry → the lookup list, the field focused again
		wlAdd: { from: "lookup", can: canShowTab, undo: function () { plShowTab("dict"); } },
		// My List opened → folded
		rowAdd: { from: "myList", can: canFold, undo: function () { panelDo("collapse"); } },
		// the Learn tab → the tab the reader pressed it from
		band: { from: "learnTab", can: canShowTab, undo: function (e) { plShowTab(e.tab); } },
		// the band's list opened → folded
		bandAdd: { from: "band", can: canFold, undo: function () { panelDo("collapse"); } },
		// the Topics picker opened by the walk → put away, the graded lists live again (owner,
		// 2026-10-08: a re-shown levels box over a still-open picker whose bands could not be
		// pressed — the live step was still the picker's)
		wTopicPick: { from: "wLevels", can: canFold, undo: function () { panelDo("collapse"); } }
	};

	// The step a Back has just rewound to, held against its own `done()` for a moment. Some undos land
	// a tick late — the lookup list comes back over the wire, so the looked-up entry the `lookup`
	// step's done() reads is still on screen right after the press — and the walker banks a done
	// step on sight, which would carry the cursor straight forward again. Released the first time
	// done() reads false (the undo has landed), or after BACK_HOLD_MS (it never will: the walk goes
	// on rather than holding a popup over a press that has happened).
	var BACK_HOLD_MS = 3000;
	var backHold = null;

	// Asked BEFORE the step's done() on every pass, never behind an `&&` that only reaches it when
	// done() is true: the release is the pass that sees done() false, and a hold nobody looks at in
	// that moment outlives it — the reader re-presses within the 3 s and the press is held back too.
	function heldBack(step) {
		if (!backHold || backHold.id !== step.id) { return false; }
		if (!(step.done && step.done()) || Date.now() - backHold.t > BACK_HOLD_MS) {
			backHold = null;
			return false;
		}
		return true;
	}

	function backTarget() {
		var live = peekLive || cueFor;
		var step = live && stepById(live);
		if (!step || step.notice) { return null; }
		// The popup ON SCREEN is the one Back answers for — the re-shown one while peeking. Its undo
		// is the same as when it was live (owner, 2026-10-08: Back on a re-shown "The whole word" —
		// reached by Back from Got it — offered nothing, because only the LIVE step's undo was asked).
		var at = peekIdx !== null ? peekIdx : lastShown(live);
		if (at < 0) { return null; }
		var here = shown[at].id;
		if (here === "mEntry" && window.LRN && LRN.canUnflipMeet && LRN.canUnflipMeet()) {
			return { undo: "unflip" };
		}
		if (here === "mReveal" && at > 0 && shown[at - 1].id === "start" && window.LRN && LRN.backToTray) {
			return { undo: "tray", to: at - 1 };
		}
		if (here === "type" && at > 0 && shown[at - 1].id === "drill" && window.leaveExerciseView) {
			return { undo: "drill", to: at - 1 };
		}
		var tb = peekIdx === null && at > 0 ? TOUR_BACK[here] : null;
		if (tb && shown[at - 1].id === tb.from && tb.can()) {
			return { undo: "tour", to: at - 1 };
		}
		if (peekIdx !== null) {
			var further = samePrev(peekIdx, step);
			return further >= 0 ? { peek: further } : null;
		}
		var k = samePrev(at, step);
		if (k >= 0) { return { peek: k }; }
		return (walkHere && at === 0) ? { welcome: true } : null;
	}

	function lastShown(id) {
		for (var i = shown.length - 1; i >= 0; i--) { if (shown[i].id === id) { return i; } }
		return -1;
	}

	// The entry just before `at`, if it is the SAME SCREEN as the live step — or -1. Only the entry
	// immediately before: an entry from another list or tab in between means the screen changed, and
	// what lies beyond it is a screen the reader has left.
	function samePrev(at, liveStep) {
		var k = at - 1;
		if (k < 0) { return -1; }
		var e = shown[k], step = stepById(e.id);
		if (!step || e.id === liveStep.id || vlList(step) !== vlList(liveStep) || e.tab !== activeTab()) { return -1; }
		// An explanation only: a step that asked for a press or for typing is not re-shown (see above).
		if (!isInfo(step) || step.ready) { return -1; }
		var a = null;
		try { a = step.anchor(); } catch (x) { /* asked out of its own context: not on screen */ }
		return (a && visible(a) && !PL.tip.outOfSight(a)) ? k : -1;
	}

	function goBack() {
		var live = peekLive || cueFor;
		if (!live) { return; }
		var t = backTarget();
		if (window.VISIT_LOG) {
			VISIT_LOG.mark("back", { step: live, to: t && t.peek != null ? shown[t.peek].id : null,
				how: !t ? "none" : t.peek != null ? "peek" : t.welcome ? "welcome" : t.undo });
		}
		if (!t) { return; }
		if (t.peek != null) { peek(t.peek); return; }
		if (t.welcome) { startOver("back"); return; }
		hideCue("back");
		if (t.undo === "unflip") {
			LRN.unflipMeet();
			var at = lastShown("mReveal");
			shown.length = at >= 0 ? at + 1 : Math.max(0, shown.length - 1);
			var chain = chainOfStep(MEET[0]);
			chainSet(chain, 0);   // mReveal: its `done` (the flip) re-banks the moment the card flips again
		} else if (t.undo === "drill") {
			shown.length = t.to + 1;
			window.leaveExerciseView();       // the drill's own Stop: the Practice tab, Start Exercise! live again
		} else if (t.undo === "tour") {
			var prev = shown[t.to];
			shown.length = t.to + 1;
			// The step comes back as it first came: its preparation runs again (the lookup field
			// takes the cursor), and an Esc that had put its box away is forgotten.
			delete entered[prev.id];
			// …and so does the step Back left: its undo has just put away what its preparation set up
			// (the Topics picker), and reached again it must set it up again, not wait for it.
			delete entered[live];
			dismissed = null;
			// Cursor and hold FIRST, the undo after: the undo runs through the app's own handlers,
			// which call straight back into sync() — and with the cursor still on the live step that
			// sync re-showed it (the entry was still up) for one tick, which put a second copy of the
			// rewound step in the history and cost it its own Back (measured on 8071).
			backHold = { id: prev.id, t: Date.now() };
			var back = chainOfStep(stepById(prev.id));
			chainSet(back, back.steps.indexOf(stepById(prev.id)));
			TOUR_BACK[live].undo(prev);
		} else {
			shown.length = t.to + 1;
			if (LRN.leave) { LRN.leave(); }   // the sound, the Enter binding, any reveal drill
			LRN.backToTray();                 // …and the plain Learn tab, the words still in the tray
		}
		sync();
	}

	// The welcome card was answered (onboarding.js): the walk's history starts here, empty, and its
	// first popup is the one whose Back returns to the card. Anything the page showed before — a step
	// that flashed while a replay emptied the trays — belongs to no walk the reader has seen.
	function runStarted() {
		dropPeek("run");
		shown = [];
		walkHere = true;
	}

	function peek(k) {
		var step = stepById(shown[k].id);
		var live = stepById(peekLive || cueFor);
		if (!step || !live || !target) { return; }
		var title, body, a = null, extra = [];
		try {
			title = stepTitle(step);
			body = stepBody(step);
			a = step.anchor();
			if (step.rings) { extra = elemList(step.rings()); }
		} catch (e) { /* a step asked out of its own context: no anchor, and the text below decides */ }
		if (!title && !body) { return; }
		if (peekIdx === null) { peekLive = live.id; }
		peekIdx = k;
		var onScreen = !!(a && visible(a) && !PL.tip.outOfSight(a));
		peekRing = onScreen ? [a].concat(extra.filter(function (x) { return x !== a && visible(x); })) : null;
		var anchorEl = onScreen ? a : target;
		cuePlace = step.place;
		cueAlign = step.align || "center";
		cueSized = anchorEl;
		var o = cueOpts(live, title || "", body || "");
		o.onHide = peekGone;
		delete o.fadeInMs;
		pins[pinName(live)] = PL.tip.pin(anchorEl, o);
		cueAnchor = anchorEl;
		syncFoot();
		pinHalo();
		announce((title || "") + ". " + (body || ""));
	}

	// The re-shown box went by some route other than its own buttons (its anchor died, the engine
	// let go of it): the live step's own box comes back on the next tick.
	function peekGone() {
		if (peekIdx === null) { return; }
		dropPeek("anchor");
		cueFor = null;
		setTimeout(sync, 0);
	}

	// Continue, or a press the live step allows: back to the live step's own box and ring.
	function endPeek(how) {
		if (peekIdx === null) { return; }
		var live = stepById(peekLive);
		dropPeek(how);
		if (live && stepObj === live && target) {
			showCue(live, target);
			pinHalo();
		} else {
			cueFor = null;
			sync();
		}
	}

	// The bookkeeping half alone, for the routes where something else decides what shows next.
	function dropPeek(how) {
		if (peekIdx === null) { return; }
		if (window.VISIT_LOG) { VISIT_LOG.mark("peekend", { how: how, step: peekLive }); }
		peekIdx = null;
		peekLive = null;
		peekRing = null;
	}

	// "Start the tour over" — Back on the walk's first popup, or the quit card's third button. The walk
	// comes down the way endTour takes it down (no sign-off, no early-exit marker: the reader is not
	// leaving), off any session screen, and then the hub's own replay: the welcome card, emptied
	// trays, new words. The history goes too — the replayed walk's Back starts from its own start
	// (runStarted, when the card is answered).
	function startOver(how) {
		if (window.VISIT_LOG) { VISIT_LOG.mark("restart", { how: how, step: stepId || (stageLit ? "stage" : null) }); }
		quitAsked = false;
		blocked = 0;
		stageLit = null;
		entered = {};
		shown = [];
		walkHere = false;
		refoldPanel();
		hideCue("restart");
		clearGlow();
		hideWall();
		leaveSessions();
		if (window.ONBOARDING && ONBOARDING.replay) { ONBOARDING.replay(); }
	}

	// Both boxes, unconditionally: which of the two is up is bookkeeping this does not need
	// to be right about, and closing an already-hidden pin is a no-op. cueFor is cleared
	// FIRST so the onHide that follows reads it as "the walk took this one down", never as
	// a reader's dismissal.
	//
	// `why` names the caller's branch, so the visit log can tell one walk exit from another — until
	// 2026-10-07 every one of them wrote the same bare how=walk, and a popup seen to BLINK on prod
	// (closed and re-shown for the same step within seconds, no input between; one visitor, five
	// times in 28 s) could not say which branch had taken it down (docs/project/tour-popup-blink.md).
	function hideCue(why) {
		if (cueFor && window.VISIT_LOG) { VISIT_LOG.mark("tipclosed", walkCloseDetail(why)); }
		// A Back being read goes with the box (a step change, a card, the walk's end): nothing is
		// re-shown, the next show is the live step's own.
		dropPeek(why || "other");
		cueFor = null;
		cueAnchor = null;
		for (var name in pins) {
			if (pins.hasOwnProperty(name)) { pins[name].close(); }
		}
	}

	// The closing step's own predicates, read at the instant the walk took its popup down — the
	// same three sync() asks (`w` its context, `a` its anchor on screen, `d` its read-back), plus
	// the two outside states that silence a step without any of those changing: a card owning the
	// stage, and a meet card's excursion being open. Booleans, a flat object, nothing typed by the
	// reader — the visit log's detail contract (VisitBatch.flatObject). Guarded: a predicate asked
	// out of its context must never cost the close itself.
	function walkCloseDetail(why) {
		var d = { step: cueFor, how: "walk", why: why || "other" };
		try {
			var step = stepById(cueFor);
			if (step) {
				if (step.when) { d.w = !!step.when(); }
				d.a = visible(step.anchor());
				if (step.done) { d.d = !!step.done(); }
			}
			d.card = cardLive();
			d.drill = drillOpen();
		} catch (e) { d.err = true; }
		return d;
	}

	// EVERY open popover (the add menu, its revealed submenu, a sort/move menu) as one box
	// the popup must not cover.
	//
	// This used to apply only while the popup's own anchor lived inside the menu — the theory
	// being that a box about something else may float wherever it likes. It may not: the
	// owner opened My Garden's Add menu with the Elementary cue up and the cue landed square
	// on the menu, hiding half its items (report 2026-07-27, second pass). A popup over an
	// open menu is a mess whether or not the two are about the same thing.
	function menuFamilyRect() {
		var pops = document.querySelectorAll(".wlPopoverClass");
		var box = null;
		for (var i = 0; i < pops.length; i++) {
			if (!visible(pops[i])) { continue; }
			var r = pops[i].getBoundingClientRect();
			if (!r.width || !r.height) { continue; }
			if (!box) {
				box = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
			} else {
				box.left = Math.min(box.left, r.left);
				box.top = Math.min(box.top, r.top);
				box.right = Math.max(box.right, r.right);
				box.bottom = Math.max(box.bottom, r.bottom);
			}
		}
		return box;
	}

	// The fallback side for each preference. Every side a step may name is in here: an unmapped
	// one would hand `candidate` an undefined kind, which lands on the "at" branch — the box's
	// own corner ON the anchor's, i.e. squarely over the thing being explained.
	var OPPOSITE = { above: "below", below: "above", left: "right", right: "left" };

	// Preferred side first, 10px off the anchor, 8px viewport margins — the beacon
	// popover's manners grown to four sides, and PL.tip.place's job since PR 1/7. WHILE ANY
	// POPOVER IS OPEN the stronger rule applies to every popup, wherever its anchor lives:
	// the first candidate that covers NO part of the open menu family wins (the panel sits
	// near the viewport's left edge, so "left" often has no room and a naive flip would
	// drop the box straight onto the menu — both harness runs and both owner reports
	// caught that). place() states that as `avoid`: avoidance then decides instead of
	// the fit test, and the family's flanks (avoid-left/right/below, y still centred on
	// the anchor row so the box still reads as the ROW's) are the fallbacks.
	//
	// Re-asked on EVERY placement (the pin follows its anchor), so the family it avoids is
	// always the one on screen now — never the one that was open when the box popped.
	function cuePlaceOpts() {
		var fam = menuFamilyRect();
		return {
			sides: fam ? [cuePlace, "avoid-left", "avoid-right", "avoid-below"]
				: [cuePlace, OPPOSITE[cuePlace]],
			fallback: fam ? "avoid-right" : OPPOSITE[cuePlace],
			gap: 10,
			margin: 8,
			align: alignFor(),
			avoid: fam ? [fam] : []
		};
	}

	// A step that rings something HUGE gets its box at the ring's top corner, whatever it asked
	// for. Centring is right for a control — the box lines up with the thing and reads as its
	// label — and wrong for a half-screen card or a full-height panel, where the middle is 350px
	// of empty air from the corner the reader is actually looking at (owner rule 2026-08-13: the
	// popup belongs NEXT TO the top-left corner of the highlighted element). Measured, not
	// guessed: `tour-popup-adjacency-probe.mjs` found the Word Lists step's box floating 346px
	// below its own panel's top.
	//
	// A backstop rather than a per-step clause on purpose: this is the third report of the same
	// shape (the entry chapter's examples, its "whole word" and "to go back" steps), and a step
	// appended later must not be able to re-earn it. A step may still say `align` — "start" is
	// what this arrives at anyway, and nothing needs "center" on a ring this size.
	var BIG_RING = 0.5;   // of the window's height

	function alignFor() {
		var stated = cueAlign || "center";
		var el = cueSized || cueAnchor;
		if (stated === "start" || !el || !el.getBoundingClientRect) { return stated; }
		return el.getBoundingClientRect().height > window.innerHeight * BIG_RING ? "start" : stated;
	}

	// ── the wall: during the tour, only the step's own control answers ───────────
	//
	// Owner call 2026-08-11 ("let's experiment"): while the guide is live, the app outside
	// the glowing control is DIMMED and does not answer clicks. Two halves, deliberately
	// separate:
	//
	//   the dim  — one fixed box on the target's rect, dimming everything around itself
	//              through a far-spread box-shadow, POINTER-EVENTS:NONE. It is paint and
	//              nothing else, so hovering still reveals what hovering reveals (word-list
	//              row controls, menu submenus) and the wheel still scrolls the panel under
	//              it. A blocking overlay would have taken all of that with it, and half the
	//              steps need it. The shadow honours the box's corner radius, so the lit
	//              area keeps the SILHOUETTE of the control it frames — the four panels
	//              this used to be could only cut a rectangle.
	//   the mute — one capture-phase listener that swallows presses landing anywhere but
	//              the step's own control. Capture at WINDOW is ahead of every app
	//              listener and of PL.layers' document capture, so nothing downstream ever
	//              hears the press.
	//
	// An INFO step has no control to press, so its wall has no hole at all — the next step
	// is the actionable one, and the point is that the reader gets there (owner call: no
	// deviating in between).
	//
	// A SESSION SCREEN has no step at all, and the wall runs on `stageLit` instead of on a
	// target: same dim, same mute, a hole the size of the whole central panel (see
	// sessionStage). Every "is the wall up" test below therefore asks for EITHER.
	//
	// Repeated blocked presses are a question, not a wall to push against: the fourth one
	// asks whether to finish the tour (the card is the only way to say "yes, let me out",
	// besides the "?" hub's guided-mode switch, which stays live for exactly that reason).
	// TWO. It was raised to four on the afternoon of 2026-10-07 (owner call, against the prod
	// "blink": a reader clicking around the entry a step is showing was asked "are you trying to
	// leave?" every second click — five times in 28 s for one prod visitor, who then said yes,
	// docs/project/tour-popup-blink.md) and put back to two the same evening (owner, testing on
	// localhost: "did you make now 4 clicks on unclickable space to bring up the 'finish?' dialog?
	// let's keep it two"). By the evening Esc raised the quit card uniformly (onKeyDown), so a
	// reader who wants out has a one-key way, and this count only has to catch the one who does
	// not know it. The nudge still answers every blocked press.
	// The un-dimmed area's clearance around the lit control. 6px until 2026-09-01, which put the
	// dim's edge 1px outside the ring's outer edge (GLOW_PAD 6 + GLOW_RING 2 = 8 now, 5 then):
	// the ring had the darkness pressed right against it and its glow bled straight into the dim
	// instead of fading over the page. 12px gives the ring 4px of lit ground to sit on. Purely
	// visual — what a step lets the reader PRESS is computed from element sets (pressElements /
	// litElements), never from this rectangle, so widening it grants no new clicks.
	var WALL_PAD = 12;
	var BLOCKED_BEFORE_ASK = 2;
	// …and the two must come in quick SUCCESSION (owner, 2026-10-07 late evening): "since most of
	// the functionality is disabled, a user who keeps clicking on disabled functionality and nothing
	// happens gets frustrated — that's why we offer an exit after two clicks. Those two clicks should
	// be in reasonable succession, not spaced minutes apart." A blocked press older than this window
	// no longer counts towards the next one: a reader who tried one thing, read for a while and tried
	// another is exploring, not stuck. Only SWALLOWED presses ever count — a press the step allows
	// (Got it, the lit control, the walk's own boxes) returns from onPress before the counter — and,
	// since 2026-10-08, only swallowed presses OUTSIDE what the step keeps lit (see onPress).
	var BLOCKED_WINDOW_MS = 5000;
	var lastBlockedTs = 0;

	var wall = null, wallHole = null, blocked = 0, quitAsked = false, doorAsked = false;
	var stageLit = null;   // the element lit when the wall runs without a step (a session screen)

	// An info step's wall has no hole — with one exception, and the exception is what `lit`
	// means. A step that names its own lit area is saying "the wall here is not about me": the
	// entry chapter rings small parts of a dictionary card the reader is being told to READ,
	// scroll and eventually leave, and sealing the panel it lives in would contradict the very
	// thing being taught. A step that only points at something, naming nothing, keeps the
	// hole-less wall — that is still the rule (the garden bed, My Progress).
	function holelessWall() {
		return !stageLit && stepObj && isInfo(stepObj) && !stepObj.lit;
	}

	function ensureWall() {
		if (wall) { return wall; }
		wall = document.createElement("div");
		wall.className = "obGuideWallClass";
		wall.setAttribute("aria-hidden", "true");
		wallHole = document.createElement("div");
		wallHole.className = "obGuideWallHoleClass";
		wall.appendChild(wallHole);
		document.body.appendChild(wall);
		return wall;
	}

	// A step's `lit` / `press` may name one element or several; everything downstream wants a list
	// with the nulls (an anchor that has not painted, a button this entry does not carry) gone.
	function elemList(v) {
		var raw = (v == null) ? [] : (v.length === undefined ? [v] : v);
		var out = [];
		for (var i = 0; i < raw.length; i++) { if (raw[i]) { out.push(raw[i]); } }
		return out;
	}

	// Everything that stays lit for this step: the control itself, and — when it lives
	// inside a popover the tour just had the user open — that whole menu family, because
	// walling three quarters of an open menu is how a menu gets misread as broken.
	function litElements() {
		if (stageLit) { return [stageLit]; }
		// A step may name what stays LIT separately from what it rings — the drill's answer-field
		// step glows the field and leaves the whole session panel live behind it, and the entry
		// chapter rings small parts of a dictionary card while keeping the whole card readable.
		if (stepObj && stepObj.lit) {
			var whole = elemList(stepObj.lit());
			if (whole.length) { return whole; }
		}
		var els = [];
		if (target) { els.push(target); }
		if (stepObj && stepObj.pass) {
			var extra = stepObj.pass() || [];
			for (var i = 0; i < extra.length; i++) { if (extra[i]) { els.push(extra[i]); } }
		}
		if (target && target.closest(".wlPopoverClass")) {
			var pops = document.querySelectorAll(".wlPopoverClass");
			for (var j = 0; j < pops.length; j++) { if (visible(pops[j])) { els.push(pops[j]); } }
		}
		return els;
	}

	// …and everything that still ANSWERS a press, which is a different and usually smaller set
	// (owner call 2026-08-12, walking the guided run screenshot by screenshot: "only the control
	// which is supposed to be pressed should work, with the exception of a few harmless buttons,
	// like sound"). Lit means READABLE; pressable means live. They used to be one list, so a step
	// that kept a whole dictionary entry bright also kept every control in it live, and the reader
	// had a dozen ways to walk off the rails mid-sentence.
	//
	//   • a step that names `press` gets exactly that, however much is lit around it;
	//   • an INFO step that names none has nothing to press by definition — its popup's Got it is
	//     the only thing to do;
	//   • a SESSION SCREEN, which has no step to ask, names its workflow (sessionLive);
	//   • everything else keeps the old rule: what is lit is live.
	function pressElements() {
		if (stageLit) { return sessionLive(); }
		if (stepObj && stepObj.press) { return elemList(stepObj.press()); }
		if (stepObj && isInfo(stepObj)) { return []; }
		return litElements();
	}

	// The session screen's own workflow — the ONLY controls that answer while the wall stands on a
	// session stage (a meet card, a drill question). The panel stays LIT and scrollable, because on
	// a session screen the panel is what the reader is reading; what it stops being is uniformly
	// LIVE. `stageLit` used to be handed back as its own press list, which made "the panel is the
	// workflow" (sessionStage) mean "every control in the panel is the workflow" — and the meet
	// card carries a great many that are not. The owner pressed the phrase gutter's microphone on
	// a meet card mid-tour and got the whole pronunciation-practice popup over the walk (report
	// 2026-08-31, "this should not be shown"). A first-time reader has not been introduced to any
	// of that yet; the run's forward path is the only thing they have been asked to do.
	//
	// An ALLOW list rather than a deny list, deliberately: a control added to a session screen
	// later is asleep during the tour until someone says otherwise, which is the failure that
	// costs nothing. The reverse fails open, silently, exactly the way this one did.
	var SESSION_LIVE_SEL = [
		".lrnTopActionClass",    // the ladder's uniform forward action: "Got it →" / "Continue →"
		".lrnRevealClass",       // the meet card's front face: the prompt that opens the entry
		// The ladder's ANSWER controls — the rung's question is the one thing the reader has been
		// asked to do, and until now none of them were on this list (owner report 2026-08-31: the
		// meaning cards on R1/R2 wore the asleep face and swallowed every press). One entry per
		// shape the rungs actually render:
		//   • the choice rungs deal `.lrnOptClass` cards (the "none of these" tail carries it too);
		//   • the typing rungs each have their own field — R4's two letters, R4's change-part gaps
		//     on a form-set card, R6's full-word overlay. All three also had a paint bug worth
		//     naming: the asleep face's `opacity:.42` beat `.lrnFullInputClass`'s own `opacity:0`,
		//     so R6's invisible overlay field was drawn at 42% ON TOP of the slot row it covers;
		//   • and typing was not merely dim — the fields autofocus, and `onFocusIn` blurred them
		//     right back out, so the tour's own typing rungs could not be answered at all.
		".lrnOptClass", ".lrnLetterInputClass", ".lrnGapInputClass", ".lrnFullInputClass",
		// "Repeat the word" on R6 — same word, same rung, fresh field. It cannot move the ladder or
		// leave the screen, which is the whole test for being live here.
		".lrnRepeatButtonClass",
		"#answerBoxId",          // the drill's answer band: field, "reveal the word", self-check row
		"#exerciseContinueId",   // the drill's forward action on a revealed answer
		"#stopId",               // Stop Exercise — chapter 2 opens on the screen this produces
		// Pause / Continue — Start Exercise! wearing its in-drill face. Freezes the clock and takes the
		// reader nowhere, and pressing it again picks the same card up (owner, 2026-10-08: "don't see
		// any harm"). Only ever visible on the Practice tab, so a meet card never sees it.
		"#startId",
		// Sound is the owner's standing exception (2026-08-12: "a few harmless buttons, like
		// sound"). Hearing the word takes the reader nowhere, so every speaker on the screen stays
		// live — the card's own, the entry's, a phrase row's. The MICROPHONE beside them does not:
		// it opens a scored practice popup, which is a surface of its own.
		".lrnSndClass", ".lrnSndLgClass", ".ai-sound-btn", ".phrase-sound-btn"
	].join(",");

	// Taken as a ROOT rather than off `stageLit`, because the one step that runs on a session screen
	// (`type`, the drill's answer field) needs the same list and has no stageLit to read it from:
	// sync() gives an onStage step the panel as its own `lit`, which used to mean the panel was its
	// own press list too. Same screen, same rule, whether or not a step is pointing at part of it.
	function sessionLiveIn(root) {
		if (!root) { return []; }
		var out = [], found = root.querySelectorAll(SESSION_LIVE_SEL);
		for (var i = 0; i < found.length; i++) {
			if (visible(found[i])) { out.push(found[i]); }
		}
		return out;
	}

	function sessionLive() { return sessionLiveIn(stageLit); }

	// …and everything that must NOT answer even though it sits inside what the step lit.
	//
	// A step that rings a word-list HEADER is asking for the list to be OPENED, and nothing
	// else — but the header also carries the row's own controls (the "Add to Learning" pill,
	// rename, delete), so "what is lit is live" handed them over with it. The owner pressed
	// Add on the My List row during the "open your list" step and got the staging menu on top
	// of the tour (report 2026-08-13). It is a rule about the panel rather than about any one
	// step, so it lives here rather than in nine `deny` clauses — and it covers HOVER too,
	// because the Add pill's menu opens on hover alone.
	function deniedElements() {
		if (stageLit) { return []; }
		var out = (stepObj && stepObj.deny) ? elemList(stepObj.deny()) : [];
		if (target && target.classList && target.classList.contains("wlHeaderClass")) {
			out = out.concat(elemList([target.querySelector(".wlAddPracticeClass"),
				target.querySelector(".wlHeaderActionsClass")]));
		}
		return out;
	}

	function within(els, node) {
		for (var i = 0; i < els.length; i++) {
			if (els[i] === node || els[i].contains(node)) { return true; }
		}
		return false;
	}

	// The walk's own furniture, which is never behind its own wall: the step popup and any card
	// the walk raised.
	//
	// The "?" hub used to be in here too, as the escape hatch — the switch that turns guided mode
	// off. It is sealed like everything else since 2026-09-01 (owner call: "in guided tour help
	// should not work"). Nothing is lost: the way out of the walk is the quit card, which the
	// second sealed press raises, and the hub is where the walk itself ENDS (the SIGN chapter),
	// which is a better place to be told about the mode switch than a menu opened mid-lesson.
	function walkOwned(node) {
		return !!(node && node.closest
			&& node.closest(".obGuideCueClass, .obCardClass, .msgOverlayClass"));
	}

	// The lit area wears the silhouette of what it lights, the way the halo ring does: a
	// single lit control hands over its own corner radius, grown by the pad — offsetting a
	// rounded rect outwards by p grows every corner by exactly p. A merged rect over a menu
	// family has no one silhouette to borrow, so it takes the app's panel radius.
	function holeRadius(els) {
		if (els.length !== 1) { return "var(--pl-radius-panel)"; }
		var rad = getComputedStyle(els[0]).borderRadius;
		if (!rad) { return "var(--pl-radius-panel)"; }
		// Per-corner and elliptical shorthands ("6px 6px 0 0", "…/…") grow token by token
		// through ringRadius — the same arithmetic the halo ring uses, so the hole and the
		// ring around one control describe the same curve.
		return ringRadius(rad, WALL_PAD);
	}

	// A single LIT control that leans keeps its lean, exactly as the halo ring does (pinHalo, and the
	// same three paragraphs of reasoning): the mode tabs are skewed chips, and an upright hole around
	// one leaves bright wedges poking out past the chip's own edge — the tab looked framed by a
	// rectangle that was not its own (owner report 2026-08-12). Sized from the UNtransformed box for
	// the same reason the ring is: getBoundingClientRect() reports the box AFTER the skew, which is
	// wider than the chip. A merged family (an open menu) has no one silhouette, so it stays upright.
	function litShape(els) {
		if (els.length !== 1) { return null; }
		var el = els[0];
		if (!el.offsetWidth || !el.offsetHeight) { return null; }
		var shape = shapeTransform(getComputedStyle(el));
		if (!shape) { return null; }
		var r = el.getBoundingClientRect();
		var w = el.offsetWidth + 2 * WALL_PAD, h = el.offsetHeight + 2 * WALL_PAD;
		var left = r.left + r.width / 2 - w / 2, top = r.top + r.height / 2 - h / 2;
		return { left: left, top: top, right: left + w, bottom: top + h,
			radius: holeRadius(els), transform: shape };
	}

	// The hole, or null when there is nothing on screen to cut it around. Elements scrolled
	// out of their panel are dropped first, for the reason pinRing hides a ring on one: a
	// bright patch at the coordinates a control USED to occupy frames whatever took its place.
	// With none of them left the wall closes over — the reader has scrolled the step's control
	// away, the popup (which holds on now) still says what it is, and scrolling back brings
	// the hole with it.
	function litRect() {
		var els = [], all = litElements();
		for (var q = 0; q < all.length; q++) {
			if (!PL.tip.outOfSight(all[q])) { els.push(all[q]); }
		}
		if (!els.length) { return null; }
		var leaning = litShape(els);
		if (leaning) { return leaning; }
		var box = null;
		for (var i = 0; i < els.length; i++) {
			var r = els[i].getBoundingClientRect();
			if (!r.width && !r.height) { continue; }
			if (!box) {
				box = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
			} else {
				box.left = Math.min(box.left, r.left);
				box.top = Math.min(box.top, r.top);
				box.right = Math.max(box.right, r.right);
				box.bottom = Math.max(box.bottom, r.bottom);
			}
		}
		if (!box) { return null; }
		return {
			left: box.left - WALL_PAD, top: box.top - WALL_PAD,
			right: box.right + WALL_PAD, bottom: box.bottom + WALL_PAD,
			radius: holeRadius(els)
		};
	}

	// No clamping to the viewport any more: the shadow reaches every corner from wherever
	// the box sits, so a hole running off the top of the screen needs no special case.
	function pinWall() {
		if ((!target && !stageLit) || noticeUp()) { hideWall(); return; }
		ensureWall();
		// No hole only for a pointing INFO step. A session screen always has one — its own panel.
		var hole = holelessWall() ? null : litRect();
		var s = wallHole.style;
		if (!hole) {
			// Nothing to press: collapse the box to a point and the shadow covers the lot.
			s.left = (window.innerWidth / 2) + "px";
			s.top = (window.innerHeight / 2) + "px";
			s.width = "0px";
			s.height = "0px";
			s.borderRadius = "0";
			s.transform = "";
		} else {
			s.left = hole.left + "px";
			s.top = hole.top + "px";
			s.width = Math.max(0, hole.right - hole.left) + "px";
			s.height = Math.max(0, hole.bottom - hole.top) + "px";
			s.borderRadius = hole.radius;
			// The dimming shadow is cast by this box, so the box leaning is the whole of the lit
			// area leaning with the control it frames (litShape). 200vmax of spread still reaches
			// every corner of the viewport through a skew.
			s.transform = hole.transform || "";
		}
		wall.style.display = "block";
		markAsleep();
	}

	function hideWall() {
		if (wall) { letGoFade(wall); wall.style.display = "none"; }
		clearAsleep();
	}

	// ── the asleep face: lit is READABLE, live is PRESSABLE, and now they look different ──
	//
	// The dim is drawn from a RECTANGLE and the mute is computed from a SET, and until now only the
	// rectangle had a visual language. So a control outside the hole said "not now" by going dim,
	// while a control inside it — the meet card's five header buttons under a step that lights the
	// whole card and presses none of it — stayed at full contrast, kept its pointer cursor, and did
	// nothing when pressed (owner report 2026-08-31: "some controls are just disabled"). Nothing
	// distinguished it from the one control that WAS live except trying them.
	//
	// One class on each lit container, one on each live control, and the CSS subtracts: everything
	// interactive in between wears the asleep face. Marking the two SETS rather than walking the
	// DOM for controls is what keeps this honest — the same two lists the mute reads, so the paint
	// cannot drift from the behaviour the way the dim rectangle did.
	//
	// A DENIED control is the one case the subtraction cannot reach: it sits inside what the step
	// lit, the step's press list is the lit set (the two are equal on an ordinary step), and yet it
	// is the one thing in there that must not answer — the word-list header's Add pill. It is
	// marked directly instead, with the class the CSS also hangs the asleep face on.
	var asleepHosts = [], awakeMarks = [], dozeMarks = [];

	function sameList(a, b) {
		if (a.length !== b.length) { return false; }
		for (var i = 0; i < a.length; i++) { if (a[i] !== b[i]) { return false; } }
		return true;
	}

	function clearAsleep() {
		for (var i = 0; i < asleepHosts.length; i++) { asleepHosts[i].classList.remove("obAsleepClass"); }
		for (var j = 0; j < awakeMarks.length; j++) { awakeMarks[j].classList.remove("obAwakeClass"); }
		for (var k = 0; k < dozeMarks.length; k++) { dozeMarks[k].classList.remove("obDozeClass"); }
		asleepHosts = [];
		awakeMarks = [];
		dozeMarks = [];
	}

	// Called from every pinWall — which is every tracker tick — so it does nothing at all unless the
	// sets have actually changed. They do change mid-step without a step change: the drill's
	// self-check row replaces the answer field in the same band, a meet card re-renders on the flip.
	function markAsleep() {
		var lit = litElements(), live = pressElements(), deny = deniedElements();
		if (sameList(lit, asleepHosts) && sameList(live, awakeMarks) && sameList(deny, dozeMarks)) {
			return;
		}
		clearAsleep();
		for (var i = 0; i < lit.length; i++) {
			lit[i].classList.add("obAsleepClass");
			asleepHosts.push(lit[i]);
		}
		for (var j = 0; j < live.length; j++) {
			live[j].classList.add("obAwakeClass");
			awakeMarks.push(live[j]);
		}
		for (var k = 0; k < deny.length; k++) {
			deny[k].classList.add("obDozeClass");
			dozeMarks.push(deny[k]);
		}
	}

	// ── the nudge: the small answer to a blocked press ────────────────────────────
	//
	// Pulses whatever the walk has on screen to point WITH — the rings, the popup that says what to
	// do, and the lit area's own edge. No new copy: the popup already carries the sentence, and a
	// press that makes it jump is a press that has been answered. A session screen has neither ring
	// nor popup (the walk says nothing there on purpose — recall focus is sacred), which is exactly
	// why the wall's own hole is in the list: on that screen it is the only thing that can say
	// "here". Re-armed by taking the class off and forcing a reflow, so a second press inside the
	// first pulse restarts it rather than being swallowed by the animation already running.
	var NUDGE_MS = 900;
	var nudgeTimer = null, nudged = [];

	function nudgeOff() {
		for (var i = 0; i < nudged.length; i++) { nudged[i].classList.remove("obGuideNudgeClass"); }
		nudged = [];
	}

	function nudge() {
		clearTimeout(nudgeTimer);
		nudgeOff();
		var els = [];
		for (var i = 0; i < halos.length; i++) {
			if (halos[i].style.display === "block") { els.push(halos[i]); }
		}
		if (wallHole && wallLive()) { els.push(wallHole); }
		for (var name in pins) {
			if (pins.hasOwnProperty(name) && pins[name].el && visible(pins[name].el)) {
				els.push(pins[name].el);
			}
		}
		for (var j = 0; j < els.length; j++) {
			void els[j].offsetWidth;   // reflow between remove and add, or the animation never restarts
			els[j].classList.add("obGuideNudgeClass");
			nudged.push(els[j]);
		}
		nudgeTimer = setTimeout(function () { nudgeTimer = null; nudgeOff(); }, NUDGE_MS);
	}

	function wallLive() {
		return !!(wall && wall.style.display === "block" && (target || stageLit));
	}

	// Leaving a Learn session mid-run is NOT a way out of the tour (owner call 2026-08-12): "back to
	// the words" is dead for as long as the walk is escorting, and pressing it goes the way every
	// other sealed control goes — enough presses (BLOCKED_BEFORE_ASK) ask whether to finish the guided tour. The
	// escort's whole job is keeping a first-time reader on one simple path, and the quit card is the
	// door. (The practice drill's own stop button is left alone: chapter 2 opens ON the finish screen
	// and has a step that points at it.)
	function sealedControl(node) {
		return !!node.closest(".lrnBackLinkClass");
	}

	// The press is let through when it lands on something this step declares pressable, or on the
	// walk's own furniture.
	function pressAllowed(node) {
		if (!node || !node.closest) { return true; }
		if (walkOwned(node)) { return true; }
		if (sealedControl(node)) { return false; }
		if (within(deniedElements(), node)) { return false; }
		// An INFO step has nothing to press — that is what makes it an info step. Its ANCHOR is not
		// a way through either: the finish screen's garden bed opens the garden vista, and pressing
		// it under a hole-less wall put a full-size overlay UNDER the dim with the tour's own halo
		// and popup floating over it (owner report 2026-08-12 — "click on the garden and it all
		// looks like a mess"). Read it, press Got it, and the next step is the one with something to
		// press. An info step that names `press` is naming its exceptions — the entry's speaker and
		// add-to-list, which take the reader nowhere.
		return within(pressElements(), node);
	}

	// HOVER is muted the same way, and by the SAME list: what answers a press answers a hover, and
	// nothing else does. It used to default to what is LIT, which is the wider set — so a step that
	// kept a whole meet card bright kept every hover machine in it running, and the reader who
	// dwelt on an example sentence got its gutter, its popups and (via the gutter) a practice
	// surface, on a card the step was only asking them to read. Two allow-lists for one question
	// is how the two drifted apart; there is one now, and `hover` / `noHover` still override it.
	//
	// The wall paints but does not block (pointer-events:none), so until now every hover machine in
	// the app went on running underneath it: the reader hovered the word-list panel during the
	// "press Start Learning" step and got a full Add dropdown over the dim (owner report
	// 2026-08-12 — "no dropdowns, it's confusing otherwise"), and the entry chapter's steps sat
	// beside live per-word popups on the very card they were asking the reader to just look at.
	// Muting the events rather than the paint keeps what the wall was always careful to keep: the
	// wheel still scrolls, and a lit control's own :hover face is CSS and unaffected.
	//
	// `mouseenter`/`mouseleave` bind natively (PL.hover.intent) and `mouseover` is what jQuery
	// delegates them from, so both spellings have to go. Only the ENTER half: a machine that opened
	// before the wall went up must still be able to hear the pointer leave.
	function hoverAllowed(node) {
		if (!node || !node.closest) { return true; }
		if (walkOwned(node)) { return true; }
		// A step may name exactly what answers a hover, which is neither "everything lit" nor
		// "nothing": the entry chapter's word step wants the dwell on the ONE word it rings and
		// the card that dwell raises, with the rest of the entry as quiet as the rule below makes
		// it. Read before `noHover` so a chapter-wide mute stays the default it is stamped as.
		if (stepObj && stepObj.hover) { return within(elemList(stepObj.hover()), node); }
		if (stepObj && stepObj.noHover) { return false; }
		if (within(deniedElements(), node)) { return false; }
		return within(pressElements(), node);
	}

	// A NOTICE step (the sign-off) is standing: nothing is walled, so nothing here is muted — but a
	// press outside the notice's own menu and popup is the reader getting on with the app, and the
	// notice goes rather than lingering over what they went for (first-run-redesign R17). A real
	// mouse press has usually closed the menu already (PL.layers' outside-pointerdown, which never
	// consumes the press); this is the same answer for the presses that arrive as a bare click — a
	// keyboard Enter, a synthetic click — and the one tick later is when its `done` (the menu being
	// gone) is read back and the chapter's own closing runs.
	function noticeUp() {
		return !!(stepObj && stepObj.notice);
	}

	function closeNotice() {
		if (hubMenuEl() && window.ONBOARDING && ONBOARDING.closeHelpHub) { ONBOARDING.closeHelpHub(); }
		setTimeout(sync, 0);
	}

	// The "?" that opened the menu is left to its own toggle: closing the menu here first would
	// have its click open a fresh one.
	function onPress(e) {
		// A press inside the walk's OWN popup is never an "outside press" to whatever popover the step
		// has open (PL.layers listens at document capture, below this window listener). The add menu
		// solved that once by listing the pin boxes among its anchors (cueAnchors); since 2026-10-08
		// every popup has buttons — Back on the action box too — and the topic picker, which lists
		// nothing, closed under the reader's Back press, taking the live step's anchor with it. Only
		// the press half: the click still reaches the button.
		if ((e.type === "pointerdown" || e.type === "mousedown") && e.target && e.target.closest
			&& e.target.closest(".obGuideCueClass")) {
			e.stopPropagation();
			// …and a press on the popup's PROSE keeps the cursor where it was. The browser's default for
			// a mousedown on plain text is to move focus to <body>, so one click on "type a word here"
			// took the cursor out of the very box it was about — and the reader's typing went nowhere
			// (tour-exhaustive-test.md D6, the search step). The foot's buttons keep their default.
			if (e.type === "mousedown" && !e.target.closest("button, a, input, textarea")) { e.preventDefault(); }
			return;
		}
		if (noticeUp()) {
			var n = e.target;
			if (e.type === "click" && n && n.closest && !walkOwned(n) && !within(litElements(), n)
				&& !n.closest("#helpBookmarkId")) {
				closeNotice();
			}
			return;
		}
		// A door is not a sealed control and not a live one: the press is held back from the tab's
		// own handler, and the question goes up instead (see doorFor). Asked whether or not the wall
		// is standing this instant — a tab must not switch silently in the tick between two steps.
		var door = doorFor(e.target);
		if (door) {
			e.preventDefault();
			e.stopPropagation();
			if (e.type === "click") { openDoor(door); }
			return;
		}
		// A press the live step allows, made while a Back is being read: the reader has gone on with
		// the tour, so its own box comes back — and the press still lands (nothing is consumed).
		if (peekIdx !== null && (e.type === "pointerdown" || e.type === "click") && !walkOwned(e.target)
			&& (!wallLive() || pressAllowed(e.target))) {
			endPeek("press");
		}
		if (!wallLive() || pressAllowed(e.target)) { return; }
		e.preventDefault();
		e.stopPropagation();
		if (e.type !== "click") { return; }   // one count per press, not one per event
		// A press on what the step keeps LIT — the word card the reader was just told to look at, the
		// session panel on a stage step — is reaching for the thing itself (the headword, the entry
		// text), not hammering a dead control: on the first day the wall was measured, 59 of 181
		// swallowed presses landed there and were the top reason the quit card was asked, three
		// visitors in four saw it (docs/project/ad-funnel-diagnosis.md §8.4). Owner call 2026-10-08:
		// still swallowed, still nudged, still marked — never counted towards the quit card. Presses
		// OUTSIDE the lit area (the word lists, the panes, a tab) count as before; Esc stays the
		// one-key way out.
		var onLit = within(litElements(), e.target);
		// A press that comes long after the previous blocked one starts the count over
		// (BLOCKED_WINDOW_MS): the card is for the reader hammering dead controls, not for one who
		// tried something, read on, and tried something else minutes later.
		var now = Date.now();
		if (!onLit) {
			if (now - lastBlockedTs > BLOCKED_WINDOW_MS) { blocked = 0; }
			lastBlockedTs = now;
		}
		// …and SAID, because the visit log cannot see it: its click listener sits on `document`,
		// below this window-capture mute, so a swallowed press left no row at all. That is what made
		// the 2026-10-07 "blink" look input-free — two swallowed presses raise the quit card (which
		// takes the popup down), a press on its backdrop closes it on pointerdown (which puts the
		// popup back) and the browser fires no click for that press at all; askQuit marks both
		// halves (docs/project/tour-popup-blink.md).
		if (window.VISIT_LOG) {
			VISIT_LOG.mark("walled", { el: VISIT_LOG.describe ? VISIT_LOG.describe(e.target) : null,
				step: stepId || (stageLit ? "stage" : null), n: onLit ? blocked : blocked + 1, lit: onLit ? 1 : 0 });
		}
		// EVERY blocked press is answered. The first one used to be swallowed in silence and the
		// second raised the quit card, so one press read as "this control is broken" and the next
		// as "the app is asking me a question" — the same control, twice, told apart by nothing the
		// reader could see (owner report 2026-08-31). The nudge is the small answer and the card is
		// the big one (after BLOCKED_BEFORE_ASK of them); neither is a press going nowhere.
		nudge();
		if (!onLit && ++blocked >= BLOCKED_BEFORE_ASK) { blocked = 0; askQuit(); }
	}

	function onHover(e) {
		if (!wallLive() || hoverAllowed(e.target) || doorFor(e.target)) { return; }
		e.stopPropagation();   // no preventDefault: a hover has no default worth cancelling
	}

	// Focus is the third way into a sleeping control, and the quiet one: Tab still walked the whole
	// panel, parking a focus ring on buttons that would not answer (Enter on one dispatches a click,
	// which the press mute already eats — so the ring was the whole of the lie). Handing focus back
	// rather than rewriting everyone's tabindex: the app sets its own on a dozen elements in
	// main.html, and a walk that edits them owes a perfect restore for a cosmetic gain.
	function onFocusIn(e) {
		if (!wallLive() || pressAllowed(e.target) || doorFor(e.target)) { return; }
		if (e.target && e.target.blur) { e.target.blur(); }
	}

	["pointerdown", "mousedown", "mouseup", "click", "dblclick"].forEach(function (type) {
		window.addEventListener(type, onPress, true);
	});

	// SCROLL is the fourth way, and the wall used to hand it over on purpose — "the wheel still
	// scrolls" was written as a kindness, on the reasoning that a reader who cannot see the whole
	// lit thing must be able to bring it into view. What that ignored is what scrolling does to the
	// walk's own furniture: the halo and the step popup are measured once and pinned to a rect
	// (`litRect`), and so is every popover the app hangs off a control — the list picker is
	// appended to `<body>` at an absolute position. Roll the wheel one notch on a dictionary entry
	// and the card slides out from under all three, leaving the ring, the popup and the picker
	// floating over whatever moved into their place: "make the scrolling outside of that particular
	// dict entry disabled — it wrecks havoc on the view" (owner report 2026-09-01).
	//
	// So scroll follows the same rule as everything else: INSIDE what this step lights it works,
	// everywhere else it does not. That keeps the kindness where it was actually needed — a long
	// entry, a scrolling picker (`.wlPopScrollClass` lives inside the lit popover), the session
	// panel on a stage step — and takes it away only from the page the step is not about. `lit`
	// rather than `press` deliberately: reading is what scrolling serves, and the whole point of
	// the lit/press split is that a lit thing may be read without being live.
	//
	// Wheel and touch only. The keyboard's scroll keys (space, PageDown, the arrows) are left
	// alone: they arrive as keystrokes, the steps that ask for typing are the ones that would pay
	// for a mistake here, and no report has ever come from that direction. `passive:false` is
	// required — a passive listener may not preventDefault, and Chrome makes wheel passive by
	// default at the window.
	function onScroll(e) {
		if (!wallLive()) { return; }
		if (walkOwned(e.target) || within(litElements(), e.target)) { return; }
		e.preventDefault();
		e.stopPropagation();
	}

	["mouseover", "mouseenter", "pointerover", "pointerenter"].forEach(function (type) {
		window.addEventListener(type, onHover, true);
	});

	["wheel", "touchmove"].forEach(function (type) {
		window.addEventListener(type, onScroll, { capture: true, passive: false });
	});

	window.addEventListener("focusin", onFocusIn, true);

	// ESC, uniformly, while the wall is up: the quit card's question — never "put this popup away",
	// never the session screen's or a popover's own Esc. Owner, 2026-10-07, after Esc on the Start
	// popup and then on the meet card's popup each silently put one box away (first-run-redesign.md
	// §11): "maybe we should uniformly make Esc bring up the 'finish tour?' dialog … they have the
	// direct way and will learn about Esc later when doing it themselves". Capture phase on the
	// window, so neither the pin engine (js/pl/tip.js), the session screen, a menu nor an input sees
	// the key. The quit card itself is an .obCardClass and the stage rule takes the wall down while
	// one is up — so Esc ON the card is still its own "stay". Enter, Tab and the arrows are
	// untouched: the typing steps need them. The tour copy no longer offers Esc as a way to do
	// anything (obMeetGotIt_body said "or press Esc" and does not any more).
	function onKeyDown(e) {
		if (e.key !== "Escape" || !wallLive() || quitAsked) { return; }
		e.preventDefault();
		e.stopPropagation();
		askQuit();
	}
	window.addEventListener("keydown", onKeyDown, true);

	function askQuit() {
		if (quitAsked || !window.ONBOARDING || !ONBOARDING.tourQuitCard) { return; }
		quitAsked = true;
		if (window.VISIT_LOG) { VISIT_LOG.mark("quitcard", { step: stepId || null }); }
		ONBOARDING.tourQuitCard(function (cardEl) {
			endTour(cardEl, "quit");
		}, function () {
			if (window.VISIT_LOG) { VISIT_LOG.mark("quitstay", { step: stepId || null }); }
			quitAsked = false;
			sync();
		}, function () {
			// The third answer (owner, 2026-10-08): the same "Start the tour over" Back reaches at
			// its far end, one press away for a reader who reached for Esc or hit two dead controls.
			startOver("quit");
		});
		sync();   // the card is an .obCardClass: the stage rule takes wall and glow down
	}

	// ── the tour's two DOORS: the Practice and Dictionary tabs (first-run R16, R21) ──────────────
	//
	// The mode tabs stay on screen through a Learn session now (R15), and to a reader looking for
	// "the rest of the app" they are the first thing to press. Walled, a press there said nothing
	// about where the tab would have gone — one more dead control on the way to the quit card. So
	// each of the two answers with a QUESTION instead (owner's design and copy, R16): Practice —
	// "go straight there? the tour carries on there", and the two words go too (R21); Dictionary —
	// "this ends the tour, and the '?' menu starts it again".
	//
	// CHAPTER 1 ONLY (the guided run, chapter 1½ inside it): the Practice card's "No, stay in the
	// Learn exercise" is about the Learn chapter, chapter 2 has a step of its own ON the Dictionary
	// tab, and the later chapters run where these doors lead anyway. A tab is a door only away from
	// itself: Practice from the Learn tab (Start screen, cards, recap), the Dictionary from Learn or
	// Practice. The tabs stay under the wall's dim like everything else outside the step (R3) — the
	// door is in the ANSWER to a press, not in the look.
	// Asked on EVERY press, hover and focus in the page (the wall's window listeners), so the tab test
	// comes first and the rest only for the two tabs themselves.
	function doorFor(node) {
		var tab = (node && node.closest) ? node.closest("#tabPracticeId, #tabDictId") : null;
		if (!tab || !walkArmed() || cardLive()) { return null; }
		if (tab.id === "tabPracticeId") { return activeTab() === "learn" ? "practice" : null; }
		return activeTab() !== "dict" ? "dict" : null;
	}

	// The marks say which door, from which step, and which answer — the step table's `walled` rows
	// cannot: a door press is not walled, and the tour can end through one (`tourend how:"dict"`).
	function openDoor(kind) {
		if (doorAsked || !window.ONBOARDING || !ONBOARDING.tourDoorCard) { return; }
		doorAsked = true;
		var from = stepId || (stageLit ? "stage" : null);
		if (window.VISIT_LOG) { VISIT_LOG.mark("door", { tab: kind, step: from }); }
		ONBOARDING.tourDoorCard(kind, function (cardEl) {
			doorAsked = false;
			blocked = 0;
			if (window.VISIT_LOG) { VISIT_LOG.mark("doorgo", { tab: kind, step: from }); }
			if (kind === "dict") {
				// The whole ending, the sign-off included: endTour flies this card into the "?" menu's
				// Guided tour row — which is where the card has just said the tour starts again. The tab
				// opens after the walk is down (endTour's own order: wall first, then the app's exits),
				// and before the sign-off's menu goes up a tick later, so the menu opens over the
				// dictionary and no click of the switch can read as a press outside it.
				endTour(cardEl, "dict");
				if (window.openDictTab) { window.openDictTab(); }
				return;
			}
			// The tour carries on: chapter 1's `drill` step is the Practice tab with a set staged, and
			// it lights Start Exercise! as soon as the words have painted. Nothing to tell the walk.
			if (!(window.LRN && LRN.toPractice && LRN.toPractice()) && window.openPracticeTab) {
				window.openPracticeTab();
			}
		}, function () {
			doorAsked = false;
			if (window.VISIT_LOG) { VISIT_LOG.mark("doorstay", { tab: kind, step: from }); }
			sync();
		});
		sync();   // the card is an .obCardClass: the stage rule takes wall and glow down
	}

	// "Stop the tour" has to mean the app is ORDINARY again, and taking the wall down is only half of
	// that (owner call 2026-09-01: "when a user says stop the tour, we should stop the tour — stop
	// the Learn or Practice sessions if any and just show the normal, unlocked view of the Learn or
	// Practice tabs"). A reader let out of the walk on a session screen is un-walled and still ON the
	// screen the walk led them to — and the drill and its finish screen HIDE THE MODE TABS, so there
	// the only door out is a control they have just been told the tour was blocking. (A Learn
	// session hid them too until first-run R15; the plain tab is still where a stopped tour lands.)
	//
	// Each screen is left by its own exit rather than by anything invented here — the Learn ladder
	// and its recap by "back to the words" (LRN.backToTray), the drill and its finish screen by Stop
	// (leaveExerciseView) — so the reader lands exactly where those buttons land them, on the tab
	// they were already on. Both are gated by the screen actually being up: #learnBoxId is the
	// session box (display:none outside a session, and it holds the recap too), and main.js's own
	// isExerciseView answers for the drill side.
	function leaveSessions() {
		if (window.LRN && LRN.backToTray && visible(document.getElementById("learnBoxId"))) {
			LRN.backToTray();
		}
		if (window.leaveExerciseView) { window.leaveExerciseView(); }
	}

	// The tour is over, whichever chapter it was in: the cursor's end value disarms both
	// (walkArmed refuses once tourOver, and chapter 2 has nothing left to point at). `cardEl` is
	// the card whose button ended the walk (undefined on Esc/the overlay) — passed straight
	// through to signOff, which flies it, shrinking, to the "?" when it has one. `how` names an
	// EARLY exit ("quit" | "declinedWl" | "dict" — the Dictionary door, first-run R16) and is
	// recorded before the cursors below are reset; the welcome card's decline passes none — its own
	// `skipped` state already says so. An early exit from the guided RUN also stops the welcome card
	// coming back on the next load (R23 — onboarding.js endedEarly).
	function endTour(cardEl, how) {
		if (window.VISIT_LOG) { VISIT_LOG.mark("tourend", { how: how || "explore" }); }
		quitAsked = false;
		blocked = 0;
		stageLit = null;
		entered = {};   // …and a chapter that is offered again starts from an unprepared panel
		var t = T();
		if (t && how && t.endedEarly) { t.endedEarly(how); }
		// BOTH scripted chapters, whichever of them the reader was in: the quit card is one
		// door out of the whole walk, and a chapter 3 left at step 4 would otherwise resume by
		// itself on the next page load. (The entry chapter needs no such line — it rides inside
		// chapter 1's arming and stops existing with it.) tourSet(-1) is also what stops chapter
		// 1's own explore glow trail from resuming behind the sign-off (owner call 2026-09-02:
		// opting out, at any stage, ends ALL of the walk's unsolicited highlighting, not just the
		// scripted chapter the reader happened to be in) — tourOver() reads true from here on,
		// which walkArmed refuses regardless of the `skipped`/`running` state underneath it.
		if (t) { t.tourSet(-1); t.wlSet(-1); }
		// Explicitly, and not left to the next sync: this runs from inside a CARD's handler, so
		// the card is still on the page and syncPanelFold's own "hold it open through the cards"
		// clause would decline to fold — and with the walk over there may be no next sync at all
		// (the tracker stops on !armed()). The panel would keep the walk's shape until a reload.
		refoldPanel();
		hideCue("end");
		clearGlow();
		hideWall();
		// …and off whatever session screen the walk had them on, onto the plain tab behind it. AFTER
		// the wall comes down, because the exits above run through the app's own handlers and the
		// mute is a capture listener that would swallow anything they touch.
		leaveSessions();
		// …and the last word, on every ending: the one thing a reader who stops here has not been
		// told (see the SIGN chapter). No-op once it has been said.
		signOff(cardEl);
	}

	// ── tracking: keep the halo pinned while a control glows ─────────────────────

	// A meet card's rich view that the chapter has not been given a chance to look at yet.
	// Node identity is the test, so re-entering the base view after an excursion counts as a
	// fresh arrival (renderLearnRichView rebuilds the card) while a card the chapter had nothing
	// to say about is asked about exactly once.
	var meetCardSeen = null;

	function meetCardArrived() {
		if (!walkArmed() || meetOver()) { meetCardSeen = null; return false; }
		var card = meetSurface();
		if (!card) { meetCardSeen = null; return false; }
		if (meetCardSeen === card) { return false; }
		meetCardSeen = card;
		return true;
	}

	function startTracker() {
		if (!tracker) { tracker = setInterval(track, TRACK_MS); }
	}

	// The popup half of this ticker went to the engine with the pin (PR 6/7): a pin FOLLOWS
	// its anchor on the engine's own tracker and dies with it. What is left is the halo's.
	function track() {
		// FIRST, and before any of the branches below: a modal took the stage, or gave it back.
		// Cards of our own announce themselves both ways (they call sync from their own handlers),
		// but the garden vista the finish-screen bed opens is not ours and says nothing in either
		// direction — so without this the wall stayed up UNDER the overlay on the way in, and
		// nothing looked again on the way out. An edge detector rather than a state test: sync()
		// re-stamps `stageOwnedWas` on every pass, so this fires once per change.
		if (stageOwnedWas !== cardLive()) { sync(); return; }
		// The session-screen wall has no target and no step — only a panel to stay pinned to
		// through a resize or a scroll, and a screen to notice the end of. Leaving the session
		// repaints the tray, which does call the hooks; the re-check is the belt.
		if (stageLit) {
			if (!sessionScreenLive()) { sync(); return; }
			// …and for one more: the meet card being FLIPPED. That press rides no hook at all
			// (learning_session flips the card inside its own click handler, and the rich view
			// lands asynchronously after it), so the ticker is what notices chapter 1½'s stage
			// arriving — exactly as it notices the session screen's. Asked once per rendered
			// card, so a chapter with nothing to say on this one does not re-sync at 3Hz.
			if (meetCardArrived()) { sync(); return; }
			pinWall();
			return;
		}
		if (!target) {
			// Nothing glows. The ticker still runs while the guide is ARMED, for one thing:
			// noticing a session screen take the stage. Its paint rides no usable hook —
			// learning_session calls the one it has (hideCta) BEFORE building the screen, so
			// that sync sees the tray gone and the card not yet there and finds nothing at
			// all — and a walk that has just cleared its glow would otherwise stop ticking
			// there and never look again. That is the whole of "the wall vanished at the meet
			// card" (owner report 2026-08-12).
			if (!armed()) {
				clearInterval(tracker);
				tracker = null;
				return;
			}
			if (sessionStage()) { sync(); }
			return;
		}
		// Re-resolve when the anchor died OR the live step's own predicate stopped
		// holding. The second half exists for state that flips with NO engine hook
		// behind it — the practice empty render disabling a stale-enabled Start was
		// the found case (smoke, 2026-08-08): the halo sat on a disabled button
		// forever because nothing ever called sync again. when() is a cheap
		// predicate; at the tracker's 300ms cadence that is free.
		if (!visible(target) || (stepObj && stepObj.when && !stepObj.when())) { sync(); return; }
		// …and a chapter-2 step is finished by its own read-back, which no hook rides
		// either (leaving the finish screen, an entry appearing under the lookup field).
		if (stepObj && !heldBack(stepObj) && stepObj.done && stepObj.done()) { sync(); return; }
		// A step whose anchor CHANGES shape without changing step: the word-list row's
		// add button only exists while the pointer is on the row (its controls are
		// hover-revealed), so the glow rides the row until the button appears and then
		// moves onto it. No hook rides that reveal either.
		if (stepObj && stepObj.anchor() !== target) { sync(); return; }
		syncFoot();
		pinHalo();
	}

	// Scroll/resize move anchors without any app hook firing — same capture trick
	// as the beacon popover's repositioning. Bound on DOM ready, NOT at parse time: track() reads
	// PL (guidedOn), and a resize or scroll while the page loads fired it before the module seam had
	// set window.PL — `ReferenceError: PL is not defined` on 24 prod loads in three days
	// (tour-exhaustive-test.md §5.5). Nothing glows before ready, so nothing is lost.
	$(function () {
		window.addEventListener("resize", track);
		window.addEventListener("scroll", track, true);
	});

	// ── the tour cursor ──────────────────────────────────────────────────────────

	// Forward only, and only from the step that is actually live: a late click on a
	// control the chain has already moved past must not skip the step after it. Serves both
	// scripted chains — the step itself says which cursor it belongs to (chainOfStep).
	function advanceStep(fromId) {
		var step = stepById(fromId);
		var chain = step && chainOfStep(step);
		if (!chain || !T()) { return; }
		var i = chain.at();
		if (i === null || i < 0 || i >= chain.steps.length || chain.steps[i].id !== fromId) { return; }
		hideCue("advance");
		clearGlow();
		chainSet(chain, i + 1);
		sync();
	}

	// A step that is not eligible RIGHT NOW is waited for, not skipped — for a few seconds.
	// The finish screen's garden bed is the case that made this a rule: it lands on an async
	// second pass which empties and rebuilds its mount, so both "is the screen up" and "is the
	// anchor there" read false for a moment, and the tour used to skip its own opening step by
	// losing a race with a network round trip (smoke, twice). The wait is bounded, because a
	// step whose moment has genuinely passed must not hang the tour, and a re-sync is scheduled
	// while it lasts — nothing else is guaranteed to look again.
	//
	// A step with a `done()` never waits: that predicate is checked first and moves the cursor
	// the instant the press has happened, so leaving a screen behind is never a wait.
	var ANCHOR_WAIT_MS = 5000;
	var waitFor = null, waitSince = 0, waitTimer = null;

	function waitingOn(k) {
		if (waitFor !== k) { waitFor = k; waitSince = Date.now(); }
		if (Date.now() - waitSince >= ANCHOR_WAIT_MS) { return false; }
		if (!waitTimer) {
			waitTimer = setTimeout(function () { waitTimer = null; sync(); }, 400);
		}
		return true;
	}

	// A step's `enter` hook has run — once per step, and cleared when a chapter is (re)started, so
	// a replay walks the same chapter with the same preparation. See the `enter` rule above WL.
	var entered = {};

	// The live step of a SCRIPTED, in-order chain (chapter 2 and chapter 3 alike): the first
	// one at or after the cursor whose context holds and whose anchor is on screen. Anything
	// skipped on the way is skipped for good — the cursor is written forward, so a step whose
	// moment has passed never comes back.
	function chainStep(chain, retreated) {
		var i = chain.at();
		if (i === null || i < 0) { return null; }
		for (var k = i; k < chain.steps.length; k++) {
			var step = chain.steps[k];
			// Already true = nothing left to teach here; bank that in the cursor right away,
			// so the step is done even if the NEXT one has to wait for its anchor. (Unless a Back
			// has just rewound to it and the screen is still catching up — see heldBack.)
			if (!heldBack(step) && step.done && step.done()) {
				chainSet(chain, k + 1);
				// …and if that was the move that ended the chapter, stop: `ended` may have put
				// a card on the stage, and nothing else may glow behind one.
				if (chain.at() === -1) { return null; }
				continue;
			}
			// The one exception to "forward only": a step that lives INSIDE something the reader
			// opened — the staging menu's three — and finds it shut. Skipping forward there walks
			// onto the next step inside the same shut menu, and off the end of the chapter with
			// nothing on screen (tour-exhaustive-test.md D1: the quit card, Esc or a reload taking
			// the menu down left a tour with no popup, no wall and a cursor that survived reloads).
			// So the cursor goes BACK to the step that opens it, and the walk re-banks forward from
			// there whatever is still open — each of these steps' `when` is the step before's
			// `done`. Once per pass: the step retreated to has no retreat of its own, so the second
			// pass cannot come back here. An EMPTY tray is waited on first: there the count arrow
			// stages straight in and shuts the menu itself, and the words land a round trip later.
			if (step.retreat && !retreated && step.when && !step.when()) {
				if (trayCount() === 0 && waitingOn(step.id)) { return null; }
				var to = chain.steps.indexOf(stepById(step.retreat));
				if (to >= 0 && to < k) {
					waitFor = null;
					chainSet(chain, to);
					return chainStep(chain, true);
				}
			}
			// "There is nothing here to wait FOR": a list this language pair has not got, an own
			// list with no first row. The bounded anchor wait below is for a control that has not
			// PAINTED yet, and spending it on one that is never coming is five seconds of a walk
			// saying nothing at all.
			if (step.gone && step.gone()) { continue; }
			// The step's own preparation, before anything is asked of the panel: several of
			// chapter 3's steps ring a control that does not exist until their hook has opened the
			// list holding it, so this cannot wait until the anchor has been found. Deferred by a
			// tick and re-synced rather than run inline — a hook moves the panel, and moving the
			// panel calls straight back into sync through the app's own hooks.
			if (step.enter && !entered[step.id]) {
				entered[step.id] = true;
				setTimeout(function (s) {
					return function () { s.enter(); sync(); };
				}(step), 0);
				return null;
			}
			var inContext = !step.when || step.when();
			var a = inContext ? step.anchor() : null;
			tourFocus(a);
			if (!visible(a)) {
				if (waitingOn(step.id)) { return null; }   // give it a moment — see ANCHOR_WAIT_MS
				continue;                                  // …its moment has passed: on to the next
			}
			waitFor = null;
			if (k !== i) { chain.set(k); }
			return { step: step, el: a };
		}
		// Every step from the cursor on was done, gone or waited out: the chapter is OVER, and says so.
		// Returning null alone left the cursor on the last step for ever — no popup, no wall, no
		// sign-off, and the same again after every reload (tour-exhaustive-test.md D5: a reload on
		// "name your list" drops the name row, the step banks itself, and wNewUse has no list to ring).
		chainSet(chain, chain.steps.length);
		return null;
	}

	function tourStep() { return chainStep(CHAINS[0]); }

	function wlStep() { return chainStep(CHAINS[2]); }

	// The live MEET step, and the one place chapter 1½ parts company with the tour's walker.
	//
	// The tour runs through one continuous stretch of app, so a step that is not eligible right
	// now is waited a few seconds for and then written off. This chapter's surfaces come and go
	// with the reader instead — a meet card is flipped open, an excursion is opened and left,
	// the session is walked out of and rejoined — so:
	//
	//   surface not up   → YIELD. The cursor stays exactly where it is and the chapter picks up
	//                      at the next flipped card. No timer, and nothing is written off: a
	//                      reader who leaves halfway has not used up their one run.
	//   surface up, but  → SKIP. There is nothing to point at and there never will be on this
	//   no anchor          card — a thin entry with no examples, a card with no clickable word.
	function meetStep() {
		var i = meetAt();
		if (i === null || i < 0) { return null; }
		for (var k = i; k < MEET.length; k++) {
			var step = MEET[k];
			// Already true = nothing left to teach here; bank it in the cursor right away.
			if (step.done && step.done()) {
				chainSet(CHAINS[1], k + 1);
				continue;
			}
			if (step.when && !step.when()) { return null; }
			var a = step.anchor();
			if (!visible(a)) { continue; }
			if (k !== i) { T().meetSet(k); }
			return { step: step, el: a };
		}
		return null;
	}

	// ── evaluation ───────────────────────────────────────────────────────────────

	// One pass: the tour's live step if there is one, else the highest-priority eligible
	// step of the loop; make the DOM match. Idempotent and cheap — OB_TIPS.evaluate calls
	// it from every hook, free to run often.
	// ── the folded panel, unfolded for the walk and folded back ──────────────────
	//
	// `compactLevelLists` / `compactVerbLists` draw the five LEVEL rows and the two verb rows as two
	// strips of chips (docs/project/compact-dictionary-lists.md). The walk still speaks in ONE flag
	// — "was anything folded?" — and plWlPanel.setCompact widens it back out per group, so putting
	// the panel back cannot fold a group the reader had left open. A folded group is not a
	// shortened row — its
	// sections are off screen entirely — so chapters 2 and 3 lose anchors: Conjugation, the list
	// filter box, the verb add and Verb forms each find nothing and skip themselves in turn, which
	// is the tour "breaking at the frequency bands" (owner report 2026-08-13). The two steps that
	// do survive fall back to a chip, and a ring on one letter under copy about five named lists is
	// not much better.
	//
	// So the walk unfolds the panel while it needs it and PUTS IT BACK. Render only — the setting
	// itself is never written, so a reader who reloads mid-walk, or abandons it, still has the
	// panel they chose. `compactWas` is the latch: non-null exactly while the walk owes a refold.
	//
	// Held through the CARDS too (the offer, the quit question): chapter 2 ends by writing its
	// cursor to -1 and then raising a card, so a plain "is a chapter live" test would fold the
	// panel back under the card and unfold it again on "yes".
	//
	// Chapter 1 is deliberately NOT included. The explore glow trail is compact-AWARE rather than
	// compact-intolerant — its list step swaps its own copy for `obTipExpChip_body` ("click this
	// letter") when the anchor is a chip — and it escorts a reader who is browsing on their own,
	// not walking a script.
	var compactWas = null;

	function refoldPanel() {
		if (compactWas === null) { return; }
		panelDo("setCompact", compactWas);
		compactWas = null;
	}

	function syncPanelFold() {
		if (!guidedOn()) { refoldPanel(); return; }
		if (tourLive() || wlLive()) {
			if (compactWas === null && panelAsks("compact")) {
				compactWas = true;
				panelDo("setCompact", false);
			}
			return;
		}
		// A card HOLDS an unfold the walk already owns; it never starts one. Holding matters
		// because chapter 2 ends by writing its cursor to -1 and only then raising the offer card,
		// so a plain "is a chapter live" test would fold the panel back under the card and unfold
		// it again on "yes". Not starting matters because the WELCOME card is an .obCardClass too,
		// and it belongs to chapter 1 — which is compact-aware and wants its chips.
		if (cardLive()) { return; }
		refoldPanel();
	}

	// T-15 (docs/project/tablet-support-plan.md §4): the touch layer's own way through a step whose
	// control it hides (R1). PL.tourVariant is unset on a desktop; js/touch/tour.js sets it and gets
	// each step ONCE, with its chapter's name, to adjust in place — in place, because the chains find a step by identity and
	// keep their cursors as indices (a step added or removed would move every stored cursor).
	var variantsDone = false;
	function applyVariants() {
		if (variantsDone || !(window.PL && PL.tourVariant)) { return; }
		variantsDone = true;
		[["walk", STEPS], ["tour", TOUR], ["wl", WL], ["sign", SIGN], ["meet", MEET]].forEach(function (c) {
			c[1].forEach(function (step) { PL.tourVariant(step, c[0]); });
		});
	}

	// T-14: the step's anchor, before it is judged visible. PL.tourFocus is unset on a desktop;
	// js/touch/tour.js opens the side pane that holds it (touch shows one side at most, and none in
	// portrait), and answers true while that is under way.
	function tourFocus(a) {
		return !!(a && window.PL && PL.tourFocus && PL.tourFocus(a));
	}

	function sync() {
		applyVariants();
		syncPanelFold();
		stageOwnedWas = cardLive();
		if (!guidedOn() || stageOwnedWas) {
			stageLit = null;
			clearGlow();
			hideCue(stageOwnedWas ? "card" : "off");
			return;
		}
		// The ticker runs for as long as the guide is armed, whether or not anything is
		// glowing right now — see track() for the one thing it is watching for when nothing is.
		startTracker();
		// The drill screen is gone: the answer-field step is owed again to whoever meets one next
		// (a replay, a second escorted run). Latched only for as long as the screen it belongs to.
		if (!visible(document.getElementById("answerId"))) { typedInDrill = false; }
		// A session screen: nothing glows and nothing is explained, but the wall holds
		// (sessionStage). The ONE exception is a step that declares itself fit for one — the drill's
		// answer field, which is the first thing a first-time reader has to be told what to do with.
		// Every other step is handed back to the plain panel-wide wall here, which is the rule that
		// keeps the walk quiet on a meet card and mid-question. clearGlow takes the wall down with
		// the glow, so the order is clear-then-raise, never the other way round.
		// Chapter 1½ opens itself: the first meet card of the escorted run is its moment — the card
		// as it ARRIVES, unflipped, since its opening step is the press that flips it. Neither the
		// render nor the flip rides a hook of its own (see track()).
		if (walkArmed() && meetAt() === null && meetSurface()) { T().meetSet(0); }
		var stage = sessionStage();
		// The TOUR is not even asked on a session screen: tourStep() advances the cursor as a side
		// effect of asking (a step whose done() already holds is banked on the spot), and chapter 2
		// has no step that belongs on one anyway. Chapter 1 is asked, for the one that does.
		//
		// Chapter 1½ is asked FIRST and falls through when it has nothing to say: it owns the
		// flipped meet card and nothing else, so between its steps (the rest of the ladder, the
		// recap, the drill) chapter 1 must go on escorting exactly as it did before.
		// …and chapter 3 the same way: it lives entirely in the word-list panel, so a session
		// screen silences it exactly as it silences the tour.
		// The sign-off outranks everything: it only exists once every other chapter is over, and
		// the one case where another is not — the welcome card's "I'll explore on my own", which
		// leaves chapter 1's escort armed — is exactly the case where its two sentences come
		// first and the escort picks up behind them.
		var pick = signLive() ? chainStep(CHAINS[3]) : null;
		if (!pick && meetLive()) { pick = meetStep(); }
		if (!pick && wlLive()) {
			pick = stage ? null : wlStep();
		}
		if (!pick && !wlLive()) {
			pick = tourLive() ? (stage ? null : tourStep()) : (walkArmed() ? walkStep() : null);
		}
		if (stage && !(pick && pick.step.onStage)) {
			clearGlow();
			// "stage:<id>" = a step WAS found but is not one a session screen may show.
			hideCue(pick ? "stage:" + pick.step.id : "stage");
			// The dim is the ESCORT's (owner ALERT 2026-10-07, first-run-redesign R17). Asked of
			// armed() it also held for the sign-off — Start Learning pressed under the farewell
			// opened a session behind the wall — and, with no chapter left at all, guided MODE alone
			// put every later Learn session behind it. A notice that a session screen has come up
			// under is over: the reader has gone on to the app.
			if (!escorting()) {
				if (pick && pick.step.notice) { closeNotice(); }
				stageLit = null;
				return;
			}
			if (stageLit !== stage) { blocked = 0; }   // a new screen is a fresh start
			stageLit = stage;
			pinWall();
			return;
		}
		stageLit = null;
		if (!pick) {
			clearGlow();
			hideCue("nostep");
			return;
		}
		if (pick.step.id !== stepId || pick.el !== target) {
			applyGlow(pick.step, pick.el);
		} else {
			pinHalo();
			// The box may have gone by itself (Esc on an action step, an anchor that
			// scrolled out and back) while its step stayed live — put it back.
			if (cueFor !== pick.step.id) { showCue(pick.step, pick.el); }
		}
	}

	function walkStep() {
		for (var i = 0; i < STEPS.length; i++) {
			if (!STEPS[i].when()) { continue; }
			var a = STEPS[i].anchor();
			if (tourFocus(a)) { return null; }   // a pane is opening for it: the next sync shows it
			if (!visible(a)) { continue; }
			return { step: STEPS[i], el: a };
		}
		return null;
	}

	OB_GUIDE = {
		sync: sync,
		// The stage rule the tooltip engine borrows (tooltip-engine-consolidation.md §2.2):
		// while a drill question or a learn screen is live, guided-MODE tooltips render
		// NORMAL — recall focus is sacred. Exported rather than duplicated in js/pl/tip.js
		// so there is exactly one answer to "is a question on screen" in the app.
		sessionScreenLive: sessionScreenLive,
		// Both popup boxes, for layers that must not count a press inside one as an outside
		// press (the add menu concats these into its anchors). Built empty and unshown if the
		// menu asks before the walk has anything to say — the layer captures these elements
		// once, and a popup that pops later must be one of those same boxes.
		cueAnchors: function () {
			return [ensurePin("obGuideStep").el, ensurePin("obGuideInfo").el];
		},
		// True while the open popup points INTO the add menu (the Random / count-arrow
		// steps): the menu's hover machines suspend their away-close on it, so the
		// pointer can travel to the box without the menu collapsing mid-walk.
		//
		// …and while the QUIT CARD is up over an open menu. The card takes the popup down, so the
		// first half reads false, and the pointer leaving the menu for the card started the
		// away-close — which 600 ms later closed the menu's layer and, the card being the layer
		// stacked on it, the card with it: a "stay" nobody pressed, onto a menu that was gone
		// (tour-exhaustive-test.md D1). main.js asks this on the menu's layer too, so a press on
		// the card's backdrop — "stay" — closes the card and leaves the menu where it was.
		cueInMenuLive: function () {
			return !!(cueFor && inAddMenu(cueAnchor)) || (quitAsked && !!openMenu());
		},
		// True while the walk is live AND its first learn session has not completed.
		// main.js buildCountItems reads it to offer a gentler first scoop: the Random
		// menu's top preset becomes 3 instead of 5 (owner call 2026-08-08 — five felt
		// like a lot for a first meeting with the app; tightened same day to the FIRST
		// learn session only). The popup copy (obGuideFive) says 3 on the same signal.
		firstRun: function () { return walkArmed() && firstLearn(); },
		// The whole guide, either chapter — what the wall and the app's own hooks ask.
		armed: armed,
		// True while chapter 1½ owns the Escape key — learning_session.js asks before letting Esc
		// stand in for the meet card's "Got it". See holdsEscape.
		holdsEscape: holdsEscape,
		// The finish screen hands chapter 1 over to the tour (onboarding.js's finish hook).
		practiceFinished: practiceFinished,
		// The Help hub's own door into chapter 3 — the panel walk, on demand and by itself.
		// Returns false when the attr is absent (a pre-tour user the hub has not seeded yet).
		startWordLists: startWordLists,
		// Every door out of the walk goes through this now (owner call 2026-09-02), the welcome
		// card's "I'll explore on my own" included: it disarms chapter 1's explore glow trail
		// along with the two scripted chapters, then hands off to the fly-to-help sign-off. Takes
		// the CARD that just declined, for the shrink-flight's geometry (see signOff).
		endTour: endTour,
		// The welcome card was answered: the walk's Back history starts here (see backTarget).
		runStarted: runStarted,
		// signOff on its own remains exported for symmetry, but every current caller goes through
		// endTour above so the escort is disarmed first.
		signOff: signOff,
		// True while chapter 4 owns the help menu — the hub asks before letting a press, an Esc or
		// an auto-close take its own menu down under the walk's wall.
		signLive: signLive
	};

	// The mode switch is one of the walk's arming inputs (see guidedOn), and nothing else
	// would re-evaluate on a hub toggle — sync() is idempotent, so this is just "the DOM
	// now disagrees with armed(), fix it".
	document.addEventListener("plmodechange", sync);

	// Belt to the OB_TIPS boot pass (whose evaluate syncs the guide as its first
	// move): if that engine is ever dropped while the guide lives on, the guide
	// still boots on its own.
	$(function () {
		setTimeout(sync, 800);
	});

})();
